import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { dispatchShare } from '../utils/invoiceSharing';
import QuickInvoiceView from './QuickInvoiceView';
import { motion, AnimatePresence } from 'motion/react';
import {
  FileText, Download, Trash2, CheckCircle, AlertCircle,
  RefreshCw, Clock, ExternalLink, Search, Pencil, FileSpreadsheet,
  Plus, X, Copy, XCircle, Check, Mail, Send, User
} from 'lucide-react';
import XLSX from 'xlsx-js-style';
import QuickScannerModal from './QuickScannerModal';
import { InvoiceRecord, CanvasElement, BankConnection, BankTransaction } from '../types';
import CurrencySelector from './CurrencySelector';
import { getCurrencyDisplay } from '../utils/currency';
import { loadCompanyProfile, CompanyProfile, CONTACT_TYPE_META } from './ProfileView';
import { getPermissions } from '../utils/permissions';
import { postLog } from '../utils/authApi';
import {
  printReceiptUSB,
  isUSBPrinterConnected,
  connectUSBPrinter,
  tryAutoReconnectUSB,
  getUSBPrinterName,
} from '../utils/usbPrinter';
import {
  printReceiptBT,
  connectBluetoothPrinter,
  getBTPrinterName,
  resolveClientLineAr,
} from '../utils/bluetoothPrinter';
import { loadMessageTemplates, applyTemplate } from '../utils/messageTemplates';
import POSReceiptRenderer from './POSReceiptRenderer';
import { buildCwElements, renderCwToHtml, PAGE_CONFIGS } from './QuickInvoiceView';
import CanvasWorkspace from './CanvasWorkspace';
import BilingualInvoiceRenderer from './BilingualInvoiceRenderer';
import { amountToWords } from '../utils/numberToWords';
import { COUNTRY_CODES } from '../utils/countryCodes';
import { getDefaultSchemeForCountryAndRegion, computeTaxBreakdown, buildCustomScheme } from '../utils/taxSchemes';
import { inspectTaxId } from '../utils/taxId';
// Universal 240+ country phone parser
function parsePhoneValue(value: string, defaultCountry = 'IN'): { country: string; dialCode: string; nationalNumber: string } {
  const fallbackCountry = defaultCountry && COUNTRY_CODES[defaultCountry] ? defaultCountry : 'IN';
  const fallbackDialCode = (COUNTRY_CODES[fallbackCountry] || '91').replace(/\D/g, '');

  if (!value) return { country: fallbackCountry, dialCode: fallbackDialCode, nationalNumber: '' };

  const raw = String(value).trim();
  const hasLeadingPlus = raw.startsWith('+');
  let cleanDigits = raw.replace(/\D/g, '');

  if (hasLeadingPlus) {
    let bestCountry = fallbackCountry;
    let bestDialCode = fallbackDialCode;
    let maxCodeLen = 0;

    const sortedEntries = Object.entries(COUNTRY_CODES).sort(
      (a, b) => b[1].replace(/\D/g, '').length - a[1].replace(/\D/g, '').length
    );

    for (const [c, code] of sortedEntries) {
      const pureCode = code.replace(/\D/g, '');
      if (cleanDigits.startsWith(pureCode) && pureCode.length > maxCodeLen) {
        bestCountry = c;
        bestDialCode = pureCode;
        maxCodeLen = pureCode.length;
      }
    }

    return {
      country: bestCountry,
      dialCode: bestDialCode,
      nationalNumber: cleanDigits.slice(bestDialCode.length),
    };
  }

  if (cleanDigits.startsWith('0') && fallbackCountry !== 'IT' && cleanDigits.length >= 7) {
    cleanDigits = cleanDigits.replace(/^0/, '');
  }

  if (cleanDigits.startsWith(fallbackDialCode) && cleanDigits.length > 10) {
    return {
      country: fallbackCountry,
      dialCode: fallbackDialCode,
      nationalNumber: cleanDigits.slice(fallbackDialCode.length),
    };
  }

  return {
    country: fallbackCountry,
    dialCode: fallbackDialCode,
    nationalNumber: cleanDigits,
  };
}

// Validates that there are at least 4 digits in the national number, and 7-15 total digits
export function getCleanE164Phone(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parsePhoneValue(phone, defaultCountry);
  const national = parsed.nationalNumber.replace(/\D/g, '');
  const dial = parsed.dialCode.replace(/\D/g, '');
  const total = `${dial}${national}`;

  if (national.length < 4 || total.length < 7 || total.length > 15) {
    return '';
  }
  return `+${dial}${national}`;
}

// Guaranteed clean digits for WhatsApp API (e.g. "919108582545")
function resolveWaPhone(rawPhone: string, sellerCountry = 'IN'): string {
  const clean = getCleanE164Phone(rawPhone, sellerCountry);
  return clean.replace(/\D/g, '');
}
// Postgres TIMESTAMP WITHOUT TIME ZONE comes back with no 'Z' suffix, so the
// browser's Date parser treats it as local time instead of UTC — causing
// timestamps to appear shifted by the user's UTC offset (e.g. IST users see
// times ~5.5h off). This wraps any createdAt/date string safely.
function parseUtcDate(dateStr: string): Date {
  if (!dateStr) return new Date();
  return new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
}

// Phonetic fallback for amount-in-words when /translate echoes English back
// untranslated — same /transliterate endpoint POS's runPOSTranslate already
// uses for names/items that Google Translate can't handle as real words.
async function transliterateName(text: string, targetLang: string): Promise<string> {
  if (!text?.trim()) return text;
  try {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
    const res = await fetch(`${BASE}/transliterate?text=${encodeURIComponent(text.trim())}&tl=${targetLang}`, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    });
    const data = await res.json();
    return data.transliterated || text;
  } catch (e) {
    console.warn('transliterateName failed:', e);
    return text;
  }
}

// Batch translator for BillsView: Translates an entire list in 1 single API call
async function translateBatchBills(texts: string[], targetLang: string): Promise<string[]> {
  if (!texts.length) return [];
  try {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
    const res = await fetch(`${BASE}/translate/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      },
      body: JSON.stringify({ texts, tl: targetLang }),
    });
    if (!res.ok) return texts;
    const data = await res.json();
    return Array.isArray(data?.translations) ? data.translations : texts;
  } catch (e) {
    console.warn('translateBatchBills failed:', e);
    return texts;
  }
}
// Resolves actual QR position (Left / Center / Right) from stored properties or element X coordinate
function getBillQrPosition(bill: any): 'left' | 'center' | 'right' {
  if (!bill) return 'center';
  if (bill.qr_position) return bill.qr_position;
  if (bill.autoQrPosition) return bill.autoQrPosition;
  if (bill.qrPosition) return bill.qrPosition;

  // Detect position from actual X coordinate of the saved invoice QR
  const invQr = (bill.elements || []).find((el: any) => el._auto === 'invoice' || el.id?.startsWith('invoice-qr-'));
  if (invQr && typeof invQr.x === 'number') {
    if (invQr.x <= 150) return 'left';
    if (invQr.x >= 500) return 'right';
    return 'center';
  }
  return 'center';
}
// Backend stores these as snake_case (seller_country/seller_region/
// buyer_region), same as every other bilingual field in this file
// (row_labels_ar vs rowLabelsAr, etc.) — camelCase-only lookups here
// silently returned '' for every invoice, which made getDefaultSchemeForRegion
// fall back to a flat scheme and always render the blended "Total Tax"
// line instead of named CGST/SGST/IGST.
function getSellerBuyerInfo(bill: any) {
  return {
    sellerCountry: bill?.sellerCountry || bill?.seller_country || '',
    sellerRegion: bill?.sellerRegion || bill?.seller_region || '',
    buyerRegion: bill?.buyerRegion || bill?.buyer_region || '',
  };
}

// Recomputes named tax breakdown from line items — supports regional split schemes (CGST/SGST),
// custom outlet schemes (e.g. rihan), and custom B2B names (e.g. rihan4).
function computeNamedTaxBreakdown(
  lineItems: any[],
  discountLines: { rate?: number }[],
  taxScheme: any,
  sellerRegion?: string,
  buyerRegion?: string,
  isB2B = false,
  customTaxScheme?: any
): { name: string; rate: number; amount: number }[] | undefined {
  if (!taxScheme && !customTaxScheme) return undefined;

  let subtotal = 0;
  const lineBases: { base: number; taxRate: number; taxName?: string; b2bTaxName?: string }[] = [];
  (lineItems || []).forEach(li => {
    const qty = parseFloat(String(li.qty)) || 0;
    const rate = parseFloat(String(li.rate)) || 0;
    const lineTotal = qty * rate;
    const rawB2b = li.b2bTaxRate ?? li.b2b_tax_rate;
    const taxRate = isB2B && rawB2b !== undefined && rawB2b !== null
      ? parseFloat(String(rawB2b)) || 0
      : parseFloat(String(li.taxRate ?? li.tax_rate ?? 0)) || 0;
    const inclusive = !!(li.taxInclusive ?? li.tax_inclusive);
    const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
    const taxName = li.taxName ?? li.tax_name;
    const b2bTaxName = li.b2bTaxName ?? li.b2b_tax_name;
    lineBases.push({ base, taxRate, taxName, b2bTaxName });
    subtotal += base;
  });

  const discount = (discountLines || []).reduce((s, d: any) => s + subtotal * ((d.rate || 0) / 100), 0);
  const discountRatio = subtotal > 0 ? Math.min(1, discount / subtotal) : 0;
  const combined: Record<string, { rate: number; amount: number }> = {};

  const effectiveScheme = (taxScheme?.id === 'outlet_custom' || taxScheme?.id === 'custom' || !taxScheme) && customTaxScheme
    ? { ...buildCustomScheme(customTaxScheme), id: 'outlet_custom' }
    : taxScheme;

  const isCustomScheme = effectiveScheme?.id === 'outlet_custom' || effectiveScheme?.id === 'custom';
  const b2bComps = isCustomScheme ? ((customTaxScheme as any)?.b2b_components || (effectiveScheme as any)?.b2b_components) : null;
  const hasB2BSplit = isB2B && b2bComps && b2bComps.length > 1;

  lineBases.forEach(lb => {
    if (lb.taxRate <= 0) return;
    const discountedBase = lb.base * (1 - discountRatio);

    if (hasB2BSplit) {
      b2bComps.forEach((c: any) => {
        if (!combined[c.name]) combined[c.name] = { rate: c.rate, amount: 0 };
        combined[c.name].amount += (discountedBase * c.rate / 100);
      });
    } else if (!isB2B && effectiveScheme?.kind === 'component' && effectiveScheme?.components && effectiveScheme?.components.length > 1) {
      const breakdown = computeTaxBreakdown(effectiveScheme, discountedBase, { sellerRegion, buyerRegion }, lb.taxRate);
      breakdown.lines.forEach(l => {
        if (!combined[l.name]) combined[l.name] = { rate: l.rate, amount: 0 };
        combined[l.name].amount += l.amount;
      });
    } else {
      // Flat or single custom tax (e.g. rihan / rihan4 / PPN / VAT / GST)
      const standardTaxName = lb.taxName || customTaxScheme?.name || effectiveScheme?.components?.[0]?.name || effectiveScheme?.label?.replace(/\s*\(.*?\)/, '') || 'Tax';
      const customLabel = isB2B
        ? (lb.b2bTaxName || (customTaxScheme as any)?.b2b_name || (effectiveScheme as any)?.b2b_name || standardTaxName)
        : standardTaxName;

      if (!combined[customLabel]) combined[customLabel] = { rate: lb.taxRate, amount: 0 };
      combined[customLabel].amount += (discountedBase * lb.taxRate / 100);
    }
  });

  const entries = Object.entries(combined).map(([name, v]) => ({
    name,
    rate: typeof v.rate === 'number' ? v.rate : (parseFloat(String(v.rate || 0).replace('%', '')) || 0),
    amount: typeof v.amount === 'number' ? v.amount : (parseFloat(String(v.amount || 0)) || 0),
  }));
  return entries.length > 0 ? entries : undefined;
}

// ── Universal Customer Details Formatter (100% Dynamic Language Parity) ──────
function buildClientBlock(
  data: {
    name: string;
    phone: string;
    email: string;
    address: string;
    taxId: string;
    nameTr?: string;
    addressTr?: string;
    walkInTr?: string;
  },
  inc: { name: boolean; phone: boolean; email: boolean; address: boolean; taxId: boolean },
  isBilingual = false,
  labels?: {
    taxId?: string;
    phone?: string;
    email?: string;
    address?: string;
  }
): { en: string; tr?: string } {
  const isReal = Boolean(data.name && data.name.trim() && data.name.toLowerCase() !== 'walk-in customer');
  const enName = inc.name ? (isReal ? data.name.trim() : 'Walk-in Customer') : '';
  const trName = inc.name ? (isReal ? (data.nameTr || enName) : (data.walkInTr || enName)) : '';

  const trTaxLabel = labels?.taxId || 'Tax ID';
  const trPhoneLabel = labels?.phone || 'Phone';
  const trEmailLabel = labels?.email || 'Email';
  const trAddressLabel = labels?.address || 'Address';

  const enLines: string[] = [];
  const trLines: string[] = [];

  // 1. Name
  if (enName) {
    enLines.push(enName);
    if (isBilingual) trLines.push(trName);
  }
  // 2. Email
  if (inc.email && data.email?.trim() && data.email.includes('@')) {
    enLines.push(`Email: ${data.email.trim()}`);
    if (isBilingual) trLines.push(`${trEmailLabel}: \u200E${data.email.trim()}\u200E`);
  }
  // 3. Phone (wrapped in \u200E so bidirectional RTL text won't flip ":Phone")
  if (inc.phone && data.phone?.trim()) {
    enLines.push(`Phone: ${data.phone.trim()}`);
    if (isBilingual) trLines.push(`${trPhoneLabel}: \u200E${data.phone.trim()}\u200E`);
  }
  // 4. Address
  if (inc.address && data.address?.trim()) {
    enLines.push(`Address: ${data.address.trim()}`);
    if (isBilingual) trLines.push(`${trAddressLabel}: ${data.addressTr || data.address.trim()}`);
  }
  // 5. Tax ID
  if (inc.taxId && data.taxId?.trim()) {
    enLines.push(`Tax ID: ${data.taxId.trim()}`);
    if (isBilingual) trLines.push(`${trTaxLabel}: \u200E${data.taxId.trim()}\u200E`);
  }

  return {
    en: enLines.join('\n'),
    tr: isBilingual ? trLines.join('\n') : undefined,
  };
}

// Backend sometimes appends duplicate _auto QR/label elements (invoice-qr-*,
// invoice-label-*, token-qr-*, token-label-*) with mismatched counts —
// e.g. 2 labels but only 1 QR image survives. Keep only the first of each,
// same rule openEdit() already applies to its preview copy.
function dedupeInvoiceElements(elements: CanvasElement[]): CanvasElement[] {
  const seen = new Set<string>();
  return (elements || []).filter((el: any) => {
    const idKey = el.id?.startsWith('invoice-qr-') ? 'invoice-qr'
                : el.id?.startsWith('invoice-label-') ? 'invoice-label'
                : el.id?.startsWith('token-qr-') ? 'token-qr'
                : el.id?.startsWith('token-label-') ? 'token-label'
                : null;
    const autoKey = el._auto ? `auto-${el._auto}` : null;
    const textKey = (el.type === 'text' && (el.textContent === 'Scan to view invoice' || el.textContent === 'Scan to track your order'))
      ? `text-${el.textContent}` : null;
    const key = idKey || autoKey || textKey;
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
// ── inline style constants matching QuickInvoiceView ──
const qi: {
  lbl: React.CSSProperties;
  inp: React.CSSProperties;
  row: React.CSSProperties;
} = {
  lbl: { fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase' as const, letterSpacing: '0.05em', display: 'block', marginBottom: 5 },
  inp: { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid #cbd5e1', fontSize: 13, fontWeight: 500, color: '#0f172a', background: '#fff', outline: 'none', boxSizing: 'border-box' as const },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
};

interface BillsViewProps {
  userRole?: string;
  bills: InvoiceRecord[];
  setBills: React.Dispatch<React.SetStateAction<InvoiceRecord[]>>;
  authUserPerms?: Record<string, boolean>;
  authUserPlan?: string;
  authUserPlanTier?: string;
  authUserPlanStatus?: string;
  onToggleStatus: (id: string) => void;
  onDeleteBill: (id: string) => void;
  onSyncToDrive: (billId: string) => void;
  onDownloadPDF: (bill: InvoiceRecord) => void;
  onPrintPDF?: (bill: InvoiceRecord) => void;
  onExportMergedPDF?: (bills: InvoiceRecord[]) => void;
  onUpdateInvoice?: (billId: string, updatedElements: CanvasElement[], newAmount: number, updatedFields?: Partial<InvoiceRecord>) => void;
  onReuseInvoiceLayout?: (bill: InvoiceRecord) => void;
  onEditInvoice?: (bill: InvoiceRecord) => void;
  googleToken: string | null;
  activeCurrency?: string;
  isSyncingId: string | null;
  bankConnections?: BankConnection[];
  onUpdateConnections?: (conns: BankConnection[]) => void;
  bankTransactions?: BankTransaction[];
  onUpdateTransactions?: (txns: BankTransaction[]) => void;
  onGoogleLogin?: () => void;
  onAddLog?: (action: string, details: string) => void;
  onSendInvoiceWhatsApp?: (invoice: InvoiceRecord, phone: string) => Promise<void>;
  onCreateUpdatedInvoice?: (invoice: Partial<InvoiceRecord>) => Promise<void>;
  onGetPdfUrl?: (bill: InvoiceRecord) => Promise<string | null>;
}


export default function BillsView({
  userRole, bills, setBills, authUserPerms, authUserPlan, authUserPlanTier, authUserPlanStatus, onToggleStatus, onDeleteBill, onSyncToDrive,
  onDownloadPDF, onPrintPDF, onExportMergedPDF, onUpdateInvoice, onReuseInvoiceLayout,
  onEditInvoice, googleToken, activeCurrency, isSyncingId, bankConnections, onUpdateConnections,
  bankTransactions, onUpdateTransactions, onGoogleLogin, onAddLog, onSendInvoiceWhatsApp, onCreateUpdatedInvoice, onGetPdfUrl
}: BillsViewProps) {

  const displayMode = (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol';
  const sym = (currency?: string) => getCurrencyDisplay(currency || activeCurrency || 'USD', displayMode);

  const formatAmountVal = (amount: number, currency?: string) => {
    return `${sym(currency)}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all'); // 'all' | 'b2b' | 'b2c'
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showTaxDisclaimerModal, setShowTaxDisclaimerModal] = useState(false);

  // ── Pagination: Load Next 50 ──
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreBills, setHasMoreBills] = useState(bills.length >= 50);

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    try {
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
      const res = await fetch(`${BASE}/invoices?limit=50&offset=${bills.length}`, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      });
      if (res.ok) {
        const nextBatch = await res.json();
        if (Array.isArray(nextBatch)) {
          if (nextBatch.length < 50) {
            setHasMoreBills(false); // No more bills left in DB
          }
          // Append new records while avoiding duplicates
          setBills(prev => {
            const existingIds = new Set(prev.map(b => b.id));
            const fresh = nextBatch.filter(b => !existingIds.has(b.id));
            return [...prev, ...fresh];
          });
        }
      }
    } catch (err) {
      console.warn('Failed to load more bills:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Comprehensive Helper to detect buyer Tax ID across all backend formats & elements
  const getInvoiceTaxId = (b: any): string => {
    if (!b) return '';

    // 1. Check all direct invoice properties from database
    const direct =
      b.buyer_tax_id || b.buyerTaxId ||
      b.tax_id || b.taxId ||
      b.clientGSTIN || b.client_gstin ||
      b.client_tax_id || b.clientTaxId ||
      b.buyer_gstin || b.buyerGstin ||
      b.gstin || b.vat_number || b.trn;
    if (direct && String(direct).trim()) return String(direct).trim();

    // 2. Check table element in elements
    const tableEl = (b.elements || []).find((el: any) => el.type === 'table');
    const tableTaxId =
      (tableEl as any)?.buyer_tax_id || (tableEl as any)?.buyerTaxId ||
      (tableEl as any)?.client_gstin || (tableEl as any)?.clientGSTIN ||
      (tableEl as any)?.tax_id;
    if (tableTaxId && String(tableTaxId).trim()) return String(tableTaxId).trim();

    // 3. Check dynamic values
    const dyn = b.dynamicValues || b.dynamic_values || b.dynamic_values_ar || b.dynamicValuesAr;
    const dynTaxId =
      dyn?.buyer_tax_id || dyn?.buyerTaxId ||
      dyn?.client_gstin || dyn?.clientGSTIN ||
      dyn?.tax_id || dyn?.taxId || dyn?.gstin;
    if (dynTaxId && String(dynTaxId).trim()) return String(dynTaxId).trim();

    // 4. Scan all text elements for Tax ID / GSTIN / VAT / TRN / NPWP (robust across POS & A4)
    for (const el of (b.elements || [])) {
      if (el.type === 'text' && el.textContent) {
        const lines = el.textContent.split('\n');
        for (const line of lines) {
          const match = line.match(/(?:tax\s*id|gstin|npwp|vat|trn|pan)\s*[:\-#]?\s*([A-Z0-9.\-\/]+)/i);
          if (match && match[1] && match[1].trim().length >= 4) {
            const candidate = match[1].trim().replace(/[\s\.]/g, '');
            if (!/^(none|na|null|undefined|date|invoice)$/i.test(candidate)) {
              return candidate;
            }
          }
        }
      }
    }
    return '';
  };

  const isInvoiceB2B = (b: any) => {
    if (!b) return false;
    if (b.is_b2b === true || b.isB2B === true || b.b2b === true) return true;
    const taxId = getInvoiceTaxId(b);
    return !!(taxId && taxId.trim().length > 0);
  };

  // Export-to-Excel date range popover — `records` is whichever set (the
  // currently filtered table view, or a manual row selection) the user
  // clicked Export from.
  const [exportPopover, setExportPopover] = useState<{ records: InvoiceRecord[] } | null>(null);
  const [exportDateRange, setExportDateRange] = useState<'all' | 'today' | 'week' | 'month' | 'year' | 'custom'>('all');
  const [exportCustomFrom, setExportCustomFrom] = useState('');
  const [exportCustomTo, setExportCustomTo] = useState('');
  

  const [paymentStatusMap, setPaymentStatusMap] = useState<Record<string, { status: string; gateway: string; updated_at: string }>>({});

  React.useEffect(() => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    if (!jwt) return;
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

    const fetchStatuses = () => {
      fetch(`${BASE}/payments/status/by-invoice`, { headers: { Authorization: `Bearer ${jwt}` } })
        .then(r => r.json())
        .then(data => {
          if (!Array.isArray(data)) return;
          const map: Record<string, { status: string; gateway: string; updated_at: string }> = {};
          data.forEach((p: any) => {
            map[String(p.invoice_id)] = { status: p.status, gateway: p.gateway, updated_at: p.updated_at };
          });
          setPaymentStatusMap(map);
        })
        .catch(() => {});
    };

    fetchStatuses();
    const interval = setInterval(fetchStatuses, 5000);
    return () => clearInterval(interval);
  }, []);
  const perms = getPermissions(userRole, authUserPerms, authUserPlan, undefined, undefined, null);
  const toAr = (n: number, decimals = 2) =>
    n.toFixed(decimals).replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]);

  // Same USB/Bluetooth thermal printer settings Quick Invoice uses —
  // shared localStorage keys so a printer connected in one screen stays
  // connected in the other.
  const [useUSBPrinter, setUseUSBPrinter] = useState(() =>
    localStorage.getItem('cloudairambo_qi_usb_print') === 'true'
  );
  const [usbConnected, setUsbConnected] = useState(false);
  const [useBTPrinter, setUseBTPrinter] = useState(() =>
    localStorage.getItem('cloudairambo_qi_bt_print') === 'true'
  );
  const [btConnected, setBtConnected] = useState(() => !!getBTPrinterName());
  const [showPrintSettings, setShowPrintSettings] = useState(false);
  const [printingBillId, setPrintingBillId] = useState<string | null>(null);
const [downloadingBillId, setDownloadingBillId] = useState<string | null>(null);
// Bill waiting on a live printer connection — opens the picker below
// instead of silently downgrading to the OS print dialog.
const [billsPrinterPicker, setBillsPrinterPicker] = useState<InvoiceRecord | null>(null);

  React.useEffect(() => {
    tryAutoReconnectUSB().then(ok => setUsbConnected(ok || isUSBPrinterConnected()));
  }, []);

  // System keys & database fields that should NEVER show as custom table columns
  const INTERNAL_ITEM_KEYS = [
    'id', 'description', 'arabicDescription', 'arabicName', 'qty', 'rate', 'total',
    'taxRate', 'tax_rate', 'taxName', 'tax_name',
    'b2bTaxRate', 'b2b_tax_rate', 'b2bTaxName', 'b2b_tax_name',
    'taxInclusive', 'tax_inclusive',
    // Database schema internals:
    'org_id', 'orgId', 'outlet_id', 'outletId', 'created_at', 'createdAt',
    'updated_at', 'updatedAt', 'stock_quantity', 'stockQuantity',
    'low_stock_threshold', 'lowStockThreshold', 'custom_fields', 'customFields',
    'tax_scheme_id', 'taxSchemeId', 'category', 'supplier', 'warranty',
    'cost_price', 'costPrice', 'purchase_price', 'manufacturer',
    'name', 'price', 'unit', 'tax', 'mrp', 'barcode', 'product_id', 'discount'
  ];

  // Derived tax/translation fields that are already displayed in badges & descriptions (never shown as duplicate form rows)
  const READONLY_ITEM_KEYS = [
    'cat_tax_b2b', 'cat_tax_b2c', 'cat_tax_name', 'cat_tax_type', 'translatedDescription',
    'tax_b2b', 'tax_b2c', 'tax_name', 'tax_type',
  ];
  // Kept in state for preview/save/print, but never shown in the form
  const HIDDEN_ITEM_KEYS = [
    'translated_description', 'arabic_description', 'arabic_name',
    'unit_price', 'amount', 'tax', 'tax_amount', '__customAr', 'barcode', 'product_id',
    'cat_tax_b2b', 'cat_tax_b2c', 'cat_tax_name', 'cat_tax_type', 'translatedDescription',
  ];
  const isEditableItemKey = (k: string) =>
    !INTERNAL_ITEM_KEYS.includes(k) && !READONLY_ITEM_KEYS.includes(k) && !HIDDEN_ITEM_KEYS.includes(k);

  const CUSTOM_COLUMN_LABELS: Record<string, string> = {
    cat_stock: 'Stock',
    cat_barcode: 'Barcode',
    cat_brand: 'Brand',
    cat_unit: 'Unit',
    cat_category: 'Category',
    cat_description: 'Description',
    cat_tax_b2b: 'Tax',
    cat_tax_b2c: 'Tax',
    cat_tax_name: 'Tax Name',
    cat_tax_type: 'Tax Type',
    mrp: 'MRP',
    batch_no: 'Batch No',
    hsn: 'HSN',
    remarks: 'Remarks',
    translatedDescription: 'Translated Description',
    translated_description: 'Translated Description',
    arabicDescription: 'Arabic Description',
    arabic_description: 'Arabic Description',
  };

  const formatColumnLabel = (raw: string): string => {
    if (!raw) return '';
    const key = String(raw).trim();
    const lower = key.toLowerCase();
    if (CUSTOM_COLUMN_LABELS[key]) return CUSTOM_COLUMN_LABELS[key];
    if (CUSTOM_COLUMN_LABELS[lower]) return CUSTOM_COLUMN_LABELS[lower];

    return key
      .replace(/^cat_/i, '')          // 👈 Strips 'cat_' prefix
      .replace(/_/g, ' ')             // 👈 Replaces underscores with spaces
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  // Edit modal state
  const [editingInvoice, setEditingInvoice] = useState<InvoiceRecord | null>(null);
  const [showScanner, setShowScanner] = useState(false);
  const [productDropdownId, setProductDropdownId] = useState<string | null>(null);

  // Close catalog dropdown on outside click
  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.edit-product-search-container')) {
        setProductDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, []);

  const handleScanAdd = (
    description: string,
    qty: number,
    rate: number,
    barcode?: string,
    taxInfo?: { taxRate?: number; b2bTaxRate?: number; taxInclusive?: boolean }
  ) => {
    setEditLineItems(prev => {
      const existing = prev.findIndex(li =>
        li.description?.toLowerCase().trim() === description.toLowerCase().trim()
      );
      if (existing >= 0) {
        return prev.map((li, i) =>
          i === existing ? { ...li, qty: (parseFloat(String(li.qty)) || 1) + qty } : li
        );
      }
      const withoutEmpty = prev.filter(li => li.description?.trim() !== '');
      const info = getItemTaxInfo({ description, rate, barcode, ...taxInfo }, isEditB2B);
      return [...withoutEmpty, {
        id: `li-${Date.now()}-${Math.random()}`,
        description,
        arabicDescription: '',
        qty,
        rate,
        barcode,
        taxRate: info.effectiveRate,
        tax_rate: info.effectiveRate,
        taxName: info.effectiveName,
        tax_name: info.effectiveName,
        b2bTaxRate: info.b2bRate,
        b2b_tax_rate: info.b2bRate,
        b2bTaxName: info.b2bName,
        b2b_tax_name: info.b2bName,
        taxInclusive: info.isInclusive,
        tax_inclusive: info.isInclusive,
      }];
    });
  };

  // Initialized at the top so getItemTaxInfo and editLineBases can access them safely
  const editSellerBuyerInfo = React.useMemo(() => getSellerBuyerInfo(editingInvoice), [editingInvoice]);
  const editTaxScheme = React.useMemo(
    () => getDefaultSchemeForCountryAndRegion(editSellerBuyerInfo.sellerCountry, editSellerBuyerInfo.sellerRegion),
    [editSellerBuyerInfo]
  );
  const editCustomTaxScheme = (editingInvoice as any)?.custom_tax_scheme || (editingInvoice as any)?.customTaxScheme;

  // Edit modal local form state (pre-filled from invoice)
  const [editClientName, setEditClientName] = useState('');
  const [editClientEmail, setEditClientEmail] = useState('');
  const [editClientPhone, setEditClientPhone] = useState('');
  const [editClientAddress, setEditClientAddress] = useState('');
  const [editTaxId, setEditTaxId] = useState('');
  const [editIncludeFields, setEditIncludeFields] = useState({
    name: true,
    phone: true,
    email: true,
    address: true,
    taxId: true,
  });
  const isEditB2B = editTaxId.trim().length > 0; // 👈 100% driven by whether Tax ID is entered

  const editTaxIdMeta = React.useMemo(
    () => inspectTaxId(editTaxId, editSellerBuyerInfo.sellerCountry || 'IN'),
    [editTaxId, editSellerBuyerInfo.sellerCountry]
  );
  const [catalogProducts, setCatalogProducts] = useState<any[]>([]);
  const [showEditAddCol, setShowEditAddCol] = useState(false);
  const [newEditColName, setNewEditColName] = useState('');

  const BUILTIN_CATALOG_COLS = [
    { key: 'cat_barcode', label: 'Barcode', header: 'Barcode', get: (p: any) => p.barcode || '' },
    { key: 'cat_brand', label: 'Brand', header: 'Brand', get: (p: any) => p.brand || '' },
    { key: 'cat_category', label: 'Category', header: 'Category', get: (p: any) => p.category || '' },
    { key: 'cat_unit', label: 'Unit', header: 'Unit', get: (p: any) => p.unit || '' },
    { key: 'cat_stock', label: 'Stock', header: 'Stock', get: (p: any) => p.stock_quantity != null ? String(p.stock_quantity) : '' },
    { key: 'cat_description', label: 'Description', header: 'Description', get: (p: any) => p.description || '' },
  ];

  const getCatalogColValue = (p: any, col: { key: string; header: string }): string => {
    if (!p) return '';
    const builtin = BUILTIN_CATALOG_COLS.find(c => c.key === col.key);
    if (builtin) return builtin.get(p);
    let fields: any = {};
    if (typeof p.custom_fields === 'string') {
      try { fields = JSON.parse(p.custom_fields); } catch {}
    } else if (p.custom_fields && typeof p.custom_fields === 'object') {
      fields = p.custom_fields;
    }
    if (fields[col.key] != null && fields[col.key] !== '') return String(fields[col.key]);
    if (fields[col.header] != null && fields[col.header] !== '') return String(fields[col.header]);
    const norm = col.header.toLowerCase().replace(/[\s_-]/g, '');
    for (const [k, v] of Object.entries(fields)) {
      if (k.toLowerCase().replace(/[\s_-]/g, '') === norm && v != null && v !== '') return String(v);
    }
    return '';
  };

  const catalogColumnOptions = useMemo(() => {
    const customKeys = new Set<string>();
    catalogProducts.forEach((p: any) => {
      let fields = p.custom_fields;
      if (typeof fields === 'string') {
        try { fields = JSON.parse(fields); } catch {}
      }
      if (fields && typeof fields === 'object') {
        Object.keys(fields).forEach(k => customKeys.add(k));
      }
    });
    const builtinLabels = new Set(BUILTIN_CATALOG_COLS.map(c => c.label.toLowerCase()));
    return [
      ...BUILTIN_CATALOG_COLS.map(c => ({ key: c.key, label: c.label, header: c.header })),
      ...Array.from(customKeys)
        .filter(k => !builtinLabels.has(k.toLowerCase()))
        .map(k => ({ key: k.toLowerCase().replace(/\s+/g, '_'), label: k, header: k })),
    ];
  }, [catalogProducts]);

  const [editStatus, setEditStatus] = useState('');
  const [editAmountPaid, setEditAmountPaid] = useState<number>(0);
  const [editNotes, setEditNotes] = useState('');
  const [editLineItems, setEditLineItems] = useState<{ id: string; description: string; arabicDescription: string; qty: number; rate: number }[]>([]);
  const [editHiddenColumns, setEditHiddenColumns] = useState<string[]>([]);

  // ── Auto-detect all columns from invoice table (matches POS 1:1) ──
  const editTableCols: any[] = useMemo(() => {
    if (!editingInvoice) return [];
    const tableEl = (editingInvoice.elements || []).find((el: any) => el.type === 'table');
    return (tableEl as any)?.columns || [];
  }, [editingInvoice]);

  const editMainColKey = useMemo(() => {
    return (editTableCols.find((c: any) => c.key === 'description') || editTableCols[0])?.key || 'description';
  }, [editTableCols]);

  // Only show columns that actually exist on the invoice table — never dump raw database fields!
  const editToggleableCols: { key: string; label: string }[] = useMemo(() => {
    const internalKeys = [
      'id', 'description', 'arabicName', 'arabicDescription',
      'taxRate', 'tax_rate', 'taxName', 'tax_name',
      'b2bTaxRate', 'b2b_tax_rate', 'b2bTaxName', 'b2b_tax_name',
      'taxInclusive', 'tax_inclusive'
    ];

    return editTableCols
      .filter((c: any) => c.key && c.key !== editMainColKey && !internalKeys.includes(c.key))
      .map((c: any) => ({
        key: c.key,
        // 👈 Cleans raw headers so 'cat_stock' turns into 'Stock'
        label: formatColumnLabel(c.originalHeader || c.header || c.key),
      }));
  }, [editTableCols, editMainColKey]);

  const handleDeleteColumn = (colKey: string) => {
    setEditLineItems(prev => prev.map(li => {
      const { [colKey]: _removed, ...rest } = li as any;
      return rest;
    }));
    setEditShownCustomKeys(prev => prev.filter(k => k !== colKey));
  };

  // Robust Tax Resolver: extracts B2C/B2B rate & name from Catalog, b2bTaxRate, cat_tax_b2b, or custom tax schemes
  const getItemTaxInfo = (li: any, isB2B: boolean) => {
    const match = catalogProducts.find((p: any) =>
      (li.barcode && p.barcode === li.barcode) ||
      (p.name && p.name.trim().toLowerCase() === (li.description || '').trim().toLowerCase())
    );

    // 1. Resolve B2C Rate & Name
    let b2cRate = match?.tax_rate ?? li.taxRate ?? li.tax_rate;
    if (b2cRate === undefined && li.cat_tax_b2c) {
      b2cRate = parseFloat(String(li.cat_tax_b2c).replace('%', ''));
    }
    b2cRate = parseFloat(String(b2cRate ?? 0)) || 0;
    const b2cName = match?.tax_name || li.taxName || li.tax_name || li.cat_tax_name || (editCustomTaxScheme as any)?.name || editTaxScheme?.components?.[0]?.name || 'Tax';

    // 2. Resolve B2B Rate & Name
    let rawB2bRate = match?.b2b_tax_rate ?? li.b2bTaxRate ?? li.b2b_tax_rate;
    if ((rawB2bRate === undefined || rawB2bRate === null || String(rawB2bRate).trim() === '') && li.cat_tax_b2b) {
      rawB2bRate = parseFloat(String(li.cat_tax_b2b).replace('%', ''));
    }
    const hasB2bRate = rawB2bRate !== undefined && rawB2bRate !== null && String(rawB2bRate).trim() !== '';
    const b2bRate = hasB2bRate ? (parseFloat(String(rawB2bRate)) || 0) : b2cRate;

    const b2bName = match?.b2b_tax_name || li.b2bTaxName || li.b2b_tax_name || (editCustomTaxScheme as any)?.b2b_name || (hasB2bRate ? `${b2cName} B2B` : b2cName);

    const effectiveRate = isB2B ? b2bRate : b2cRate;
    const effectiveName = isB2B ? b2bName : b2cName;
    const isInclusive = Boolean(li.taxInclusive ?? li.tax_inclusive ?? match?.tax_inclusive);

    return { b2cRate, b2cName, b2bRate, b2bName, effectiveRate, effectiveName, isInclusive };
  };

  // Load catalog products for the invoice's outlet
  useEffect(() => {
    if (!editingInvoice) return;
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
    const outletId = (editingInvoice as any)?.outlet_id;
    const qs = outletId ? `?outlet_id=${outletId}` : '';
    fetch(`${BASE}/products/${qs}`, {
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCatalogProducts(data);
          // Re-sync line items with catalog once products arrive
          setEditLineItems(prev => prev.map(li => {
            const match = data.find((p: any) =>
              (li.barcode && p.barcode === li.barcode) ||
              (p.name && p.name.trim().toLowerCase() === (li.description || '').trim().toLowerCase())
            );
            if (!match) return li;
            const b2cRate = match.tax_rate ?? 0;
            const b2cName = match.tax_name || 'Tax';
            const b2bRate = (match.b2b_tax_rate !== undefined && match.b2b_tax_rate !== null && String(match.b2b_tax_rate).trim() !== '') ? Number(match.b2b_tax_rate) : b2cRate;
            const b2bName = match.b2b_tax_name || (match.b2b_tax_rate !== undefined ? `${b2cName} B2B` : b2cName);
            return {
              ...li,
              taxRate: isEditB2B ? b2bRate : b2cRate,
              tax_rate: isEditB2B ? b2bRate : b2cRate,
              taxName: isEditB2B ? b2bName : b2cName,
              tax_name: isEditB2B ? b2bName : b2cName,
              b2bTaxRate: b2bRate,
              b2b_tax_rate: b2bRate,
              b2bTaxName: b2bName,
              b2b_tax_name: b2bName,
              taxInclusive: match.tax_inclusive ?? li.taxInclusive,
              tax_inclusive: match.tax_inclusive ?? li.tax_inclusive,
            };
          }));
        }
      })
      .catch(() => {});
  }, [editingInvoice?.id, isEditB2B]);

  // Automatically re-sync item rates whenever Tax ID is added or cleared
  useEffect(() => {
    setEditLineItems(prev => prev.map((li: any) => {
      const info = getItemTaxInfo(li, isEditB2B);
      return {
        ...li,
        taxRate: info.effectiveRate,
        tax_rate: info.effectiveRate,
        taxName: info.effectiveName,
        tax_name: info.effectiveName,
        b2bTaxRate: info.b2bRate,
        b2b_tax_rate: info.b2bRate,
        b2bTaxName: info.b2bName,
        b2b_tax_name: info.b2bName,
      };
    }));
  }, [isEditB2B]);
  const [editTaxLines, setEditTaxLines] = useState<{ label: string; rate: number }[]>([]);
  const [editDiscountLines, setEditDiscountLines] = useState<{ label: string; rate: number }[]>([]);
  const [editShipping, setEditShipping] = useState(0);
  const [editCurrency, setEditCurrency] = useState('USD');
  const [editTranslatedValues] = useState<Record<string, string>>({});
  const [editZoom, setEditZoom] = useState(1.3);
const [editShowStamp, setEditShowStamp] = useState(true);
const [editPaymentMode, setEditPaymentMode] = useState<'cash' | 'card' | 'upi'>('cash');
const [editingInvoiceHasToken, setEditingInvoiceHasToken] = useState(false);
const [editShownCustomKeys, setEditShownCustomKeys] = useState<string[]>([]);

const editLeftRef = React.useRef<HTMLDivElement>(null);
const editCanvasWorkspaceRef = React.useRef<HTMLDivElement>(null);
const editDragging = React.useRef(false);
const editStartX = React.useRef(0);
const editStartW = React.useRef(0);

const onEditDividerMouseDown = (e: React.MouseEvent) => {
  editDragging.current = true;
  editStartX.current = e.clientX;
  editStartW.current = editLeftRef.current?.offsetWidth ?? 490;
  document.body.style.cursor = 'col-resize';
  document.body.style.userSelect = 'none';

  const onMove = (ev: MouseEvent) => {
    if (!editDragging.current || !editLeftRef.current) return;
    const dx = ev.clientX - editStartX.current;
    const newW = Math.min(Math.max(editStartW.current + dx, 380), 850);
    editLeftRef.current.style.width = `${newW}px`;
  };
  const onUp = () => {
    editDragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  };
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
};
  // Open edit modal — pre-fill all fields from saved invoice
  const openEdit = (bill: InvoiceRecord) => {
    const tableEl = bill.elements.find(el => el.type === 'table');
    const savedCols = (tableEl?.columns || []) as any[];
    // Remember which standard columns were previously removed
    const previouslyHiddenCols = ['qty', 'rate', 'total'].filter(key =>
      savedCols.length > 0 && !savedCols.some((c: any) => c.key === key)
    );
    setEditHiddenColumns(previouslyHiddenCols);

    // ── Restore saved ticks / unticks from POS or Quick Invoice ──
    const savedInc =
      (bill as any).includeFields ||
      (bill as any).include_fields ||
      (bill.elements?.[0] as any)?.includeFields ||
      (bill.elements?.[0] as any)?.include_fields;

    if (savedInc && typeof savedInc === 'object') {
      setEditIncludeFields({
        name: savedInc.name !== false,
        phone: savedInc.phone !== false,
        email: savedInc.email !== false,
        address: savedInc.address !== false,
        taxId: savedInc.taxId !== false,
      });
    } else {
      setEditIncludeFields({
        name: true,
        phone: true,
        email: true,
        address: true,
        taxId: true,
      });
    }

    setEditClientName(bill.clientName || (bill as any).client_name || '');
    setEditClientEmail(bill.clientEmail || (bill as any).client_email || '');

    const tokenFromOrder = (bill as any).order_token || (bill as any).orderToken;
    const tokenFromElements = bill.elements?.find((el: any) =>
      el.id?.startsWith('token-label-') || (el.type === 'text' && /token:\s*(\w+)/i.test(el.textContent || ''))
    )?.textContent?.match(/token:\s*(\w+)/i)?.[1];
    const resolvedToken = tokenFromOrder || tokenFromElements || '';

    const hasToken = Boolean(
      resolvedToken ||
      (bill as any).show_tracking_qr ||
      (bill as any).showTrackingQR ||
      bill.elements.some((el: any) =>
        el.id?.startsWith('token-qr-') ||
        el.id?.startsWith('token-label-') ||
        el._auto === 'tracking' ||
        el._auto === 'tracking_label'
      )
    );
    setEditingInvoiceHasToken(hasToken);
    // Try to extract client address from Bill To text element
const billToEl = bill.elements.find(el =>
  el.type === 'text' && el.textContent &&
  (el.textContent.toLowerCase().startsWith('bill to') || el.textContent.toLowerCase().startsWith('client:'))
);
const billToLines = billToEl?.textContent?.split('\n') || [];
const isTaxOrContactLine = (l: string) =>
  l.includes('@') ||
  /[\d\+\-\(\)\s]{6,}/.test(l) ||
  /(gstin|tax\s*id|vat|trn|pan|npwp)/i.test(l);

const extractedAddress = billToLines.slice(2).filter((l: string) => !isTaxOrContactLine(l)).join(', ');

// Extract and clean Arabic name & address
const billToArLines = billToEl?.arabicContent?.split('\n') || [];
const rawArAddr =
  (bill as any).dynamicValuesAr?.client_address ||
  (bill as any).dynamic_values_ar?.client_address ||
  billToArLines.slice(2).filter((l: string) => !isTaxOrContactLine(l)).join(', ') ||
  '';

const existingArAddr = rawArAddr
  .split(/[،,]/)
  .filter(part => !/(gstin|tax\s*id|vat|trn|pan|تاكس|غستين|ضريب)/i.test(part))
  .join('، ')
  .trim();

const existingArName =
  (bill as any).dynamicValuesAr?.client_name ||
  (bill as any).dynamic_values_ar?.client_name ||
  billToArLines[1] ||
  '';

setEditTranslatedFields({
  clientName: existingArName,
  clientAddress: existingArAddr,
});

const extractedPhone = billToLines.find((l: string) => /[\d\+\-\(\)\s]{6,}/.test(l) && !l.includes('@')) || '';
setEditClientPhone((bill as any).clientPhone || (bill as any).client_phone || extractedPhone || '');
    setEditClientAddress((bill as any).clientAddress || (bill as any).client_address || extractedAddress || '');
    const initialTaxId = getInvoiceTaxId(bill) || '';
    setEditTaxId(initialTaxId);
    setEditStatus(bill.status || 'Draft');
    setEditPaymentMode(((bill as any).payment_mode as any) || 'cash');
    setEditAmountPaid(bill.amountPaid || 0);
    setEditNotes((bill as any).notes || '');
    setEditCurrency(bill.currency || activeCurrency || 'USD');
    setEditShipping((tableEl as any)?.shippingCost || 0);
    setEditLineItems(
      (tableEl?.rows || []).map((r: any) => {
        const rawVals = r.values || {};
        const parsedQty = parseFloat(String(rawVals.qty)) || 1;
        const parsedRate = parseFloat(String(rawVals.rate ?? rawVals.unit_price ?? rawVals.price ?? 0)) || 0;
        const parsedTaxRate = parseFloat(String(rawVals.taxRate ?? rawVals.tax_rate ?? 0).replace('%', '')) || 0;
        const rawB2b = rawVals.b2bTaxRate ?? rawVals.b2b_tax_rate;
        const parsedB2bTaxRate = rawB2b !== undefined && rawB2b !== null
          ? (parseFloat(String(rawB2b).replace('%', '')) || 0)
          : undefined;

        return {
          ...rawVals,
          id: r.id || `li-${Date.now()}-${Math.random()}`,
          description: rawVals.description || '',
          arabicDescription: rawVals.arabicDescription || rawVals.arabicName || '',
          qty: parsedQty,
          rate: parsedRate,
          taxRate: parsedTaxRate,
          tax_rate: parsedTaxRate,
          ...(parsedB2bTaxRate !== undefined ? { b2bTaxRate: parsedB2bTaxRate, b2b_tax_rate: parsedB2bTaxRate } : {}),
          ...((tableEl?.columns || []) as any[]).reduce((acc: any, c: any) => {
            const cur = rawVals[c.key];
            if (cur !== undefined && cur !== null && cur !== '') return acc;
            if (c.key === 'cat_tax_b2c' || c.key === 'cat_tax_b2b') acc[c.key] = parsedTaxRate ? `${parsedTaxRate}%` : '';
            else if (c.key === 'cat_tax_name') acc[c.key] = rawVals.taxName ?? rawVals.tax_name ?? '';
            else if (c.key === 'cat_tax_type') acc[c.key] = (rawVals.taxInclusive ?? rawVals.tax_inclusive) ? 'Inclusive' : 'Exclusive';
            return acc;
          }, {}),
        };
      })
    );
    const taxes = (tableEl as any)?.taxes || [];
    const discounts = (tableEl as any)?.discounts || [];
    setEditTaxLines(
      taxes.length > 0
        ? taxes.map((t: any) => ({
            ...t,
            rate: parseFloat(String(t.rate ?? 0).replace('%', '')) || 0,
          }))
        : [{ label: 'Tax', rate: 0 }]
    );
    setEditDiscountLines(
      discounts.length > 0
        ? discounts.map((d: any) => ({
            ...d,
            rate: parseFloat(String(d.rate ?? 0).replace('%', '')) || 0,
          }))
        : [{ label: 'Discount', rate: parseFloat(String((tableEl as any)?.discountRate ?? 0).replace('%', '')) || 0 }]
    );
    const existingCustomColKeys = (tableEl?.columns || [])
      .map((c: any) => c.key)
      .filter((k: string) => !INTERNAL_ITEM_KEYS.includes(k));
    setEditShownCustomKeys(existingCustomColKeys);
    const dedupedBill = JSON.parse(JSON.stringify(bill));
const seen = new Set<string>();
// Remove duplicate appended QR/label elements and legacy client duplicates
dedupedBill.elements = dedupedBill.elements.filter((el: any) => {
  if (el.id?.startsWith('synth-client-block') || el.id?.startsWith('client-info-')) return false;
  const key = el.id?.startsWith('invoice-qr-') ? 'invoice-qr'
            : el.id?.startsWith('invoice-label-') ? 'invoice-label'
            : el.id?.startsWith('token-qr-') ? 'token-qr'
            : el.id?.startsWith('token-label-') ? 'token-label'
            : null;
  if (!key) return true;
  if (seen.has(key)) return false;
  seen.add(key);
  return true;
});
let seenInvoiceQr = false;
dedupedBill.elements = dedupedBill.elements.filter((el: any) => {
  if (el.id?.startsWith('invoice-qr-')) {
    if (seenInvoiceQr) return false;
    seenInvoiceQr = true;
  }
  return true;
});

// Only keep bilingual view if invoice was explicitly translated
const wasTranslated = (bill as any).isBilingual === true;
if (!wasTranslated) {
  dedupedBill.elements = dedupedBill.elements.map((el: any) => {
    const { arabicContent, ...rest } = el;
    return rest;
  });
}

// Keep saved auto QR and tracking QR elements for existing invoices
dedupedBill.elements = dedupedBill.elements.filter((el: any) => {
  if (el._auto === 'tracking' || el._auto === 'tracking_label' || el.id?.startsWith('token-')) {
    return hasToken;
  }
  return true;
});

// 1. Ensure Invoice QR exists
const publicToken = (bill as any).publicToken || (bill as any).public_token || bill.id;
const hasExistingInvoiceQr = dedupedBill.elements.some((el: any) =>
  el._auto === 'invoice' || el.id?.startsWith('invoice-qr-')
);
if (!hasExistingInvoiceQr && publicToken) {
  const isThermal = (bill.pageSize || '').startsWith('THERMAL');
  const qrPos = (bill as any).qr_position || (bill as any).autoQrPosition || 'center';
  const pageWidth = isThermal ? 302 : (bill.pageSize === 'LETTER' ? 816 : 794);
  const qrSize = 100;
  const qrX = isThermal ? (pageWidth - qrSize) / 2
    : qrPos === 'left' ? 60
    : qrPos === 'right' ? (pageWidth - qrSize - 60)
    : (pageWidth - qrSize) / 2;

  dedupedBill.elements.push(
    {
      id: `invoice-qr-${bill.id}`,
      type: 'qr',
      _auto: 'invoice',
      qrValue: `${window.location.origin}/invoice/view/${publicToken}`,
      x: qrX,
      y: 600,
      width: qrSize,
      height: qrSize,
    },
    {
      id: `invoice-label-${bill.id}`,
      type: 'text',
      _auto: 'invoice_label',
      textContent: 'Scan to view invoice',
      arabicContent: 'مسح لعرض الفاتورة',
      x: qrX - 25,
      y: 710,
      width: 150,
      height: 20,
      fontSize: 9,
      textAlign: 'center',
      color: '#000000',
    }
  );
}

// 2. Ensure Tracking QR exists if bill has a token
if (hasToken && resolvedToken) {
  const hasExistingTrackingQr = dedupedBill.elements.some((el: any) =>
    el._auto === 'tracking' || el.id?.startsWith('token-qr-')
  );
  if (!hasExistingTrackingQr) {
    dedupedBill.elements.push(
      {
        id: `token-qr-${bill.id}`,
        type: 'qr',
        _auto: 'tracking',
        qrValue: `${window.location.origin}/wait?token=${resolvedToken}`,
        x: 420,
        y: 600,
        width: 100,
        height: 100,
      },
      {
        id: `token-label-${bill.id}`,
        type: 'text',
        _auto: 'tracking_label',
        textContent: `Token: ${resolvedToken} — Scan to track order`,
        arabicContent: `${resolvedToken} — مسح لتتبع الطلب`,
        x: 420,
        y: 710,
        width: 200,
        height: 20,
        fontSize: 9,
        color: '#000000',
      }
    );
  }
}

const allEditQrs = dedupedBill.elements.filter((el: any) => el.type === 'qr' || el.type === 'image');
const invoiceQrElAlign = allEditQrs.find((q: any) => q.id?.startsWith('invoice-qr-'));
const tokenQrElAlign = allEditQrs.find((q: any) => q.id?.startsWith('token-qr-'));

if (invoiceQrElAlign && tokenQrElAlign) {
  tokenQrElAlign.y = invoiceQrElAlign.y;
}

const invoiceLabelElAlign = dedupedBill.elements.find((el: any) =>
  el.type === 'text' && el.id?.startsWith('invoice-label-')
);
const tokenLabelElAlign = dedupedBill.elements.find((el: any) =>
  el.type === 'text' && el.id?.startsWith('token-label-')
);

const EDIT_QR_SIZE = 105;

if (invoiceQrElAlign) {
  invoiceQrElAlign.width = EDIT_QR_SIZE;
  invoiceQrElAlign.height = EDIT_QR_SIZE;
}
if (tokenQrElAlign) {
  tokenQrElAlign.width = EDIT_QR_SIZE;
  tokenQrElAlign.height = EDIT_QR_SIZE;
}

if (invoiceQrElAlign && tokenQrElAlign) {
  tokenQrElAlign.y = invoiceQrElAlign.y;
}

if (invoiceQrElAlign && invoiceLabelElAlign) {
  const lWidth = Math.max(invoiceLabelElAlign.width || 0, EDIT_QR_SIZE + 40, 150);
  invoiceLabelElAlign.y = invoiceQrElAlign.y + EDIT_QR_SIZE + 8;
  invoiceLabelElAlign.width = lWidth;
  invoiceLabelElAlign.x = invoiceQrElAlign.x + Math.round((EDIT_QR_SIZE - lWidth) / 2);
  invoiceLabelElAlign.textAlign = 'center';
  invoiceLabelElAlign.fontSize = 10.5;
}

if (tokenQrElAlign && tokenLabelElAlign) {
  const lWidth = Math.max(tokenLabelElAlign.width || 0, EDIT_QR_SIZE + 40, 200);
  tokenLabelElAlign.y = tokenQrElAlign.y + EDIT_QR_SIZE + 8;
  tokenLabelElAlign.width = lWidth;
  tokenLabelElAlign.x = tokenQrElAlign.x + Math.round((EDIT_QR_SIZE - lWidth) / 2);
  tokenLabelElAlign.textAlign = 'center';
  tokenLabelElAlign.fontSize = 10.5;
}

setTimeout(() => {
  setEditingInvoice(dedupedBill);
  setEditZoom(window.innerWidth < 768 ? 0.35 : 1.3);
}, 0);
  };
 const isArabicScript = (lang?: string) => ['ar', 'ur', 'fa', 'ps'].includes(lang || '');
 const toArNums = (s: string, lang?: string) => isArabicScript(lang) ? s.replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[+d]) : s;
  // Compute totals from edit line items
  // Extract net (pre-tax) base per line first — for tax-inclusive items,
  // `rate` already contains the tax, so subtotal must strip it out before
  // summing, otherwise inclusive-tax lines get taxed twice.
  const editLineBases = editLineItems.map((li: any) => {
    const lineTotal = li.qty * li.rate;
    const info = getItemTaxInfo(li, isEditB2B);
    const taxRate = info.effectiveRate;
    const inclusive = info.isInclusive;
    const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
    return { base, taxRate };
  });
  const editSubtotal = editLineBases.reduce((s, lb) => s + lb.base, 0);

  // Auto/loyalty discount lines are stored as a % of (subtotal - manual
  // discounts), not raw subtotal — POSView computes and freezes that rate
  // at save time (impliedRate = autoDiscountAmount / (subtotal - discTotal)).
  // Applying it against raw editSubtotal here re-inflates the discount and
  // silently changes the total every time this invoice is reopened.
  const editManualDiscountLines = editDiscountLines.filter((d: any) => !d._isAuto);
  const editAutoDiscountLine = editDiscountLines.find((d: any) => d._isAuto);
  const editManualDiscount = editManualDiscountLines.reduce((s, d) => s + editSubtotal * (d.rate / 100), 0);
  const editAutoDiscount = editAutoDiscountLine
    ? (editSubtotal - editManualDiscount) * (editAutoDiscountLine.rate / 100)
    : 0;
  const editDiscTotal = editManualDiscount + editAutoDiscount;

  // Discount reduces the taxable base before tax is calculated — same
  // standard GST/VAT math as every other totals block in the app.
  const editDiscountRatio = editSubtotal > 0 ? Math.min(1, editDiscTotal / editSubtotal) : 0;
  const editTaxableValue = editSubtotal - editDiscTotal;
  const editTaxTotal = editTaxLines.reduce((s, t) => s + editTaxableValue * (t.rate / 100), 0);
  const editProductTaxTotal = editLineBases.reduce(
    (sum, lb) => sum + lb.base * (1 - editDiscountRatio) * (lb.taxRate / 100),
    0
  );
  const editTotal = editTaxableValue + editProductTaxTotal + editTaxTotal + editShipping;
const editInvoiceTime = editingInvoice
  ? parseUtcDate(editingInvoice.createdAt).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  : '';

const editTargetLang = React.useMemo(
  () =>
    (editingInvoice as any)?.translate_target_lang ||
    (editingInvoice as any)?.translated_lang ||
    (editingInvoice as any)?.target_lang ||
    editingInvoice?.elements?.find((el: any) => el.translatedLang)?.translatedLang ||
    'en',
  [editingInvoice]
);

const editIsBilingual = React.useMemo(() => {
  const flagVal = (editingInvoice as any)?.isBilingual || (editingInvoice as any)?.bilingual_mode;
  const hasTranslated = editingInvoice?.elements?.some(
    (el: any) => !!el.arabicContent || !!el.translatedContent || !!el.translatedLang
  ) ?? false;
  return (flagVal === true || hasTranslated) && editTargetLang !== 'en';
}, [editingInvoice, editTargetLang]);

const [editTranslatedFields, setEditTranslatedFields] = useState<Record<string, string>>({});
const editTranslateTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

// Clean up all pending translation timers when modal closes to prevent memory leaks
useEffect(() => {
  return () => {
    Object.values(editTranslateTimers.current).forEach(t => clearTimeout(t));
    editTranslateTimers.current = {};
  };
}, [editingInvoice?.id]);

// Translate custom column text values (e.g. "Seasonal demand" -> "الطلب الموسمي")
const [editTranslatedColVals, setEditTranslatedColVals] = useState<Record<string, string>>({});
useEffect(() => {
  if (!editIsBilingual || !editTargetLang || editTargetLang === 'en') {
    setEditTranslatedColVals({});
    return;
  }
  let cancelled = false;
  (async () => {
    const uniqVals = new Set<string>();
    editLineItems.forEach(li => {
      Object.entries(li).forEach(([k, v]) => {
        if (isEditableItemKey(k) && typeof v === 'string') {
          const str = v.trim();
          if (str && !/^[\d.,%\s+\-]+$/.test(str)) {
            uniqVals.add(str);
          }
        }
      });
    });

    if (uniqVals.size === 0) return;

    // Batch translate all unique custom column values in 1 single API call
    const valList = Array.from(uniqVals);
    const uncachedVals = valList.filter(
      v => !localStorage.getItem(`cloudairambo_value_cache_${editTargetLang}_${v}`)
    );

    let batchMap: Record<string, string> = {};
    if (uncachedVals.length > 0) {
      const translatedBatch = await translateBatchBills(uncachedVals, editTargetLang);
      uncachedVals.forEach((orig, idx) => {
        const tr = (translatedBatch[idx] || '').trim();
        batchMap[orig] = tr || orig;
      });
    }

    const pairs = await Promise.all(
      valList.map(async val => {
        const cacheKey = `cloudairambo_value_cache_${editTargetLang}_${val}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) return [val, cached] as const;

        let tr = batchMap[val] || val;
        if (!tr || tr.toLowerCase() === val.toLowerCase()) {
          tr = await transliterateName(val, editTargetLang);
        }
        if (tr) localStorage.setItem(cacheKey, tr);
        return [val, tr || val] as const;
      })
    );

    if (!cancelled) {
      setEditTranslatedColVals(Object.fromEntries(pairs.filter(([, v]) => !!v)));
    }
  })();
  return () => { cancelled = true; };
}, [editLineItems, editIsBilingual, editTargetLang]);
// Named CGST/SGST/IGST breakdown for the edit-modal preview — derived
// from the invoice's saved seller/buyer region, recomputed live as line items/discounts change.
const editTaxBreakdownEntries = React.useMemo(
  () => computeNamedTaxBreakdown(
    editLineItems as any,
    editDiscountLines,
    editTaxScheme,
    editSellerBuyerInfo.sellerRegion,
    editSellerBuyerInfo.buyerRegion,
    isEditB2B,
    editCustomTaxScheme
  ),
  [editLineItems, editDiscountLines, editTaxScheme, editSellerBuyerInfo, isEditB2B, editCustomTaxScheme]
);
const [editTaxBreakdownAr, setEditTaxBreakdownAr] = useState<Record<string, string>>({});
useEffect(() => {
  if (!editIsBilingual || !editTaxBreakdownEntries || editTaxBreakdownEntries.length === 0) {
    setEditTaxBreakdownAr({});
    return;
  }
  let cancelled = false;
  (async () => {
    const pairs = await Promise.all(
      editTaxBreakdownEntries.map(async e => [e.name, await transliterateName(e.name, editTargetLang)] as const)
    );
    if (!cancelled) setEditTaxBreakdownAr(Object.fromEntries(pairs));
  })();
  return () => { cancelled = true; };
}, [editIsBilingual, editTargetLang, editTaxBreakdownEntries]);
const editTranslateOne = async (text: string, key: string, _isRetry = false) => {
  if (!text?.trim()) { setEditTranslatedFields(prev => ({ ...prev, [key]: '' })); return; }
  try {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

    // Same per-part translation strategy BatchInvoiceGenerator's
    // translateCached uses — a comma-joined address like "Connaught
    // Place, New Delhi, Delhi" often gets echoed back untranslated
    // whole, but each part translates cleanly on its own.
    const translateOnePart = async (raw: string): Promise<string> => {
      const res = await fetch(
        `${BASE}/translate?text=${encodeURIComponent(raw.trim())}&tl=${editTargetLang}`,
        { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
      );
      if (!res.ok) throw new Error(`translate failed: ${res.status}`);
      const data = await res.json();
      const translated = data?.translated || raw;
      // Names, company names, and place names (client name, address) are
      // often echoed back UNCHANGED by /translate — "Acme Tech Solutions"
      // isn't a dictionary word, so Google Translate has nothing to
      // translate and just returns it as-is. Same echo problem already
      // handled for amount-in-words and cashier/pharmacist names
      // elsewhere in this file — fall back to /transliterate (phonetic
      // Arabic script) whenever the "translation" is identical to the
      // English input.
      const echoed = translated.trim().toLowerCase() === raw.trim().toLowerCase();
      return echoed ? await transliterateName(raw, editTargetLang) : translated;
    };

    const isRtl = ['ar', 'ur', 'fa', 'ps', 'he', 'iw', 'ug', 'ckb', 'sd'].includes(editTargetLang);
    let finalValue: string;
    if (text.includes(',') && !/@/.test(text)) {
      const parts = text.split(',').map(p => p.trim()).filter(Boolean);
      const translatedParts = await Promise.all(parts.map(translateOnePart));
      finalValue = translatedParts.join(isRtl ? '، ' : ', ');
    } else {
      finalValue = await translateOnePart(text);
    }

    setEditTranslatedFields(prev => ({ ...prev, [key]: finalValue }));
  } catch {
    // One retry before giving up — a single flaky request must never
    // leave this key holding a PREVIOUS invoice's stale translated value
    // (openEdit now clears editTranslatedFields on every open, but a
    // failed retry-of-one still shouldn't silently keep old data around
    // for THIS invoice either). If the retry also fails, explicitly fall
    // back to the live English value rather than leaving the field unset.
    if (!_isRetry) {
      await editTranslateOne(text, key, true);
    } else {
      setEditTranslatedFields(prev => ({ ...prev, [key]: text }));
    }
  }
};
const scheduleEditTranslate = (text: string, key: string) => {
  if (!editIsBilingual) return;
  if (editTranslateTimers.current[key]) clearTimeout(editTranslateTimers.current[key]);
  editTranslateTimers.current[key] = setTimeout(() => editTranslateOne(text, key), 700);
};

// Seed editTranslatedFields on open for client name, address, and all labels dynamically for any language
useEffect(() => {
  if (!editIsBilingual || !editingInvoice || !editTargetLang || editTargetLang === 'en') return;
  if (editClientName) editTranslateOne(editClientName, 'clientName');
  if (editClientAddress) editTranslateOne(editClientAddress, 'clientAddress');

  // Dynamically translate all field headers to the active target language
  editTranslateOne('Tax ID', 'taxIdLabel');
  editTranslateOne('Phone', 'phoneLabel');
  editTranslateOne('Email', 'emailLabel');
  editTranslateOne('Address', 'addressLabel');
  editTranslateOne('Walk-in Customer', 'walkInLabel');
}, [editingInvoice?.id, editIsBilingual, editTargetLang]);

// Amount-in-words translation, recomputed live as the total changes —
// works regardless of what (if anything) the backend saved for this field.
// Derived fresh every render (not useState) — the flag must reflect
// whichever invoice is currently open in editingInvoice, and a useState
// initializer only runs once on mount, back when editingInvoice was still
// null. That stale `false` never re-evaluates when openEdit() swaps in a
// different invoice, so it silently showed nothing for every invoice.
const editShowAmountWords = true;

const [editAmountWordsTranslated, setEditAmountWordsTranslated] = useState('');
useEffect(() => {
  if (!editIsBilingual || !editShowAmountWords || !editTargetLang) {
    setEditAmountWordsTranslated('');
    return;
  }

  const timer = setTimeout(async () => {
    try {
      const englishWords = amountToWords(editTotal, editCurrency);
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

      const res = await fetch(
        `${BASE}/translate?text=${encodeURIComponent(englishWords)}&tl=${encodeURIComponent(editTargetLang)}`,
        { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} }
      );

      if (!res.ok) throw new Error('Translation request failed');

      const data = await res.json();
      const translated = (data?.translated || '').trim();

      const isSameAsEnglish =
        translated.toLowerCase() === englishWords.trim().toLowerCase();

      setEditAmountWordsTranslated(
        isSameAsEnglish ? '' : translated
      );
    } catch {
      setEditAmountWordsTranslated('');
    }
  }, 500);

  return () => clearTimeout(timer);
}, [
  editTotal,
  editCurrency,
  editIsBilingual,
  editShowAmountWords,
  editTargetLang
]);
  // Build a live preview invoice record from current edit state
  const liveEditInvoice = React.useMemo((): InvoiceRecord | null => {
    if (!editingInvoice) return null;
    const clone = JSON.parse(JSON.stringify(editingInvoice)) as InvoiceRecord;
    // Update client fields in text elements
    clone.clientName = editClientName;
    clone.clientEmail = editClientEmail;
    clone.status = editStatus;
    clone.currency = editCurrency;
    // ── Build client block using dynamic labels (Zero Hardcoded Strings) ──
    const clientBlock = buildClientBlock(
      {
        name: editClientName,
        phone: editClientPhone,
        email: editClientEmail,
        address: editClientAddress,
        taxId: editTaxId,
        nameTr: editTranslatedFields['clientName'] || editClientName,
        addressTr: editTranslatedFields['clientAddress'] || editClientAddress,
        walkInTr: editTranslatedFields['walkInLabel'],
      },
      editIncludeFields,
      editIsBilingual,
      {
        taxId: editTranslatedFields['taxIdLabel'],
        phone: editTranslatedFields['phoneLabel'],
        email: editTranslatedFields['emailLabel'],
        address: editTranslatedFields['addressLabel'],
      }
    );

    // Patch elements using {client_details} token or any customer details box
    clone.elements = clone.elements.map((el: any) => {
      if (el.type !== 'text' || !el.textContent) return el;

      // 1. Direct {client_details} token replacement
      if (el.textContent.includes('{client_details}')) {
        const text = el.textContent.replace(/\{client_details\}/g, clientBlock.en);
        const textTr = el.arabicContent || el.translatedContent
          ? (el.arabicContent || el.translatedContent).replace(/\{client_details\}/g, clientBlock.tr || clientBlock.en)
          : undefined;
        return {
          ...el,
          textContent: text,
          ...(textTr ? { arabicContent: textTr, translatedContent: textTr, translatedLang: editTargetLang } : {}),
        };
      }

      // 2. Universal target detector: detects "Bill To:", ID tag, or email/phone/address blocks
      const firstLine = el.textContent.split('\n')[0].toLowerCase().trim();
      const hasClientHeader = /^(bill\s*to|client|customer|patient|sold\s*to|seller|buyer|to|recipient|depositor|owner)\s*:/i.test(firstLine);
      const isClientTarget =
        hasClientHeader ||
        el.id === 'auto-injected-client-box' ||
        (el.textContent.includes('Email:') && el.textContent.includes('Phone:')) ||
        (el.textContent.includes('@') && /[\d\+\-\(\)\s]{7,}/.test(el.textContent)) ||
        (Boolean(editClientName) && firstLine === editClientName.trim().toLowerCase());

      if (isClientTarget) {
        const origLines = el.textContent.split('\n');
        const origHeader = hasClientHeader ? origLines[0] : '';
        const newText = origHeader ? `${origHeader}\n${clientBlock.en}` : clientBlock.en;

        if (editIsBilingual) {
          const trLines = (el.arabicContent || el.translatedContent || '').split('\n');
          const origTrHeader = hasClientHeader ? (trLines[0] || origHeader) : '';
          const newTranslated = origTrHeader ? `${origTrHeader}\n${clientBlock.tr || clientBlock.en}` : (clientBlock.tr || clientBlock.en);

          return {
            ...el,
            textContent: newText,
            arabicContent: newTranslated,
            translatedContent: newTranslated,
            translatedLang: editTargetLang,
          };
        }
        return { ...el, textContent: newText };
      }

      return el;
    });
    // Patch table
    const tableEl = clone.elements.find(el => el.type === 'table');
    if (tableEl) {
      tableEl.rows = editLineItems.map(li => {
        const freshArabic = editTranslatedFields[`item_${li.id}`];
        const arabicDesc = editIsBilingual && freshArabic !== undefined ? freshArabic : li.arabicDescription;
        const customAr = Object.fromEntries(
          Object.entries(li)
            .filter(([k, v]) => isEditableItemKey(k) && typeof v === 'string' && (editTranslatedColVals[v.trim()] || (li as any).__customAr?.[k]))
            .map(([k, v]) => [k, editTranslatedColVals[(v as string).trim()] || (li as any).__customAr?.[k] || ''])
        );
        const info = getItemTaxInfo(li, isEditB2B);
        const currentTaxRate = info.effectiveRate;
        const currentTaxName = info.effectiveName;
        const rawB2b = info.b2bRate;

        return {
          id: li.id,
          values: {
            description: li.description,
            arabicDescription: arabicDesc,
            arabicName: arabicDesc,
            qty: li.qty,
            rate: li.rate,
            total: li.qty * li.rate,
            taxRate: currentTaxRate,
            tax_rate: currentTaxRate,
            taxName: currentTaxName,
            tax_name: currentTaxName,
            b2bTaxRate: rawB2b,
            b2b_tax_rate: rawB2b,
            b2bTaxName: li.b2bTaxName || li.b2b_tax_name,
            b2b_tax_name: li.b2bTaxName || li.b2b_tax_name,
            ...Object.fromEntries(
              Object.entries(li).filter(([k]) =>
                !['id','description','arabicDescription','qty','rate','taxRate','tax_rate','taxName','tax_name'].includes(k)
              )
            ),
            __customAr: customAr,
          }
        };
      });
      (tableEl as any).taxes = editTaxLines.filter(t => t.rate > 0);
      (tableEl as any).discounts = editDiscountLines.filter(d => d.rate > 0);
      (tableEl as any).shippingCost = editShipping;
      (tableEl as any).taxBreakdown = editTaxBreakdownEntries || null;
      (tableEl as any).buyer_tax_id = editTaxId.trim() || null;
      (tableEl as any).buyerTaxId = editTaxId.trim() || null;

      tableEl.columns = (tableEl.columns || []).filter((c: any) =>
        !['arabicName', 'arabicDescription', 'taxRate', 'tax_rate', 'taxInclusive', 'tax_inclusive'].includes(c.key) &&
        !['arabicName', 'arabicDescription', 'Arabic Name', 'arabic name', 'taxRate', 'taxInclusive'].includes(c.header) &&
        (c.key === editMainColKey || !editHiddenColumns.includes(c.key))
      );
       const existingColKeys = new Set((tableEl.columns || []).map((c: any) => c.key));
  const customKeys = Object.keys(editLineItems[0] || {}).filter(k =>
    !['id','description','arabicDescription','qty','rate','taxRate','tax_rate','taxInclusive','tax_inclusive'].includes(k) &&
    !existingColKeys.has(k) &&
    editShownCustomKeys.includes(k)
  );
  if (customKeys.length > 0) {
    tableEl.columns = [
      ...(tableEl.columns || []),
      ...customKeys.map(k => ({
        id: `col-${k}`,
        header: formatColumnLabel(k), // 👈 Sets clean label like 'Stock' instead of 'cat_stock'
        key: k,
        align: editLineItems.every(li => {
  const v = (li as any)[k];
  return v === '' || v === undefined || !isNaN(parseFloat(String(v)));
}) ? 'right' : 'left',
        width: 15,
      }))
    ];
  }

  // Also clean any existing table column headers in the live preview
  if (tableEl.columns) {
    tableEl.columns = tableEl.columns.map((c: any) => ({
      ...c,
      header: formatColumnLabel(c.originalHeader || c.header || c.key),
    }));
  }
}
    clone.elements = clone.elements.map((el: any) => {
  if (el.type !== 'text' || (!el.arabicContent && !el.translatedContent)) return el;
  const content = el.arabicContent || el.translatedContent || '';
  const formattedNum = isArabicScript(editTargetLang)
    ? `\u202A${toArNums(editingInvoice?.invoiceNumber || '', editTargetLang)}\u202C`
    : (editingInvoice?.invoiceNumber || '');

  return {
    ...el,
    arabicContent: content
      .replace(/\{invoice_number\}/g, formattedNum)
      .replace(/\b[A-Z]{2,4}-(?:[A-Z]{2,4}-)?[\d]{3,}\b/g, formattedNum),
    translatedContent: content
      .replace(/\{invoice_number\}/g, formattedNum)
      .replace(/\b[A-Z]{2,4}-(?:[A-Z]{2,4}-)?[\d]{3,}\b/g, formattedNum),
  };
});
    clone.elements = clone.elements.map((el: any) =>
      el.type === 'stamp' ? { ...el, visible: editShowStamp } : el
    );
    clone.amount = editTotal;
    return clone;
  }, [editingInvoice, editIncludeFields, editClientName, editClientEmail, editClientPhone, editClientAddress, editTaxId, editStatus, editCurrency, editLineItems, editTaxLines, editDiscountLines, editShipping, editTotal, editShownCustomKeys, editTranslatedFields, editShowStamp, editIsBilingual, editHiddenColumns]);

const handleSaveEdit = async () => {
  if (!editingInvoice || !liveEditInvoice) return;

  const originalBill = bills.find(b => b.id === editingInvoice.id);
  const tokenEls = (originalBill?.elements || editingInvoice.elements || []).filter((el: any) =>
    el.id?.startsWith('token-qr-') ||
    el.id?.startsWith('token-label-') ||
    el._auto === 'tracking' ||
    el._auto === 'tracking_label' ||
    (el.type === 'image' && el.id?.includes('token'))
  );
  const hasTranslatedContentOnSave = (originalBill?.elements || []).some((el: any) => !!el.arabicContent || !!el.translatedContent || !!el.translatedLang);
  const isBilingualInvoice = ((editingInvoice as any).isBilingual === true || (editingInvoice as any).bilingual_mode === true || hasTranslatedContentOnSave) && editTargetLang !== 'en';

  const hasInvoiceQr = liveEditInvoice.elements.some((el: any) =>
    el.id?.startsWith('invoice-qr-') ||
    el.id?.startsWith('qr-injected-') ||
    el._auto === 'invoice'
  );
  const maxBottomEdit = liveEditInvoice.elements.reduce((m: number, el: any) => Math.max(m, (el.y || 0) + (el.height || 0)), 0);
  const editQrY = maxBottomEdit + 20;
  const isThermalSave = editingInvoice.pageSize === 'THERMAL_80MM' || editingInvoice.pageSize === 'THERMAL_58MM';
  const saveQrPos = (editingInvoice as any).qr_position || (editingInvoice as any).autoQrPosition || 'center';
  const savePageW = isThermalSave ? 302 : (editingInvoice.pageSize === 'LETTER' ? 816 : 794);
  const saveQrSize = 80;
  const saveQrX = isThermalSave ? (savePageW - saveQrSize) / 2
    : saveQrPos === 'left' ? 60
    : saveQrPos === 'right' ? (savePageW - saveQrSize - 60)
    : (savePageW - saveQrSize) / 2;

  const qrInjected = hasInvoiceQr ? [] : [
    {
      id: `invoice-qr-${editingInvoice.id}`,
      type: 'qr',
      _auto: 'invoice',
      qrValue: `${window.location.origin}/invoice/view/${(editingInvoice as any).publicToken || (editingInvoice as any).public_token || editingInvoice.id}`,
      x: saveQrX,
      y: editQrY,
      width: saveQrSize,
      height: saveQrSize,
      zIndex: 100,
      opacity: 100,
    },
    {
      id: `invoice-label-${editingInvoice.id}`,
      type: 'text',
      _auto: 'invoice_label',
      textContent: 'Scan to view invoice',
      arabicContent: 'مسح لعرض الفاتورة',
      x: saveQrX - 30,
      y: editQrY + saveQrSize + 6,
      width: 140,
      height: 20,
      fontSize: 9,
      textAlign: 'center',
      color: '#64748b',
      zIndex: 101,
      opacity: 100,
    }
  ];
// Deduplicate elements so QR codes and labels never get multiplied on save
const existingQrKeys = new Set(
  liveEditInvoice.elements
    .filter((el: any) => el.type === 'qr' || el.type === 'image')
    .map((el: any) => el._auto || (el.id?.startsWith('invoice-qr') ? 'invoice' : el.id?.startsWith('token-qr') ? 'tracking' : el.id))
);

const filteredTokenEls = tokenEls.filter((el: any) => {
  const k = el._auto || (el.id?.startsWith('token-qr') ? 'tracking' : null);
  return !k || !existingQrKeys.has(k);
});

const filteredQrInjected = qrInjected.filter((el: any) => {
  const k = el._auto || (el.id?.startsWith('invoice-qr') ? 'invoice' : null);
  return !k || !existingQrKeys.has(k);
});

let finalElements = [...liveEditInvoice.elements, ...filteredTokenEls, ...filteredQrInjected].map((el: any) => {
  if (isBilingualInvoice) return el;
  const { arabicContent, translatedContent, ...rest } = el;
  return rest;
});
const allSaveQrs = finalElements.filter((el: any) => el.type === 'qr' || el.type === 'image');
const saveInvoiceQrEl = allSaveQrs.find((q: any) => q.id?.startsWith('invoice-qr-'));
const saveTokQrEl = allSaveQrs.find((q: any) => q.id?.startsWith('token-qr-'));

if (saveInvoiceQrEl && saveTokQrEl) {
  saveTokQrEl.y = saveInvoiceQrEl.y;
}

const saveInvLabel = finalElements.find((el: any) =>
  el.type === 'text' && el.id?.startsWith('invoice-label-')
);
const saveTokLabel = finalElements.find((el: any) =>
  el.type === 'text' && el.id?.startsWith('token-label-')
);

if (saveInvoiceQrEl && saveInvLabel) {
  const qrH = saveInvoiceQrEl.height || 80;
  const qrW = saveInvoiceQrEl.width || 80;
  const lWidth = Math.max(saveInvLabel.width || 0, qrW, 120);
  saveInvLabel.y = saveInvoiceQrEl.y + qrH + 6;
  saveInvLabel.width = lWidth;
  saveInvLabel.x = saveInvoiceQrEl.x + Math.round((qrW - lWidth) / 2);
  saveInvLabel.textAlign = 'center';
}

if (saveTokQrEl && saveTokLabel) {
  const qrH = saveTokQrEl.height || 80;
  const qrW = saveTokQrEl.width || 80;
  const lWidth = Math.max(saveTokLabel.width || 0, qrW, 180);
  saveTokLabel.y = saveTokQrEl.y + qrH + 6;
  saveTokLabel.width = lWidth;
  saveTokLabel.x = saveTokQrEl.x + Math.round((qrW - lWidth) / 2);
  saveTokLabel.textAlign = 'center';
}
finalElements = finalElements.map((el: any) => {
    if (el.type === 'table') {
      return {
        ...el,
        taxBreakdown: editTaxBreakdownEntries || null,
        buyer_tax_id: editTaxId.trim() || null,
        buyerTaxId: editTaxId.trim() || null,
        columns: (el.columns || []).filter((c: any) => c.key === editMainColKey || !editHiddenColumns.includes(c.key)),
      };
    }
    return el;
  });
  const renderTemplate = { ...(liveEditInvoice as any), elements: finalElements };

  const editSym = sym(editCurrency);
  const invoiceNumber = editingInvoice.invoiceNumber;
  const sellerCountry = getSellerBuyerInfo(editingInvoice).sellerCountry || 'IN';
  const cleanPhone = getCleanE164Phone(editClientPhone, sellerCountry);
  const phoneToSend = cleanPhone;
  const isThermal = editingInvoice.pageSize === 'THERMAL_80MM';

  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

  const isB2bToSave = editTaxId.trim().length > 0;

  const englishAmountWords = amountToWords(editTotal, editCurrency);

  const savedStaffName = (
    (editingInvoice as any)?.staff_name ||
    (editingInvoice as any)?.cashier_name ||
    (editingInvoice as any)?.cashierName ||
    (editingInvoice as any)?.created_by_name ||
    'Staff'
  );

  // Bake includeFields directly into elements[0] so public link & PDF respect ticks
  if (finalElements.length > 0) {
    finalElements[0] = {
      ...finalElements[0],
      includeFields: { ...editIncludeFields },
      include_fields: { ...editIncludeFields },
    };
  }

  // PUT — update in place, invoice number never changes
  await fetch(`${BASE}/invoices/${editingInvoice.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
    body: JSON.stringify({
      is_bilingual: isBilingualInvoice,
      bilingual_mode: isBilingualInvoice,
      is_b2b: isB2bToSave,
      include_fields: { ...editIncludeFields },
      includeFields: { ...editIncludeFields },
      client_name: (editIncludeFields.name ? editClientName : '').trim(),
      client_email: (editIncludeFields.email ? editClientEmail : '').trim(),
      client_phone: (editIncludeFields.phone ? cleanPhone : '').trim(),
      client_address: (editIncludeFields.address ? editClientAddress : '').trim(),
      buyer_tax_id: (editIncludeFields.taxId && editTaxId.trim()) ? editTaxId.trim() : null,
      // 👤 Preserve original creator staff name in database
      staff_name: savedStaffName,
      cashier_name: savedStaffName,
      status: editStatus.toLowerCase(),
      payment_mode: editPaymentMode,
      amount: editTotal,
      amount_paid: editAmountPaid,
      balance_due: Math.max(0, editTotal - editAmountPaid),
      currency: editCurrency,
      elements: finalElements,
      notes: editNotes,
      notes_ar: (editingInvoice as any)?.notes_ar || (editingInvoice as any)?.notesAr || '',
      tax_lines: editTaxLines.filter(t => t.rate > 0),
      discount_lines: editDiscountLines.filter(d => d.rate > 0),
      shipping_cost: editShipping,
      seller_country: editSellerBuyerInfo.sellerCountry,
      seller_region: editSellerBuyerInfo.sellerRegion,
      buyer_region: editSellerBuyerInfo.buyerRegion,
      custom_tax_scheme: editCustomTaxScheme || null,
      tax_breakdown: editTaxBreakdownEntries || null,
      tax_lines_ar: (editingInvoice as any)?.tax_lines_ar || (editingInvoice as any)?.taxLinesAr || [],
      discount_lines_ar: (editingInvoice as any)?.discount_lines_ar || (editingInvoice as any)?.discountLinesAr || [],
      row_labels_ar: (editingInvoice as any)?.row_labels_ar || (editingInvoice as any)?.rowLabelsAr || null,
      translate_target_lang: editTargetLang,
      translated_lang: editTargetLang,
      show_amount_in_words: editShowAmountWords,
      amount_in_words: englishAmountWords,
      amount_in_words_translated: editAmountWordsTranslated || '',
      amount_in_words_ar: editAmountWordsTranslated || '',
      dynamic_values: {
        ...((editingInvoice as any).dynamicValues || (editingInvoice as any).dynamic_values || {}),
        invoice_number: invoiceNumber,
        client_name: editIncludeFields.name ? editClientName : '',
        client_email: editIncludeFields.email ? editClientEmail : '',
        client_phone: editIncludeFields.phone ? cleanPhone : '',
        client_address: editIncludeFields.address ? editClientAddress : '',
        buyer_tax_id: (editIncludeFields.taxId && editTaxId.trim()) ? editTaxId.trim() : null,
      },
      dynamic_values_ar: {
        ...((editingInvoice as any).dynamicValuesAr || (editingInvoice as any).dynamic_values_ar || {}),
        client_name: editTranslatedFields['clientName'] || (editingInvoice as any)?.dynamicValuesAr?.client_name || editClientName,
        client_address: editTranslatedFields['clientAddress'] || (editingInvoice as any)?.dynamicValuesAr?.client_address || editClientAddress,
      },
      line_items: editLineItems.map(li => {
        const lineAmount = li.qty * li.rate;
        const rawB2b = (li as any).b2bTaxRate ?? (li as any).b2b_tax_rate;
        const effectiveRate = isEditB2B && rawB2b !== undefined && rawB2b !== null
          ? parseFloat(String(rawB2b)) || 0
          : parseFloat(String((li as any).taxRate ?? (li as any).tax_rate ?? 0)) || 0;
        const isIncl = Boolean((li as any).taxInclusive ?? (li as any).tax_inclusive);
        const taxAmt = effectiveRate > 0
          ? (isIncl ? lineAmount - lineAmount / (1 + effectiveRate / 100) : lineAmount * (effectiveRate / 100))
          : 0;
        const customAr = Object.fromEntries(
          Object.entries(li)
            .filter(([k, v]) => isEditableItemKey(k) && typeof v === 'string' && (editTranslatedColVals[v.trim()] || (li as any).__customAr?.[k]))
            .map(([k, v]) => [k, editTranslatedColVals[(v as string).trim()] || (li as any).__customAr?.[k] || ''])
        );
        return {
          ...li,
          description: li.description,
          arabic_name: li.arabicDescription || (li as any).arabicName || '',
          arabicDescription: li.arabicDescription || (li as any).arabicName || '',
          translated_description: li.arabicDescription || (li as any).arabicName || '',
          qty: li.qty,
          unit_price: li.rate,
          rate: li.rate,
          amount: lineAmount,
          total: lineAmount,
          tax_rate: effectiveRate,
          tax_name: (() => {
            const baseName = (li as any).taxName || (li as any).tax_name || (editCustomTaxScheme as any)?.name || editTaxScheme?.components?.[0]?.name || editTaxScheme?.label?.replace(/\s*\(.*?\)/, '') || 'Tax';
            return isEditB2B ? ((li as any).b2bTaxName || (li as any).b2b_tax_name || (editCustomTaxScheme as any)?.b2b_name || baseName) : baseName;
          })(),
          tax_inclusive: isIncl,
          tax_amount: parseFloat(taxAmt.toFixed(2)),
          __customAr: customAr,
        };
      }),
    }),
  });

  const updatedRecord = {
    ...editingInvoice,
    ...liveEditInvoice,
    id: editingInvoice.id,
    invoiceNumber,
    include_fields: { ...editIncludeFields },
    includeFields: { ...editIncludeFields },
    // 👤 Preserve staff record in frontend state
    staff_name: savedStaffName,
    cashier_name: savedStaffName,
    cashierName: savedStaffName,
    clientName: editClientName,
    clientEmail: editClientEmail,
    clientPhone: cleanPhone,
    clientAddress: editClientAddress,
    buyer_tax_id: editTaxId.trim() || null,
    buyerTaxId: editTaxId.trim() || null,
    clientGSTIN: editTaxId.trim() || '',
    amount: editTotal,
    amountPaid: editAmountPaid,
    balanceDue: Math.max(0, editTotal - editAmountPaid),
    currency: editCurrency,
    status: editStatus as any,
    payment_mode: editPaymentMode,
    elements: finalElements,
    lastEditedAt: new Date().toISOString(),
  } as InvoiceRecord;

  // 1. Immediately update bills list in local state so reopening shows new changes
  setBills(prev => prev.map(b => b.id === editingInvoice.id ? updatedRecord : b));

  // 2. Notify parent callback
  onUpdateInvoice?.(
    editingInvoice.id,
    finalElements,
    editTotal,
    updatedRecord
  );

  setEditingInvoice(null);

  onAddLog?.('INVOICE_EDITED', `Invoice ${invoiceNumber} updated in place.`);
  postLog('INVOICE_EDITED', `Invoice ${invoiceNumber} edited | Client: ${editClientName} | ${editSym}${editTotal.toFixed(2)}`);

  // Send revised PDF via WhatsApp — only if phone present
  const phoneDigitsCheck = phoneToSend.replace(/\D/g, '');
if (phoneToSend && phoneDigitsCheck.length >= 7) {
    try {
      const pageWidth = isThermal ? 302 : 794;
      let html: string;

      if (isThermal || isBilingualInvoice) {
        // Thermal receipts and bilingual invoices keep their existing
        // dedicated renderers — only the plain-template path below moves
        // to CanvasWorkspace, to match the fix made for Quick Invoice.
        const container = document.createElement('div');
        container.style.cssText = `position:absolute;left:-9999px;top:-9999px;width:${pageWidth}px`;
        document.body.appendChild(container);

        const { createRoot } = await import('react-dom/client');
        const tempRoot = createRoot(container);

        const editItemsWithCustom = editLineItems.map(li => {
          const customAr = Object.fromEntries(
            Object.entries(li)
              .filter(([k, v]) => isEditableItemKey(k) && typeof v === 'string' && (editTranslatedColVals[v.trim()] || (li as any).__customAr?.[k]))
              .map(([k, v]) => [k, editTranslatedColVals[(v as string).trim()] || (li as any).__customAr?.[k] || ''])
          );
          return {
            ...li,
            arabicName: li.arabicDescription,
            __customAr: customAr,
            customColAr: customAr,
          };
        });

        const combinedDynamicVals = {
          invoice_number: invoiceNumber,
          ...((editingInvoice as any).dynamicValues || {}),
          ...((editingInvoice as any).dynamic_values || {}),
        };

        await new Promise<void>(resolve => {
          tempRoot.render(
            isThermal
              ? React.createElement(POSReceiptRenderer, {
                  template: liveEditInvoice || editingInvoice,
                  clientName: editClientName,
                  clientEmail: editClientEmail,
                  clientPhone: editClientPhone,
                  clientAddress: editClientAddress,
                  buyerTaxId: editTaxId.trim() || undefined,
                  isB2B: isEditB2B,
                  invoiceDate: parseUtcDate(editingInvoice.createdAt).toLocaleDateString(),
                  invoiceTime: editInvoiceTime,
                  notes: editNotes,
                  lineItems: editItemsWithCustom,
                  taxLines: editTaxLines,
                  discountLines: editDiscountLines,
                  sym: editSym,
                  shippingCost: editShipping,
                  productTaxTotal: editProductTaxTotal,
                  invoiceStatus: editStatus,
                  isThermal: true,
                  showTokenQRPlaceholder: false,
                  isEditPreview: false,
                  isBilingual: isBilingualInvoice,
                  translateTargetLang: editTargetLang,
                  taxBreakdown: editTaxBreakdownEntries,
                  taxBreakdownAr: editTaxBreakdownAr,
                  dynamicValues: combinedDynamicVals,
                })
              : React.createElement(BilingualInvoiceRenderer, {
                  template: liveEditInvoice,
                  clientName: editClientName,
                  clientNameAr: editTranslatedFields['clientName'] || '',
                  clientAddress: editClientAddress,
                  clientAddressAr: editTranslatedFields['clientAddress'] || '',
                  clientEmail: editClientEmail,
                  clientPhone: editClientPhone,
                  buyerTaxId: editTaxId.trim() || undefined,
                  isB2B: isEditB2B,
                  taxBreakdown: editTaxBreakdownEntries,
                  taxBreakdownAr: editTaxBreakdownAr,
                  taxScheme: editTaxScheme,
                  sellerRegion: editSellerBuyerInfo.sellerRegion,
                  buyerRegion: editSellerBuyerInfo.buyerRegion,
                  savedElements: finalElements,
                  autoQrPosition: getBillQrPosition(editingInvoice),
                  invoiceDate: parseUtcDate(editingInvoice.createdAt).toLocaleDateString(),
                  invoiceTime: editInvoiceTime,
                  notes: editNotes,
                  notesTranslated: (editingInvoice as any)?.notes_ar || (editingInvoice as any)?.notesAr || '',
                  lineItems: editItemsWithCustom,
                  taxLines: editTaxLines,
                  discountLines: editDiscountLines,
                  sym: editSym,
                  shippingCost: editShipping,
                  invoiceStatus: editStatus,
                  translateTargetLang: editTargetLang,
                  isBilingual: true,
                  bilingualLayout: 'side',
                  isThermal,
                  showAmountInWords: editShowAmountWords,
                  currencyCode: editCurrency,
                  amountInWordsTranslated: editAmountWordsTranslated,
                  showTrackingQR: editingInvoiceHasToken,
                  invoiceNumber: editingInvoice.invoiceNumber,
                  dynamicValues: combinedDynamicVals,
                })
          );
          setTimeout(resolve, 1200);
        });

        html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box;}body{background:#fff;font-family:system-ui,sans-serif;}</style></head><body>${container.innerHTML}</body></html>`;
        tempRoot.unmount();
        document.body.removeChild(container);
      } else {
        const cwData = buildCwElements((liveEditInvoice || editingInvoice) as any, {
          lineItems: editLineItems, taxLines: editTaxLines, discountLines: editDiscountLines, shippingCost: editShipping,
          clientName: editIncludeFields.name ? editClientName : '',
          clientEmail: editIncludeFields.email ? editClientEmail : '',
          clientPhone: editIncludeFields.phone ? editClientPhone : '',
          clientAddress: editIncludeFields.address ? editClientAddress : '',
          buyerTaxId: editIncludeFields.taxId ? editTaxId : '',
          notes: editNotes,
          invoiceDate: parseUtcDate(editingInvoice.createdAt).toLocaleDateString(), dueDate: '', showDueDate: false,
          showInvoiceQR: false, autoQrPosition: 'center', autoQrSize: 100, autoQrLabelSize: 8,
          autoQrOffset: { x: 0, y: 0 }, qrValue: '',
          dynamicValues: combinedDynamicVals,
        });
        html = await renderCwToHtml(
          (liveEditInvoice || editingInvoice) as any, cwData, 'fixed', pageWidth,
          editCurrency, (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol'
        );
      }

      const pdfB64Res = await fetch(`${BASE}/invoices/html-to-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ html, pageWidth, pageHeight: 0 }),
      });
      if (!pdfB64Res.ok) throw new Error('PDF generation failed');
      const pdfData = await pdfB64Res.json();
      const base64 = pdfData.base64 || pdfData.pdf_base64 || pdfData.data;
      if (!base64) throw new Error('No base64 in PDF response');
      const activeCompany = companyProfile.name || 'Our Company';
      const caption = `🧾 Updated Invoice\nHi ${editClientName}, please find your revised invoice.\nAmount: ${editSym}${editTotal.toFixed(2)}\n\n— ${activeCompany}`;
      const waPhone = resolveWaPhone(phoneToSend, getSellerBuyerInfo(editingInvoice).sellerCountry);
      await fetch(`${BASE}/whatsapp/send-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ phone: waPhone, base64, filename: `Invoice_${invoiceNumber}.pdf`, caption }),
      });
      onAddLog?.('WHATSAPP_SENT', `Revised invoice PDF sent to ${phoneToSend}.`);
    } catch (err) {
      console.warn('WhatsApp send after edit failed:', err);
    }
  }
};


  // ── rest of existing state ──

  const [sendingInvoice, setSendingInvoice] = useState<InvoiceRecord | null>(null);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [viewingLogId, setViewingLogId] = useState<string | null>(null);
  const [emailTemplateKey, setEmailTemplateKey] = useState<'friendly' | 'standard' | 'urgent'>('standard');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile>(loadCompanyProfile);

  useEffect(() => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
    if (!jwt) return;
    fetch(`${BASE}/auth/company-profile`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data && data.name) {
          setCompanyProfile(prev => ({ ...prev, ...data }));
        }
      })
      .catch(() => {});
  }, []);
  const [dismissedMatches, setDismissedMatches] = useState<string[]>([]);
  const [remindersExpanded, setRemindersExpanded] = useState(false);
  const [statusMenuOpenId, setStatusMenuOpenId] = useState<string | null>(null);
const [pendingCancelBill, setPendingCancelBill] = useState<InvoiceRecord | null>(null);
const [cancelReasonDraft, setCancelReasonDraft] = useState('');

const STATUS_OPTIONS = [
  { val: 'Draft', label: 'Draft', color: '#64748b' },
  { val: 'Sent', label: 'Sent', color: '#2563eb' },
  { val: 'Paid', label: 'Paid', color: '#059669' },
  { val: 'Overdue', label: 'Overdue', color: '#e11d48' },
  { val: 'Cancelled', label: 'Discarded', color: '#dc2626' },
];

React.useEffect(() => {
  if (!statusMenuOpenId) return;
  const close = () => {
    setStatusMenuOpenId(null);
    setPendingCancelBill(null);
    setCancelReasonDraft('');
  };
  document.addEventListener('click', close);
  return () => document.removeEventListener('click', close);
}, [statusMenuOpenId]);

const handleSetStatus = (bill: InvoiceRecord, newStatus: string) => {
  if (newStatus === 'Cancelled') {
    setPendingCancelBill(bill);
    setCancelReasonDraft('');
    return;
  }
  onUpdateInvoice?.(bill.id, bill.elements, bill.amount, { status: newStatus as any });
  onAddLog?.('INVOICE_STATUS_CHANGED', `Invoice ${bill.invoiceNumber} status changed to ${newStatus}.`);
  postLog('INVOICE_STATUS_CHANGED', `Invoice ${bill.invoiceNumber} status set to ${newStatus}`);
  setStatusMenuOpenId(null);
};

const handleConfirmCancel = (bill: InvoiceRecord) => {
  onUpdateInvoice?.(bill.id, bill.elements, bill.amount, {
    status: 'Cancelled' as any,
    cancellation_reason: cancelReasonDraft,
  } as any);
  onAddLog?.('INVOICE_STATUS_CHANGED', `Invoice ${bill.invoiceNumber} status changed to Cancelled. Reason: ${cancelReasonDraft || 'None provided'}.`);
  postLog('INVOICE_STATUS_CHANGED', `Invoice ${bill.invoiceNumber} status set to Cancelled | Reason: ${cancelReasonDraft || 'None'}`);
  setPendingCancelBill(null);
  setCancelReasonDraft('');
  setStatusMenuOpenId(null);
};

  const getInvoiceDueDate = (createdAtStr: string): Date => {
    const d = parseUtcDate(createdAtStr);
    d.setDate(d.getDate() + 14);
    return d;
  };

  const isOverdueOrNearing = (bill: InvoiceRecord) => {
    const isPaid = ['paid', 'Paid'].includes(bill.status);
    const isCancelled = ['Cancelled', 'cancelled'].includes(bill.status);
    if (isPaid || isCancelled) return { isOverdue: false, isNearing: false, daysLeft: 0, dueDateStr: '' };
    const dueDate = getInvoiceDueDate(bill.createdAt);
    const today = new Date();
    const dDate = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const tDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffDays = Math.ceil((dDate.getTime() - tDate.getTime()) / (1000 * 60 * 60 * 24));
    return {
      isOverdue: bill.status === 'Overdue' || diffDays < 0,
      isNearing: !(bill.status === 'Overdue' || diffDays < 0) && diffDays >= 0 && diffDays <= 3,
      daysLeft: diffDays,
      dueDateStr: dueDate.toLocaleDateString()
    };
  };

  const activeCompanyName = companyProfile.name || 'Our Company';

  const TEMPLATES_MAP = {
    friendly: {
      label: 'Gentle Check-in',
      subject: (invNo: string) => `Friendly Check-in: Payment Upcoming for Invoice #${invNo} — ${activeCompanyName}`,
      body: (clientName: string, invNo: string, amount: string, dueDate: string) =>
        `Payment Due Reminder\n\nDear ${clientName},\n\nThis is a gentle check-in regarding the upcoming payment for invoice #${invNo} of ${sym()}${amount} due on ${dueDate}.\n\nPlease initiate payment or let us know if you need any clarification.\n\nWarm regards,\n${activeCompanyName}`
    },
    standard: {
      label: 'Standard Reminder',
      subject: (invNo: string) => `ACTION REQUIRED: Invoice #${invNo} is Overdue — ${activeCompanyName}`,
      body: (clientName: string, invNo: string, amount: string, dueDate: string) =>
        `Overdue Payment\n\nDear ${clientName},\n\nInvoice #${invNo} for ${sym()}${amount} was due on ${dueDate} and remains outstanding.\n\nPlease settle at your earliest convenience.\n\nSincerely,\n${activeCompanyName}`
    },
    urgent: {
      label: 'Final Notice',
      subject: (invNo: string) => `FINAL DEMAND: Outstanding invoice #${invNo} — ${activeCompanyName}`,
      body: (clientName: string, invNo: string, amount: string, dueDate: string) =>
        `⚠️ Final Demand Note\n\nDear ${clientName},\n\nFINAL WARNING: Outstanding balance of ${sym()}${amount} for invoice #${invNo}, due ${dueDate}.\n\nSubmit payment immediately to avoid service restrictions.\n\nLegal & Credit Recovery\n${activeCompanyName}`
    }
  };

  React.useEffect(() => {
    if (sendingInvoice) {
      const remaining = sendingInvoice.amount - (sendingInvoice.amountPaid || 0);
      const amtStr = (remaining > 0 ? remaining : sendingInvoice.amount).toLocaleString('en-US', { minimumFractionDigits: 2 });
      const dueDateStr = getInvoiceDueDate(sendingInvoice.createdAt).toLocaleDateString();
      const temp = TEMPLATES_MAP[emailTemplateKey];
      if (temp) {
        setEmailRecipient(sendingInvoice.clientEmail || '');
        setEmailSubject(temp.subject(sendingInvoice.invoiceNumber));
        setEmailBody(temp.body(sendingInvoice.clientName, sendingInvoice.invoiceNumber, amtStr, dueDateStr).trim());
      }
    }
  }, [sendingInvoice?.id, emailTemplateKey]);

  const encodeUTF8 = (str: string) => btoa(unescape(encodeURIComponent(str)));
  const makeEmailRaw = (to: string, subject: string, htmlBody: string) => {
    const emailStr = [`To: ${to}`, `Subject: ${subject}`, 'MIME-Version: 1.0', 'Content-Type: text/html; charset=utf-8', '', htmlBody].join('\r\n');
    return encodeUTF8(emailStr).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

 const handleSendGmailReminder = async () => {
  if (!emailRecipient.trim() || !emailSubject.trim() || !emailBody.trim()) {
    alert("Please fill recipient, subject and body.");
    return;
  }
  setIsSendingEmail(true);
  try {
    const footerContacts = companyProfile.contacts.filter(c => c.showInFooter && c.value).map(c => {
      const label = c.type === 'custom' && c.label ? c.label : CONTACT_TYPE_META[c.type].label;
      return `<span style="margin-right:12px;font-size:11px;color:#64748b"><strong>${label}:</strong> ${c.value}</span>`;
    }).join('');
    const domainLine = companyProfile.domain ? `<span style="margin-right:12px;font-size:11px;color:#64748b"><strong>Website:</strong> ${companyProfile.domain}</span>` : '';
    const companyFooterBlock = (footerContacts || domainLine)
      ? `<div style="margin-top:24px;padding-top:12px;border-top:1px solid #e2e8f0"><p style="font-size:11px;color:#94a3b8;font-weight:bold;margin:0 0 6px;text-transform:uppercase">${companyProfile.name || 'Company'} — Contact</p><div>${footerContacts}${domainLine}</div></div>`
      : '';

    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
    const htmlBody = `<div style="font-family:sans-serif;padding:24px;color:#334155;max-width:600px;margin:auto;border:1px solid #e2e8f0;border-radius:12px;background:#fff">
      ${emailBody.split('\n').map(line => line.trim() ? `<p style="margin:0 0 8px">${line}</p>` : '<br/>').join('')}
    </div>`;

    let attachments: any[] = [];
    if (sendingInvoice) {
      try {
        const pdfRes = await fetch(`${BASE}/invoices/${sendingInvoice.id}/pdf`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (pdfRes.ok) {
          const pdfBlob = await pdfRes.blob();
          const base64 = await new Promise<string>(resolve => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.readAsDataURL(pdfBlob);
          });
          attachments = [{ filename: `Invoice_${sendingInvoice.invoiceNumber}.pdf`, content: base64, encoding: 'base64' }];
        }
      } catch (pdfErr) {
        console.warn('PDF attach failed, sending reminder without attachment:', pdfErr);
      }
    }

    const res = await fetch(`${BASE}/email/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({
        to: emailRecipient.trim(),
        subject: emailSubject.trim(),
        html: htmlBody + companyFooterBlock,
        attachments,
        outlet_id: (sendingInvoice as any)?.outlet_id,
      }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Email send failed');
    }
    alert(`Email sent to ${emailRecipient}${attachments.length ? ' with PDF attached' : ''}!`);
    onAddLog?.('PAYMENT_REMINDER_SENT', `Sent ${emailTemplateKey} reminder to ${emailRecipient} for Invoice ${sendingInvoice?.invoiceNumber}.`);
    postLog('PAYMENT_REMINDER_SENT', `${emailTemplateKey} email reminder sent to ${emailRecipient} | Invoice ${sendingInvoice?.invoiceNumber}`);
    setSendingInvoice(null);
  } catch (err: any) {
    alert(`Failed: ${err.message}`);
  } finally {
    setIsSendingEmail(false);
  }
};

const handleSendWhatsAppReminder = async () => {
  if (!sendingInvoice) return;
  const sellerCountry = getSellerBuyerInfo(sendingInvoice).sellerCountry || 'IN';
  const waPhone = resolveWaPhone((sendingInvoice as any).clientPhone || '', sellerCountry);
  if (!waPhone) {
    alert('No valid phone number on this invoice.');
    return;
  }
  setIsSendingWhatsApp(true);
  try {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
    const pdfRes = await fetch(`${BASE}/invoices/${sendingInvoice.id}/pdf`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (!pdfRes.ok) throw new Error('PDF fetch failed');
    const pdfBlob = await pdfRes.blob();
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });
    const res = await fetch(`${BASE}/whatsapp/send-pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({
        phone: waPhone,
        base64,
        filename: `Invoice_${sendingInvoice.invoiceNumber}.pdf`,
        caption: `${emailSubject}\n\n${emailBody}`,
      }),
    });
    if (!res.ok) throw new Error('WhatsApp send failed');
    alert(`Reminder with PDF sent to +${waPhone} via WhatsApp!`);
    onAddLog?.('WHATSAPP_SENT', `Reminder PDF sent to +${waPhone} for Invoice ${sendingInvoice.invoiceNumber}.`);
    postLog('PAYMENT_REMINDER_SENT', `WhatsApp PDF reminder sent to +${waPhone} | Invoice ${sendingInvoice.invoiceNumber}`);
    setSendingInvoice(null);
  } catch (err: any) {
    alert(`Failed: ${err.message}`);
  } finally {
    setIsSendingWhatsApp(false);
  }
};

  const livePaymentMatches = React.useMemo(() => {
    if (!bankTransactions || !bills) return [];
    const matches: any[] = [];
    bills.forEach(bill => {
      if (String(bill.status || '').trim().toLowerCase() === 'paid') return;
      const remainingBal = bill.amount - (bill.amountPaid || 0);
      if (remainingBal <= 0) return;
      bankTransactions.forEach(txn => {
        if (txn.type !== 'Credit' || txn.status !== 'Completed') return;
        const totalMatches = Math.abs(txn.amount - bill.amount) < 0.01;
        const balMatches = Math.abs(txn.amount - remainingBal) < 0.01;
        if (totalMatches || balMatches) {
          const invoiceRefMatches = txn.invoiceNumber && txn.invoiceNumber.toLowerCase().trim() === bill.invoiceNumber.toLowerCase().trim();
          const clientMatches = txn.clientName && (txn.clientName.toLowerCase().includes(bill.clientName.toLowerCase()) || bill.clientName.toLowerCase().includes(txn.clientName.toLowerCase()));
          const matchKey = `${bill.id}_${txn.id}`;
          if (!dismissedMatches.includes(matchKey)) {
            matches.push({ invoice: bill, transaction: txn, matchType: (invoiceRefMatches || clientMatches) ? 'exact' : 'amount_only', remainingBal });
          }
        }
      });
    });
    return matches;
  }, [bills, bankTransactions, dismissedMatches]);

  const handleQuickReconcile = (bill: InvoiceRecord, txn: BankTransaction) => {
    onUpdateInvoice?.(bill.id, bill.elements, bill.amount, { status: 'Paid', amountPaid: bill.amount, balanceDue: 0, paymentDate: new Date().toISOString().split('T')[0] });
    onAddLog?.('INVOICE_STATUS_CHANGED', `Smart reconciliation matched Transaction ${txn.txnSerialNumber} to Invoice ${bill.invoiceNumber}. Updated to PAID.`);
    postLog('INVOICE_RECONCILED', `Invoice ${bill.invoiceNumber} marked PAID via smart reconciliation | Txn: ${txn.txnSerialNumber} | ${sym(bill.currency)}${bill.amount.toFixed(2)}`);
    setDismissedMatches(prev => [...prev, `${bill.id}_${txn.id}`]);
  };

  // Row shape shared by every sheet in the export.
  const buildInvoiceRows = (records: InvoiceRecord[]) => records.map(b => {
    const tableEl = b.elements.find(el => el.type === 'table');
    const itemsSummary = tableEl?.rows?.map((r, i) => `#${i+1}: ${r.values.description || ''} (Qty: ${r.values.qty || 0}, Rate: $${r.values.rate || 0})`).join(' | ') || 'No items';
    const taxId = getInvoiceTaxId(b);
    const isB2bSale = isInvoiceB2B(b);
    const buyerState = (b as any).buyerRegion || (b as any).buyer_region || '';
    return {
      "Invoice ID": b.invoiceNumber,
      "Type": isB2bSale ? "B2B" : "B2C",
      "Customer Name": b.clientName,
      "Tax ID / GSTIN": taxId || "N/A",
      "Phone": (b as any).clientPhone || '',
      "Email": b.clientEmail || '',
      "Buyer State": buyerState || 'N/A',
      "Total": b.amount,
      "Paid": b.amountPaid || 0,
      "Balance": Math.max(0, b.amount - (b.amountPaid || 0)),
      "Status": b.status.toUpperCase(),
      "Payment Mode": (b as any).payment_mode || '',
      "Generated On": parseUtcDate(b.createdAt).toLocaleDateString(),
      "Items": itemsSummary,
    };
  });

  // Dedicated Customer Directory Export (Only Customer info, no item clutter)
  const handleExportCustomersOnly = (records: InvoiceRecord[]) => {
    if (records.length === 0) return alert('No invoices to export.');
    try {
      const customerMap = new Map<string, any>();
      records.forEach(b => {
        const key = (b.clientName || 'Walk-in').trim().toLowerCase() + '_' + ((b as any).clientPhone || '').trim();
        const taxId = getInvoiceTaxId(b);
        const isB2bSale = isInvoiceB2B(b);
        const prev = customerMap.get(key) || {
          "Customer Name": b.clientName || 'Walk-in Customer',
          "Type": isB2bSale ? "B2B" : "B2C",
          "Tax ID / GSTIN": taxId || "—",
          "Phone": (b as any).clientPhone || '—',
          "Email": b.clientEmail || '—',
          "State / Region": (b as any).buyerRegion || (b as any).buyer_region || '—',
          "Total Invoices": 0,
          "Total Billed": 0,
          "Total Paid": 0,
          "Outstanding Balance": 0,
          "Last Billed On": parseUtcDate(b.createdAt).toLocaleDateString(),
        };

        prev["Total Invoices"] += 1;
        prev["Total Billed"] += b.amount;
        prev["Total Paid"] += (b.amountPaid || 0);
        prev["Outstanding Balance"] += Math.max(0, b.amount - (b.amountPaid || 0));
        if (isB2bSale) prev["Type"] = "B2B";
        if (taxId) prev["Tax ID / GSTIN"] = taxId;
        customerMap.set(key, prev);
      });

      const customerRows = Array.from(customerMap.values());
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(customerRows);
      ws['!cols'] = [
        { wch: 22 }, { wch: 10 }, { wch: 18 }, { wch: 16 }, { wch: 24 },
        { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 18 }, { wch: 16 }
      ];
      XLSX.utils.book_append_sheet(wb, ws, "Customers");
      XLSX.writeFile(wb, `Customer_Directory_${Date.now().toString().slice(-6)}.xlsx`);
    } catch {
      alert("Customer export failed.");
    }
  };

  // Filters records down to a preset window (today/this week/this month/
  // this year) based on createdAt. 'all' and 'custom' are handled by the caller.
  const filterByDateRange = (records: InvoiceRecord[], range: 'today' | 'week' | 'month' | 'year') => {
    const now = new Date();
    let start: Date;
    if (range === 'today') start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    else if (range === 'week') { start = new Date(now); start.setDate(now.getDate() - 7); }
    else if (range === 'month') start = new Date(now.getFullYear(), now.getMonth(), 1);
    else start = new Date(now.getFullYear(), 0, 1); // year
    return records.filter(b => parseUtcDate(b.createdAt) >= start);
  };

  const handleExportSelectedToExcel = (
    selectedRecords: InvoiceRecord[],
    dateRange: 'all' | 'today' | 'week' | 'month' | 'year' | 'custom' = 'all',
    customFrom?: string,
    customTo?: string
  ) => {
    if (selectedRecords.length === 0) return;
    try {
      let records = selectedRecords;
      if (dateRange === 'custom' && customFrom && customTo) {
        const from = new Date(customFrom);
        const to = new Date(customTo);
        to.setHours(23, 59, 59, 999);
        records = records.filter(b => {
          const d = parseUtcDate(b.createdAt);
          return d >= from && d <= to;
        });
      } else if (dateRange !== 'all') {
        records = filterByDateRange(records, dateRange);
      }
      if (records.length === 0) {
        alert('No invoices found in that date range.');
        return;
      }

      const norm = (s: string) => (s || '').toLowerCase().trim();
      const paidRecords = records.filter(b => norm(b.status) === 'paid');
      const draftRecords = records.filter(b => norm(b.status) === 'draft');
      const pendingRecords = records.filter(b => ['sent', 'pending', 'unpaid'].includes(norm(b.status)));
      const overdueRecords = records.filter(b => norm(b.status) === 'overdue');
      const cancelledRecords = records.filter(b => norm(b.status) === 'cancelled');

      const wb = XLSX.utils.book_new();

      const HEADER_COLS = ["Invoice ID","Type","Customer Name","Tax ID / GSTIN","Phone","Email","Buyer State","Total","Paid","Balance","Status","Payment Mode","Generated On","Items"];

      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
        fill: { fgColor: { rgb: "4F46E5" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "CBD5E1" } },
          bottom: { style: "thin", color: { rgb: "CBD5E1" } },
          left: { style: "thin", color: { rgb: "CBD5E1" } },
          right: { style: "thin", color: { rgb: "CBD5E1" } },
        },
      };
      const totalRowStyle = {
        font: { bold: true, color: { rgb: "111827" } },
        fill: { fgColor: { rgb: "EEF2FF" } },
        border: {
          top: { style: "thin", color: { rgb: "A5B4FC" } },
          bottom: { style: "thin", color: { rgb: "A5B4FC" } },
        },
      };
      const summaryTitleStyle = {
        font: { bold: true, color: { rgb: "4F46E5" }, sz: 12 },
      };
      const summaryLabelStyle = { font: { bold: true, color: { rgb: "374151" } } };

      // Applies headerStyle to row 1, and totalRowStyle to the given
      // 0-indexed row numbers (the subtotal line(s) at the bottom).
      const styleSheet = (ws: any, totalRowIndices: number[] = [], titleRowIndices: number[] = []) => {
        const range = XLSX.utils.decode_range(ws['!ref']);
        for (let c = range.s.c; c <= range.e.c; c++) {
          const addr = XLSX.utils.encode_cell({ r: 0, c });
          if (ws[addr]) ws[addr].s = headerStyle;
        }
        totalRowIndices.forEach(r => {
          for (let c = range.s.c; c <= range.e.c; c++) {
            const addr = XLSX.utils.encode_cell({ r, c });
            if (ws[addr]) ws[addr].s = totalRowStyle;
          }
        });
        titleRowIndices.forEach(r => {
          const addr = XLSX.utils.encode_cell({ r, c: 0 });
          if (ws[addr]) ws[addr].s = summaryTitleStyle;
        });
        ws['!cols'] = [
          { wch: 16 }, { wch: 8 }, { wch: 22 }, { wch: 18 }, { wch: 15 },
          { wch: 22 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
          { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 50 },
        ];
        ws['!freeze'] = { xSplit: 0, ySplit: 1 };
      };

      // One tab per status — ALWAYS created, even if empty, so the
      // workbook consistently shows Paid/Pending/Overdue/Draft/Cancelled.
      const addStatusSheet = (name: string, recs: InvoiceRecord[]) => {
        const rows: any[] = recs.length > 0 ? buildInvoiceRows(recs) : [];
        const subtotal = recs.reduce((s, b) => s + b.amount, 0);
        const subtotalPaid = recs.reduce((s, b) => s + (b.amountPaid || 0), 0);
        const totalRowIdx = rows.length; // 0-indexed data row where the total line lands
        rows.push({
          "Invoice ID": `${name.toUpperCase()} TOTAL`,
          "Client": `${recs.length} invoice(s)`,
          "Total": subtotal,
          "Paid": subtotalPaid,
          "Balance": Math.max(0, subtotal - subtotalPaid),
        });
        const ws = XLSX.utils.json_to_sheet(rows, { header: HEADER_COLS });
        styleSheet(ws, [totalRowIdx + 1]); // +1 for the header row itself
        XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
      };

      addStatusSheet('Paid', paidRecords);
      addStatusSheet('Pending', pendingRecords);
      addStatusSheet('Overdue', overdueRecords);
      addStatusSheet('Draft', draftRecords);
      addStatusSheet('Cancelled', cancelledRecords);

      // Final combined tab — every invoice in the export, with a full
      // segregated summary at the bottom.
      const allRows: any[] = buildInvoiceRows(records);
      const revenue = paidRecords.reduce((s, b) => s + b.amount, 0);
      const outstanding = [...pendingRecords, ...overdueRecords].reduce((s, b) => s + Math.max(0, b.amount - (b.amountPaid || 0)), 0);
      const cancelledTotal = cancelledRecords.reduce((s, b) => s + b.amount, 0);
      const draftTotal = draftRecords.reduce((s, b) => s + b.amount, 0);

      const summaryTitleRowIdx = allRows.length + 1; // +1 header row, +1 blank row below
      allRows.push({});
      allRows.push({ "Invoice ID": '━━━ SUMMARY ━━━' });
      allRows.push({ "Invoice ID": 'Total Revenue (Paid)', "Client": `${sym()}${revenue.toFixed(2)}`, "Total": revenue });
      allRows.push({ "Invoice ID": 'Outstanding (Pending + Overdue)', "Client": `${sym()}${outstanding.toFixed(2)}`, "Total": outstanding });
      allRows.push({ "Invoice ID": 'Draft Value', "Client": `${sym()}${draftTotal.toFixed(2)}`, "Total": draftTotal });
      allRows.push({ "Invoice ID": 'Cancelled Value', "Client": `${sym()}${cancelledTotal.toFixed(2)}`, "Total": cancelledTotal });
      allRows.push({});
      allRows.push({ "Invoice ID": 'Paid Count', "Client": String(paidRecords.length) });
      allRows.push({ "Invoice ID": 'Pending Count', "Client": String(pendingRecords.length) });
      allRows.push({ "Invoice ID": 'Overdue Count', "Client": String(overdueRecords.length) });
      allRows.push({ "Invoice ID": 'Draft Count', "Client": String(draftRecords.length) });
      allRows.push({ "Invoice ID": 'Cancelled Count', "Client": String(cancelledRecords.length) });
      allRows.push({ "Invoice ID": 'Total Invoices', "Client": String(records.length) });

      const wsAll = XLSX.utils.json_to_sheet(allRows, { header: HEADER_COLS });
      styleSheet(wsAll, [], [summaryTitleRowIdx + 1]);
      XLSX.utils.book_append_sheet(wb, wsAll, 'All Invoices');

      const rangeLabel = dateRange === 'custom' ? `${customFrom}_to_${customTo}` : dateRange;
      XLSX.writeFile(wb, `Invoices_${rangeLabel}_${Date.now().toString().slice(-6)}.xlsx`);
    } catch (err) {
      alert("Excel export error.");
    }
  };

  const isLockedIndividual = authUserPlanTier === 'individual' &&
    (authUserPlan === 'basic' || authUserPlanStatus === 'expired' || authUserPlanStatus === 'cancelled');

  const filteredBills = bills.filter((b) => {
    const taxId = getInvoiceTaxId(b);
    const phone = (b as any).clientPhone || '';
    const matchesSearch = b.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.clientEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      taxId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      phone.includes(searchTerm);

    const matchesStatus = statusFilter === 'all' || b.status === statusFilter ||
      (statusFilter === 'Paid' && (b.status === 'Paid' || b.status === 'paid')) ||
      (statusFilter === 'Sent' && (b.status === 'Sent' || b.status === 'pending')) ||
      (statusFilter === 'Draft' && ['Draft','draft'].includes(b.status)) ||
      (statusFilter === 'Overdue' && ['Overdue','overdue'].includes(b.status)) ||
      (statusFilter === 'Cancelled' && ['Cancelled','cancelled'].includes(b.status));

    const isB2b = isInvoiceB2B(b);
    const matchesCustomerType = customerFilter === 'all' ||
      (customerFilter === 'b2b' && isB2b) ||
      (customerFilter === 'b2c' && !isB2b);

    return matchesSearch && matchesStatus && matchesCustomerType;
  }).slice(0, isLockedIndividual ? 5 : undefined);

  // Thermal receipts print via USB/Bluetooth (skipping the PDF pipeline
  // entirely, same as Quick Invoice) when a printer is connected — falls
  // back to the existing onPrintPDF (browser print dialog) otherwise, or
  // for non-thermal invoices, which never go through USB/BT.
  const handlePrintBill = async (b: InvoiceRecord) => {
    const isThermal = b.pageSize === 'THERMAL_80MM';
    if (!isThermal) {
      onPrintPDF?.(b);
      return;
    }
    // Neither connection type is chosen, or the chosen one isn't actually
    // live right now — a cached printer name survives a page reload, the
    // real USB/BT device handle does not. Open the picker instead of
    // silently falling through to the OS print dialog with no explanation.
    const liveUsb = useUSBPrinter && isUSBPrinterConnected();
    const liveBt = useBTPrinter && btConnected;
    if (!liveUsb && !liveBt) {
      setBillsPrinterPicker(b);
      return;
    }
    setPrintingBillId(b.id);
    try {
      // Duplicate token-label-*/invoice-label-* elements can exist on a
      // saved bill (backend sometimes appends a second copy — comment
      // above dedupeInvoiceElements notes the same issue). Picking the
      // wrong duplicate breaks the token regex match (→ no tracking QR
      // renders at all) and lets the raw, un-filtered label text leak
      // into the footer instead. Dedupe by id-prefix before anything
      // else touches `elements`, keeping the LAST one of each (most
      // recently written wins).
      // Strip any legacy synthesized client text blocks
      const rawElements: any[] = (b.elements || []).filter((el: any) =>
        !el.id?.startsWith('synth-client-block') &&
        !el.id?.startsWith('client-info-')
      );
      // Dedupe label duplicates, but prefer whichever copy actually
      // carries arabicContent — picking "whichever is last in the saved
      // array" (the old rule) discarded the Arabic-carrying duplicate
      // whenever a later, English-only copy happened to be appended
      // after it, silently printing an English-only caption even though
      // a bilingual version of that same label existed in the saved data.
      const bestLabelByKey = new Map<string, any>();
      const otherElements: any[] = [];
      rawElements.forEach((el: any) => {
        const key = el.id?.startsWith('token-label-') ? 'token-label'
                  : el.id?.startsWith('invoice-label-') ? 'invoice-label'
                  : null;
        if (!key) { otherElements.push(el); return; }
        const existing = bestLabelByKey.get(key);
        if (!existing || (!existing.arabicContent && el.arabicContent)) {
          bestLabelByKey.set(key, el);
        }
      });
      let elements: any[] = [...otherElements, ...Array.from(bestLabelByKey.values())];
      // Align token QR/label to invoice QR/label row for thermal reprints too.
      const printInvoiceQrEl = elements.find((el: any) => el.id?.startsWith('invoice-qr-'));
      const printInvoiceLabelEl = elements.find((el: any) => el.id?.startsWith('invoice-label-'));
      if (printInvoiceQrEl) {
        elements = elements.map((el: any) => {
          if (el.id?.startsWith('token-qr-')) return { ...el, y: printInvoiceQrEl.y };
          if (el.id?.startsWith('token-label-') && printInvoiceLabelEl) return { ...el, y: printInvoiceLabelEl.y };
          return el;
        });
      }
      const tableEl: any = elements.find((el: any) => el.type === 'table');
      const tableTop = tableEl?.y || 0;
      const tableBottom = tableTop + (tableEl?.height || 0);

      // b.elements already has the real, fully-patched shop header, notes,
      // and warranty/footer text baked in (same content the Live Preview
      // shows) — pull those text blocks out by position instead of
      // reconstructing them, so the physical receipt matches the preview.
      const isTextEl = (el: any) => el.type === 'text' && el.textContent?.trim();
      const sortByY = (a: any, c: any) => (a.y || 0) - (c.y || 0);
      const toLines = (els: any[]) => els.flatMap((el: any) => {
        const lines = [el.textContent as string];
        if (el.arabicContent) lines.push(el.arabicContent);
        return lines;
      });

      const headerEls = elements.filter((el: any) => isTextEl(el) && (el.y || 0) < tableTop).sort(sortByY);
      const footerEls = elements
        .filter((el: any) =>
          isTextEl(el) &&
          (el.y || 0) >= tableBottom &&
                  el.textContent !== 'Scan to view invoice' &&
        !el.textContent?.includes('Scan to track order') &&
        !el.id?.startsWith('invoice-label-') &&
        !el.id?.startsWith('token-label-')
        )
        .sort(sortByY);

      const headerLines = toLines(headerEls);
      const footerLines = toLines(footerEls);

      const items = (tableEl?.rows || []).map((r: any) => ({
        description: r.values.description || '',
        qty: parseFloat(String(r.values.qty)) || 0,
        rate: parseFloat(String(r.values.rate)) || 0,
        arabicName: r.values.arabicDescription || r.values.arabicName || '',
      }));

      // Per-item tax (each row's own taxRate/tax_rate + taxInclusive) is a
      // separate blended total from table-level taxLines — same formula
      // used everywhere else (editSubtotal/editProductTaxTotal in the edit
      // modal, computeTableTotals in CanvasWorkspace). Without this the
      // receipt's TOTAL silently drops any per-item tax and just equals
      // Subtotal, which is exactly what was happening here.
      let netSubtotal = 0;
      const lineBases: { base: number; taxRate: number }[] = [];
      const isBillB2b = isInvoiceB2B(b);
      (tableEl?.rows || []).forEach((r: any) => {
        const qty = parseFloat(String(r.values.qty)) || 0;
        const rate = parseFloat(String(r.values.rate)) || 0;
        const lineTotal = qty * rate;
        const rawB2b = r.values?.b2bTaxRate ?? r.values?.b2b_tax_rate;
        const taxRate = isBillB2b && rawB2b !== undefined && rawB2b !== null
          ? parseFloat(String(rawB2b)) || 0
          : parseFloat(String(r.values?.taxRate ?? r.values?.tax_rate ?? 0)) || 0;
        const inclusive = !!(r.values?.taxInclusive ?? r.values?.tax_inclusive);
        const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
        lineBases.push({ base, taxRate });
        netSubtotal += base;
      });
      const discTotalForTax = ((tableEl as any)?.discounts || []).reduce(
        (s: number, d: any) => s + netSubtotal * (d.rate / 100), 0
      );
      const discountRatio = netSubtotal > 0 ? Math.min(1, discTotalForTax / netSubtotal) : 0;
      const productTaxTotal = lineBases.reduce(
        (s, lb) => s + (lb.base * (1 - discountRatio)) * (lb.taxRate / 100), 0
      );

      // Named CGST/SGST/IGST breakdown — the PDF download path already
      // computes this (dlTaxBreakdownEntries/dlTaxBreakdownAr) but this
      // reprint path never did, so USB/BT reprints from Bills view fell
      // back to the blended "Total Tax" line even for GST-scheme invoices.
      const printSellerBuyerInfo = getSellerBuyerInfo(b);
      const printTaxScheme = getDefaultSchemeForCountryAndRegion(printSellerBuyerInfo.sellerCountry, printSellerBuyerInfo.sellerRegion);
      const printCustomTaxScheme = (b as any).custom_tax_scheme || (b as any).customTaxScheme;
      const printTaxBreakdownEntries = computeNamedTaxBreakdown(
        (tableEl?.rows || []).map((r: any) => r.values),
        (tableEl as any)?.discounts || [],
        printTaxScheme,
        printSellerBuyerInfo.sellerRegion,
        printSellerBuyerInfo.buyerRegion,
        isBillB2b,
        printCustomTaxScheme
      );

      // token-qr-*/invoice-qr-*'s textContent is the RENDERED PNG (base64
      // image data), not the URL it encodes. Re-printing that string as-is
      // means asking the printer to QR-encode a multi-KB base64 blob —
      // it silently fails, which is why the tracking QR disappears on
      // reprint. Recover the real URL instead, same value POS originally
      // encoded, so the reprinted QR matches the one from Save.
      const tokenLabelEl: any = elements.find((el: any) => el.id?.startsWith('token-label-'));
      const trackingToken = tokenLabelEl?.textContent?.match(/Token:\s*(\w+)/)?.[1];
      const trackingQrUrl = trackingToken ? `${window.location.origin}/wait?token=${trackingToken}` : undefined;
      // Arabic caption for the tracking QR was already saved onto this
      // same label element at Save time — just never read back here.
      let trackingQRLabel = tokenLabelEl?.arabicContent || undefined;

      const invoicePublicToken = (b as any).publicToken || (b as any).public_token;
      const invoiceQrUrl = invoicePublicToken ? `${window.location.origin}/invoice/view/${invoicePublicToken}` : undefined;
      // Duplicate invoice-label-* elements can exist on a bill (backend
      // sometimes appends a second one) — the first one found isn't
      // guaranteed to be the one carrying arabicContent. Prefer whichever
      // duplicate actually has the Arabic caption saved on it.
      const invoiceLabelEls: any[] = elements.filter((el: any) =>
        el.id?.startsWith('invoice-label-') ||
        el._auto === 'invoice_label' ||
        (el.type === 'text' && el.textContent === 'Scan to view invoice')
      );
      const invoiceLabelEl: any = invoiceLabelEls.find((el: any) => el.arabicContent) || invoiceLabelEls[0];
      let invoiceQRLabel = invoiceLabelEl?.arabicContent || undefined;

      // Must be declared before clientLineAr below, which references it —
      // referencing a const before its own declaration throws "Cannot
      // access 'translatedLang' before initialization" (JS temporal dead
      // zone). That crash was silently sending every Bills-view thermal
      // print through the PDF fallback instead of the ESC/POS path.
      const translatedLang = elements.find((el: any) => el.translatedLang)?.translatedLang;

      // Custom table columns (Tax %, Tax Name, or any catalog column added
      // in POS) were never carried into this reprint's PrintJob at all —
      // there was nothing to render them from. cat_tax_b2c/cat_tax_b2b/
      // cat_tax_name/cat_tax_type aren't stored under their own key on the
      // row; they're derived from taxRate/taxName, same as openEdit()
      // already does for the Edit modal's preview.
      const resolveColumnValue = (row: any, col: any): string => {
        const direct = row.values?.[col.key];
        if (direct !== undefined && direct !== null && direct !== '') return String(direct);
        const rawB2b = row.values?.b2bTaxRate ?? row.values?.b2b_tax_rate;
        const rate = isBillB2b && rawB2b !== undefined && rawB2b !== null
          ? parseFloat(String(rawB2b)) || 0
          : parseFloat(String(row.values?.taxRate ?? row.values?.tax_rate ?? 0)) || 0;
        if (col.key === 'cat_tax_b2c' || col.key === 'cat_tax_b2b') return rate ? `${rate}%` : '';
        if (col.key === 'cat_tax_name') return row.values?.taxName ?? row.values?.tax_name ?? '';
        if (col.key === 'cat_tax_type') return (row.values?.taxInclusive ?? row.values?.tax_inclusive) ? 'Inclusive' : 'Exclusive';
        return '';
      };
      const savedCols = (tableEl?.columns || []).filter((c: any) =>
        !['arabicName', 'arabicDescription', 'taxRate', 'tax_rate', 'taxInclusive', 'tax_inclusive'].includes(c.key)
      );
      const printCols = savedCols.length > 0 ? savedCols : [
        { key: 'description', header: 'Item', align: 'left' },
        { key: 'qty', header: 'Qty', align: 'center' },
        { key: 'rate', header: 'Rate', align: 'right' },
        { key: 'total', header: 'Amt', align: 'right' },
      ];
      // Same 5-inline / rest-overflow split POSView's buildPOSPrintJob uses.
      const printInlineCols = printCols.slice(0, 5);
      const printOverflowCols = printCols.slice(5);
      const printColHeader = (c: any) => c.originalHeader || c.header || '';
      const printColValue = (row: any, c: any, idx: number) => {
        if (c.key === 'description') return row.values?.description || '';
        if (c.key === 'qty') return String(row.values?.qty ?? '');
        if (c.key === 'rate') return `${sym(b.currency)}${(parseFloat(String(row.values?.rate)) || 0).toFixed(2)}`;
        if (c.key === 'total') {
          const t = row.values?.total ?? (parseFloat(String(row.values?.qty)) || 0) * (parseFloat(String(row.values?.rate)) || 0);
          return `${sym(b.currency)}${(parseFloat(String(t)) || 0).toFixed(2)}`;
        }
        if (['sno', 'serial', 'index'].includes(c.key)) return String(idx + 1);
        return resolveColumnValue(row, c);
      };
      const printRows = tableEl?.rows || [];
      const printTableColumns = printInlineCols.length ? printInlineCols.map((c: any) => ({
        header: printColHeader(c),
        headerAr: translatedLang ? c.arabicHeader : undefined,
        align: c.align,
        width: c.width,
        primary: c.key === 'description',
      })) : undefined;
      const printItemCells = printRows.map((r: any, i: number) => printInlineCols.map((c: any) => printColValue(r, c, i)));
      const printItemExtraLines = printRows.map((r: any, i: number) =>
        printOverflowCols
          .map((c: any) => { const v = printColValue(r, c, i); return v ? `${printColHeader(c)}: ${v}` : ''; })
          .filter(Boolean)
          .join('  ')
      );
      // The Arabic form of an overflow value (e.g. Tax Name "rihan") was
      // never saved onto the row — POS only kept it in memory at save
      // time, not persisted. Rather than needing POSView to change, do
      // the same live translate-or-transliterate BillsView already does
      // elsewhere in this function (tax breakdown names, client line,
      // amount-in-words) for each overflow value, at reprint time.
      const printOverflowHeaderAr: Record<string, string> = {};
      if (translatedLang) {
        await Promise.all(printOverflowCols.map(async (c: any) => {
          printOverflowHeaderAr[c.key] = c.arabicHeader || (await transliterateName(printColHeader(c), translatedLang));
        }));
      }
      const printItemExtraLinesAr = translatedLang
        ? await Promise.all(printRows.map(async (r: any, i: number) => {
            const parts = await Promise.all(printOverflowCols.map(async (c: any) => {
              const raw = printColValue(r, c, i);
              if (!raw) return '';
              const isNumeric = /^[\d\s.,%+\-]+$/.test(raw);
              const headerAr = printOverflowHeaderAr[c.key];
              const valueAr = isNumeric
                ? (translatedLang === 'ar' ? toArabicIndicDigits(raw) : raw)
                : await transliterateName(raw, translatedLang);
              return headerAr && valueAr ? `${headerAr}: ${valueAr}` : '';
            }));
            return parts.filter(Boolean).join('  ');
          }))
        : undefined;

      // Transliterated component names (CGST/SGST/IGST/etc.) for bilingual
      // thermal reprints — merged per-entry into printTaxBreakdownEntries,
      // same shape buildReceiptBytes expects (t.nameAr).
      const printTaxBreakdownWithAr = printTaxBreakdownEntries && translatedLang
        ? await Promise.all(printTaxBreakdownEntries.map(async e => ({
            ...e,
            nameAr: await transliterateName(e.name, translatedLang),
          })))
        : printTaxBreakdownEntries;

      // Bilingual totals labels (Subtotal/Tax/Discount/Total Tax/Shipping/
      // TOTAL) were SUPPOSED to be saved separately at Save time (row_labels_ar/
      // tax_lines_ar/discount_lines_ar on the invoice record) — but
      // QuickInvoiceView's save path never actually writes these onto the
      // invoice, so they come back undefined for every saved invoice, and
      // the totals block silently prints English-only even on a bilingual
      // bill. Resolve them live here instead — same translate call the
      // save-time code was supposed to make — cached in localStorage per
      // language so repeat reprints don't re-hit the network.
      let rowLabelsAr = (b as any).row_labels_ar || (b as any).rowLabelsAr;
      let taxLinesAr = (b as any).tax_lines_ar || (b as any).taxLinesAr || [];
      let discountLinesAr = (b as any).discount_lines_ar || (b as any).discountLinesAr || [];
      const notesAr = (b as any).notes_ar || (b as any).notesAr;

      if (translatedLang && !rowLabelsAr) {
        const _cacheKey = (label: string) => `cloudairambo_label_cache_${translatedLang}_${label}`;
        const _jwtLbl = localStorage.getItem('cloudairambo_jwt') || '';
        const _BASELbl = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
        const _getCachedLabel = async (label: string) => {
          const cached = localStorage.getItem(_cacheKey(label));
          if (cached) return cached;
          try {
            const res = await fetch(`${_BASELbl}/translate?text=${encodeURIComponent(label)}&tl=${translatedLang}`, {
              headers: _jwtLbl ? { Authorization: `Bearer ${_jwtLbl}` } : {},
            });
            const data = await res.json();
            const translated = data.translated || label;
            localStorage.setItem(_cacheKey(label), translated);
            return translated;
          } catch {
            return label;
          }
        };
        // Batch translate all 7 receipt labels in 1 single call if not cached
        const standardLabels = ['Subtotal', 'Total', 'Shipping', 'Item', 'Qty', 'Amt', 'Total Tax'];
        const missingLabels = standardLabels.filter(l => !localStorage.getItem(_cacheKey(l)));

        if (missingLabels.length > 0) {
          const fetchedBatch = await translateBatchBills(missingLabels, translatedLang);
          missingLabels.forEach((label, idx) => {
            const tr = fetchedBatch[idx] || label;
            localStorage.setItem(_cacheKey(label), tr);
          });
        }

        const [subtotalAr, totalAr, shippingAr, itemAr, qtyAr, amtAr, taxAr] = standardLabels.map(
          l => localStorage.getItem(_cacheKey(l)) || l
        );
        rowLabelsAr = { subtotal: subtotalAr, total: totalAr, shipping: shippingAr, item: itemAr, qty: qtyAr, amt: amtAr, tax: taxAr };

        if ((!taxLinesAr || taxLinesAr.length === 0) && (tableEl as any)?.taxes?.length) {
          taxLinesAr = await Promise.all(
            ((tableEl as any).taxes || []).map(async (t: any) => ({ label: t.label, labelAr: await _getCachedLabel(t.label || 'Tax') }))
          );
        }
        if ((!discountLinesAr || discountLinesAr.length === 0) && (tableEl as any)?.discounts?.length) {
          discountLinesAr = await Promise.all(
            ((tableEl as any).discounts || []).map(async (d: any) => ({ label: d.label, labelAr: await _getCachedLabel(d.label || 'Discount') }))
          );
        }
      }

      // Same problem, same fix, for the QR captions — resolved above from
      // an 'invoice-label-'/'token-label-' element that a real backend-saved
      // invoice usually doesn't actually have under that id, so it was
      // always coming back undefined.
      if (!invoiceQRLabel && translatedLang) {
        try {
          const jwtCap = localStorage.getItem('cloudairambo_jwt') || '';
          const BASECap = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
          const capRes = await fetch(`${BASECap}/translate?text=${encodeURIComponent('Scan to view invoice')}&tl=${translatedLang}`, {
            headers: jwtCap ? { Authorization: `Bearer ${jwtCap}` } : {},
          });
          const capData = await capRes.json();
          invoiceQRLabel = capData?.translated || undefined;
        } catch (e) {
          console.warn('Live invoice QR caption translation failed:', e);
        }
      }
      if (!trackingQRLabel && translatedLang && trackingQrUrl) {
        try {
          const jwtCap2 = localStorage.getItem('cloudairambo_jwt') || '';
          const BASECap2 = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
          const capRes2 = await fetch(`${BASECap2}/translate?text=${encodeURIComponent('Scan to track your order')}&tl=${translatedLang}`, {
            headers: jwtCap2 ? { Authorization: `Bearer ${jwtCap2}` } : {},
          });
          const capData2 = await capRes2.json();
          trackingQRLabel = capData2?.translated || undefined;
        } catch (e) {
          console.warn('Live tracking QR caption translation failed:', e);
        }
      }

      // "To: <name>" Arabic line — resolved fresh at print time (transliterated
      // name, or translated "Walk-in Customer"), same helper QuickInvoice/POS
      // use for their own prints. Was never being set on the reprint path,
      // which is why the client line printed English-only on reprint.
      const jwtForClient = localStorage.getItem('cloudairambo_jwt') || '';
      const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
      const clientLineAr = translatedLang
        ? await resolveClientLineAr(
            b.clientName || 'Walk-in Customer',
            translatedLang,
            BASE,
            { Authorization: `Bearer ${jwtForClient}` }
          )
        : undefined;

      // Amount-in-words was never passed to the print job either — same
      // translate-or-transliterate fallback used for the PDF download path.
      const amountInWordsEn = amountToWords(b.amount, b.currency || 'USD');
      let amountInWordsForPrint: string | undefined = amountInWordsEn;
      let amountInWordsArForPrint: string | undefined;
      if (translatedLang) {
        try {
          const wRes = await fetch(`${BASE}/translate?text=${encodeURIComponent(amountInWordsEn)}&tl=${translatedLang}`, {
            headers: jwtForClient ? { Authorization: `Bearer ${jwtForClient}` } : {},
          });
          const wData = await wRes.json();
          const translated = wData?.translated || '';
          const echoed = !translated || translated.trim().toLowerCase() === amountInWordsEn.trim().toLowerCase();
          amountInWordsArForPrint = echoed ? await transliterateName(amountInWordsEn, translatedLang) : translated;
        } catch {
          console.warn('Amount-in-words translation failed for reprint.');
        }
      }

    
      const rowInc = (b as any).includeFields || (b as any).include_fields || (b.elements?.[0] as any)?.includeFields || (b.elements?.[0] as any)?.include_fields;

      const printPayload = {
        invoiceNumber: b.invoiceNumber,
        date: parseUtcDate(b.createdAt).toLocaleDateString(),
        clientName: rowInc?.name === false ? '' : (b.clientName || 'Walk-in Customer'),
        clientPhone: rowInc?.phone === false ? undefined : ((b as any).clientPhone || (b as any).client_phone || undefined),
        clientEmail: rowInc?.email === false ? undefined : (b.clientEmail || (b as any).client_email || undefined),
        buyerTaxId: rowInc?.taxId === false ? undefined : (getInvoiceTaxId(b) || undefined),
        buyerTaxLabel: inspectTaxId(getInvoiceTaxId(b), printSellerBuyerInfo.sellerCountry).taxLabel,
        notes: (b as any).notes || undefined,
        headerLines: headerLines.length ? headerLines : undefined,
        footerLines: footerLines.length ? footerLines : undefined,
        items,
        tableColumns: printTableColumns,
        itemCells: printItemCells,
        itemExtraLines: printItemExtraLines,
        itemExtraLinesAr: printItemExtraLinesAr,
        taxLines: ((tableEl as any)?.taxes || []).map((t: any) => ({
          ...t,
          labelAr: taxLinesAr.find((x: any) => x.label === t.label)?.labelAr,
        })),
        discountLines: ((tableEl as any)?.discounts || []).map((d: any) => ({
          ...d,
          labelAr: discountLinesAr.find((x: any) => x.label === d.label)?.labelAr,
        })),
        shippingCost: (tableEl as any)?.shippingCost || 0,
        netSubtotal,
        productTaxTotal,
        taxBreakdown: printTaxBreakdownWithAr,
        amountInWords: amountInWordsForPrint,
        amountInWordsAr: amountInWordsArForPrint,
        sym: sym(b.currency),
        invoiceQR: invoiceQrUrl,
        trackingQR: trackingQrUrl,
        invoiceQRLabel,
        trackingQRLabel,
        rowLabelsAr,
        notesAr,
        clientLineAr,
        arabicNumerals: translatedLang === 'ar',
      };

      let printed = false;
      if (useBTPrinter) printed = await printReceiptBT(printPayload as any);
      if (!printed && useUSBPrinter) printed = await printReceiptUSB(printPayload as any);
      if (!printed) {
        // Connection looked live a moment ago but the actual print call
        // failed (device dropped mid-job, etc.) — same rule as above:
        // open the picker so the cashier can reconnect, instead of
        // silently downgrading to the OS dialog.
        setBillsPrinterPicker(b);
        return;
      }
    } catch (e) {
      console.warn('Thermal print failed, falling back to PDF print:', e);
      onPrintPDF?.(b);
    } finally {
      setPrintingBillId(null);
    }
  };

  const isAllFilteredSelected = filteredBills.length > 0 && filteredBills.every(b => selectedIds.includes(b.id));
  const handleSelectAllToggle = () => {
    if (isAllFilteredSelected) setSelectedIds(prev => prev.filter(id => !filteredBills.map(b => b.id).includes(id)));
    else setSelectedIds(prev => { const newIds = [...prev]; filteredBills.forEach(b => { if (!newIds.includes(b.id)) newIds.push(b.id); }); return newIds; });
  };
  const handleSelectRowToggle = (id: string) => setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);

  return (
    <>
   <style>{`
  .bills-scroll {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .bills-scroll::-webkit-scrollbar {
    width: 0px;
    height: 0px;
    display: none;
  }
  .edit-modal-body {
    flex-direction: row;
  }
  .edit-modal-left {
    width: 490px;
  }
  @media (max-width: 768px) {
    .edit-modal-body {
      display: block !important;
      overflow-y: auto !important;
      -webkit-overflow-scrolling: touch;
      height: auto !important;
    }
    .edit-modal-left {
      width: 100% !important;
      border-right: none !important;
      border-bottom: 1px solid #e5e7eb;
      overflow: visible !important;
      height: auto !important;
    }
    .edit-modal-divider {
      display: none !important;
    }
    .edit-modal-preview {
      padding: 12px 10px !important;
      flex: none !important;
      overflow: visible !important;
      height: auto !important;
    }
  }
`}</style>
    <div        className="flex-1 overflow-y-auto bg-slate-50/50 p-6 space-y-6 select-none font-sans bills-scroll"     
      style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}    
      >

      {/* HEADER */}
<div className="flex flex-col gap-4 border-b border-slate-200 pb-5">
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
    <div>
      <h1 className="text-xl font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-2">
        <FileText className="w-5 h-5 text-blue-600" />
        <span>Saved Invoices Generated</span>
      </h1>
      <p className="text-xs text-slate-400 mt-1">Browse and manage all saved invoices. Export to Excel, merge PDFs, or sync to Google Drive.</p>
    </div>
  </div>

  {/* ── STATS BAR ── */}
  {(() => {
const displaySym = activeCurrency ? sym() : '';
    const norm = (s: string) => (s || '').toLowerCase().trim();

const revenueByCurrency = bills
  .filter(b => norm(b.status) === 'paid' && b.amount > 0)
  .reduce((acc, b) => {
    const c = b.currency || 'USD';
    acc[c] = (acc[c] || 0) + b.amount;
    return acc;
  }, {} as Record<string, number>);

const outstanding = bills
  .filter(b => !['paid','cancelled','draft'].includes(norm(b.status)) && b.amount > 0)
  .reduce((s, b) => s + Math.max(0, b.amount - (b.amountPaid || 0)), 0);
const overdue = bills
  .filter(b => norm(b.status) === 'overdue').length;
    const total = bills.length;
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '10px 14px' }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Revenue</div>
          {Object.entries(revenueByCurrency).length === 0
            ? <div style={{ fontSize: 15, fontWeight: 800, color: '#10b981', fontFamily: 'monospace' }}>$0.00</div>
            : Object.entries(revenueByCurrency).map(([currency, amount]) => (
              <div key={currency} style={{ fontSize: 13, fontWeight: 800, color: '#10b981', fontFamily: 'monospace', letterSpacing: '-0.02em' }}>
                {sym(currency)}{(amount as number).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            ))
          }
        </div>
        {[
          { label: 'Outstanding', value: `${displaySym}${outstanding.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
          { label: 'Overdue', value: String(overdue), color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
          { label: 'Total', value: String(total), color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: s.color, fontFamily: 'monospace', letterSpacing: '-0.02em' }}>{s.value}</div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>
    );
  })()}
</div>

      {/* FILTER BAR */}
      <div className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input type="text" placeholder="Search invoice #, client name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-9 pl-9 pr-4 bg-slate-50 border border-slate-200 rounded-10 text-xs font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 box-border" />
        </div>
        <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
          <div className="flex flex-wrap items-center gap-1 bg-slate-50 border border-slate-200 rounded-10 p-0.5 h-9 box-border">
            {[
              { val: 'all', label: 'All', active: '#1e293b' },
              { val: 'Draft', label: 'Draft', active: '#64748b' },
              { val: 'Sent', label: 'Sent', active: '#2563eb' },
              { val: 'Paid', label: 'Paid', active: '#059669' },
              { val: 'Overdue', label: 'Overdue', active: '#e11d48' },
              { val: 'Cancelled', label: 'Discarded', active: '#dc2626' },
            ].map(({ val, label, active }) => (
              <button key={val} onClick={() => setStatusFilter(val)}
                style={statusFilter === val ? { background: active, color: '#fff' } : {}}
                className={`h-7 px-2.5 rounded-lg text-[11px] font-bold uppercase transition-all cursor-pointer ${statusFilter === val ? 'shadow-xs' : 'text-slate-500 hover:text-slate-800'}`}>
                {label}
              </button>
            ))}
          </div>
          
          {/* Customer Type Filter (All / B2B / B2C) */}
          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-10 p-0.5 h-9 box-border">
            {[
              { val: 'all', label: 'All Clients' },
              { val: 'b2b', label: '🏢 B2B' },
              { val: 'b2c', label: '👤 B2C' },
            ].map(cf => (
              <button
                key={cf.val}
                onClick={() => setCustomerFilter(cf.val)}
                style={{
                  background: customerFilter === cf.val ? (cf.val === 'b2b' ? '#16a34a' : '#2563eb') : 'transparent',
                  color: customerFilter === cf.val ? '#fff' : '#64748b',
                }}
                className={`h-7 px-2.5 rounded-lg text-[11px] font-bold cursor-pointer transition-all ${customerFilter === cf.val ? 'shadow-xs' : 'hover:text-slate-800'}`}
              >
                {cf.label}
              </button>
            ))}
          </div>

          <div className="h-9 flex items-center text-[11px] text-slate-500 font-mono uppercase bg-slate-100/50 px-3 rounded-10 border border-slate-200 box-border">
            Count: <span className="font-bold text-slate-700 ml-1">{filteredBills.length}</span>
          </div>

          {/* Export Full Invoices */}
          <button onClick={() => {
            if (!perms.canExportExcel) {
              window.dispatchEvent(new CustomEvent('show-upgrade-plans'));
              return;
            }
            setExportDateRange('all');
            setExportPopover({ records: bills });
          }}
            className={`h-9 flex items-center gap-1.5 px-3.5 rounded-10 text-xs font-bold uppercase cursor-pointer border shadow-xs transition-all ${perms.canExportExcel ? 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200' : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'}`}>
            {perms.canExportExcel ? <FileSpreadsheet className="w-4 h-4 text-emerald-600" /> : <span>🔒</span>}
            <span>Export Invoices</span>
          </button>

          {/* Export Customers Only */}
          <button onClick={() => {
            if (!perms.canExportExcel) {
              window.dispatchEvent(new CustomEvent('show-upgrade-plans'));
              return;
            }
            handleExportCustomersOnly(filteredBills.length > 0 ? filteredBills : bills);
          }}
            title="Download Customer Directory Excel with phone, email, tax id & spend"
            className={`h-9 flex items-center gap-1.5 px-3.5 rounded-10 text-xs font-bold uppercase cursor-pointer border shadow-xs transition-all ${perms.canExportExcel ? 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'}`}>
            {perms.canExportExcel ? <User className="w-4 h-4 text-indigo-600" /> : <span>🔒</span>}
            <span>Customers Excel</span>
          </button>

          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowPrintSettings(s => !s)} title="Thermal Printer Settings"
              className="flex items-center justify-center w-9 h-9 rounded-10 cursor-pointer border shadow-xs bg-white hover:bg-slate-50 text-slate-700 border-slate-200 text-base">
              🖨
            </button>
  {showPrintSettings && (
    <div onClick={() => setShowPrintSettings(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 360, boxShadow: '0 24px 64px rgba(0,0,0,0.25)', position: 'relative' }}>
      <button onClick={() => setShowPrintSettings(false)} style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af' }}>×</button>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Printer Connection</div>

      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => {
          setUseUSBPrinter(false); localStorage.setItem('cloudairambo_qi_usb_print', 'false');
          setUseBTPrinter(false); localStorage.setItem('cloudairambo_qi_bt_print', 'false');
        }} style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          padding: '8px 4px', borderRadius: 8, border: '1.5px solid',
          cursor: 'pointer', fontSize: 10, fontWeight: 700,
          background: (!useUSBPrinter && !useBTPrinter) ? '#eef2ff' : '#fff',
          borderColor: (!useUSBPrinter && !useBTPrinter) ? '#6366f1' : '#e5e7eb',
          color: (!useUSBPrinter && !useBTPrinter) ? '#4f46e5' : '#6b7280',
        }}>
          <span style={{ fontSize: 15 }}>📶</span>
          WiFi
        </button>
        <button onClick={() => { setUseUSBPrinter(true); localStorage.setItem('cloudairambo_qi_usb_print', 'true'); setUseBTPrinter(false); localStorage.setItem('cloudairambo_qi_bt_print', 'false'); }}
          style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            padding: '8px 4px', borderRadius: 8, border: '1.5px solid',
            cursor: 'pointer', fontSize: 10, fontWeight: 700,
            background: useUSBPrinter ? '#eef2ff' : '#fff',
            borderColor: useUSBPrinter ? '#6366f1' : '#e5e7eb',
            color: useUSBPrinter ? '#4f46e5' : '#6b7280',
          }}>
          <span style={{ fontSize: 15 }}>🔌</span>
          USB
        </button>
        <button onClick={() => { setUseBTPrinter(true); localStorage.setItem('cloudairambo_qi_bt_print', 'true'); setUseUSBPrinter(false); localStorage.setItem('cloudairambo_qi_usb_print', 'false'); }}
          style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            padding: '8px 4px', borderRadius: 8, border: '1.5px solid',
            cursor: 'pointer', fontSize: 10, fontWeight: 700,
            background: useBTPrinter ? '#eef2ff' : '#fff',
            borderColor: useBTPrinter ? '#6366f1' : '#e5e7eb',
            color: useBTPrinter ? '#4f46e5' : '#6b7280',
          }}>
          <span style={{ fontSize: 15 }}>🔵</span>
          Bluetooth
        </button>
      </div>

      {!useUSBPrinter && !useBTPrinter && (
        <div style={{ fontSize: 9, color: '#6b7280', lineHeight: 1.5 }}>
          Opens your device's print dialog — pick your WiFi printer there,
          same as printing from any other app.
        </div>
      )}

      {useUSBPrinter && (
        <>
          <div style={{ fontSize: 9, color: '#6b7280' }}>{usbConnected ? `Connected: ${getUSBPrinterName() || 'USB Printer'}` : 'No USB printer connected'}</div>
          {!usbConnected && (
            <button onClick={async () => { const ok = await connectUSBPrinter(); setUsbConnected(ok); }}
              style={{ width: '100%', fontSize: 11, fontWeight: 700, padding: '6px 8px', borderRadius: 6, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer' }}>
              🔌 Connect USB Printer
            </button>
          )}
        </>
      )}

      {useBTPrinter && (
        <>
          <div style={{ fontSize: 9, color: '#6b7280' }}>{btConnected ? `Connected: ${getBTPrinterName() || 'BT Printer'}` : 'No Bluetooth printer connected'}</div>
          <button onClick={async () => { const char = await connectBluetoothPrinter(); setBtConnected(!!char); }}
            style={{ width: '100%', fontSize: 11, fontWeight: 700, padding: '6px 8px', borderRadius: 6, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer' }}>
            🔵 Connect Bluetooth Printer
          </button>
        </>
      )}

      <div style={{ fontSize: 9, color: '#9ca3af' }}>Only applies to thermal (THERMAL_80MM) invoices — others still use the regular print dialog.</div>
    </div>
    </div>
  )}
</div>
        </div>
      </div>

      {/* EXPORT DATE-RANGE MODAL */}
      {exportPopover && (
        <div onClick={() => setExportPopover(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 380, boxShadow: '0 24px 64px rgba(0,0,0,0.25)', position: 'relative' }}>
            <button onClick={() => setExportPopover(null)} style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#9ca3af' }}>×</button>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>Export to Excel</div>
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{exportPopover.records.length} invoice(s) available — pick a date range</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {([
                { val: 'all', label: 'All Time' },
                { val: 'today', label: 'Today' },
                { val: 'week', label: 'This Week' },
                { val: 'month', label: 'This Month' },
                { val: 'year', label: 'This Year' },
                { val: 'custom', label: 'Custom' },
              ] as const).map(opt => (
                <button
                  key={opt.val}
                  onClick={() => setExportDateRange(opt.val)}
                  style={{
                    padding: '7px 6px', borderRadius: 8, border: '1.5px solid',
                    fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    background: exportDateRange === opt.val ? '#eef2ff' : '#fff',
                    borderColor: exportDateRange === opt.val ? '#6366f1' : '#e5e7eb',
                    color: exportDateRange === opt.val ? '#4f46e5' : '#6b7280',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {exportDateRange === 'custom' && (
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 10,
                background: 'linear-gradient(135deg,#f5f3ff,#eef2ff)',
                border: '1px solid #e0e7ff', borderRadius: 12, padding: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: -2 }}>
                  <span style={{ fontSize: 14 }}>📅</span>
                  <span style={{ fontSize: 10, fontWeight: 800, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Pick your range
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 5 }}>From</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="date"
                        value={exportCustomFrom}
                        onChange={e => setExportCustomFrom(e.target.value)}
                        style={{
                          width: '100%', padding: '10px 12px', borderRadius: 10,
                          border: '1.5px solid #ddd6fe', fontSize: 13, fontWeight: 600,
                          color: '#312e81', background: '#fff', outline: 'none',
                          boxSizing: 'border-box', accentColor: '#6366f1',
                          boxShadow: '0 1px 3px rgba(99,102,241,0.08)',
                          transition: 'border-color 0.15s, box-shadow 0.15s',
                        }}
                        onFocus={e => { e.currentTarget.style.borderColor = '#818cf8'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(129,140,248,0.15)'; }}
                        onBlur={e => { e.currentTarget.style.borderColor = '#ddd6fe'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(99,102,241,0.08)'; }}
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 5 }}>To</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="date"
                        value={exportCustomTo}
                        onChange={e => setExportCustomTo(e.target.value)}
                        style={{
                          width: '100%', padding: '10px 12px', borderRadius: 10,
                          border: '1.5px solid #ddd6fe', fontSize: 13, fontWeight: 600,
                          color: '#312e81', background: '#fff', outline: 'none',
                          boxSizing: 'border-box', accentColor: '#6366f1',
                          boxShadow: '0 1px 3px rgba(99,102,241,0.08)',
                          transition: 'border-color 0.15s, box-shadow 0.15s',
                        }}
                        onFocus={e => { e.currentTarget.style.borderColor = '#818cf8'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(129,140,248,0.15)'; }}
                        onBlur={e => { e.currentTarget.style.borderColor = '#ddd6fe'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(99,102,241,0.08)'; }}
                      />
                    </div>
                  </div>
                </div>

                {exportCustomFrom && exportCustomTo && (
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: '#4f46e5',
                    background: 'rgba(255,255,255,0.6)', borderRadius: 8,
                    padding: '6px 10px', textAlign: 'center',
                  }}>
                    {new Date(exportCustomFrom).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    {'  →  '}
                    {new Date(exportCustomTo).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                )}
              </div>
            )}

            <div style={{ fontSize: 9, color: '#9ca3af', lineHeight: 1.5 }}>
              Exports separate tabs for Paid, Pending, Overdue, Draft, and Cancelled — plus one combined "All Invoices" tab with a full summary at the bottom.
            </div>

            <button
              onClick={() => {
                if (exportDateRange === 'custom' && (!exportCustomFrom || !exportCustomTo)) {
                  alert('Pick both a from and to date.');
                  return;
                }
                handleExportSelectedToExcel(exportPopover.records, exportDateRange, exportCustomFrom, exportCustomTo);
                setExportPopover(null);
              }}
              style={{ width: '100%', padding: 11, borderRadius: 10, border: 'none', background: 'linear-gradient(to right,#3b82f6,#9333ea)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <FileSpreadsheet className="w-4 h-4" /> Export
            </button>
          </div>
        </div>
      )}

      {/* PRINTER CONNECTION PICKER — opens on Print instead of jumping straight to the OS dialog */}
      {billsPrinterPicker && (
        <div onClick={() => setBillsPrinterPicker(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 450, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 20, padding: '24px 22px', maxWidth: 340, width: '100%', boxShadow: '0 32px 80px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>Print Receipt — {billsPrinterPicker.invoiceNumber}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 14 }}>No printer connected. Choose a connection, or use the dialog instead.</div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <button onClick={() => {
                setUseUSBPrinter(false); localStorage.setItem('cloudairambo_qi_usb_print', 'false');
                setUseBTPrinter(false); localStorage.setItem('cloudairambo_qi_bt_print', 'false');
              }} style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '10px 4px', borderRadius: 10, border: '1.5px solid', cursor: 'pointer', fontSize: 10, fontWeight: 700,
                background: (!useUSBPrinter && !useBTPrinter) ? '#eef2ff' : '#fff',
                borderColor: (!useUSBPrinter && !useBTPrinter) ? '#6366f1' : '#e5e7eb',
                color: (!useUSBPrinter && !useBTPrinter) ? '#4f46e5' : '#6b7280',
              }}>
                <span style={{ fontSize: 16 }}>📶</span> WiFi
              </button>
              <button onClick={() => { setUseUSBPrinter(true); localStorage.setItem('cloudairambo_qi_usb_print', 'true'); setUseBTPrinter(false); localStorage.setItem('cloudairambo_qi_bt_print', 'false'); }}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '10px 4px', borderRadius: 10, border: '1.5px solid', cursor: 'pointer', fontSize: 10, fontWeight: 700,
                  background: useUSBPrinter ? '#eef2ff' : '#fff',
                  borderColor: useUSBPrinter ? '#6366f1' : '#e5e7eb',
                  color: useUSBPrinter ? '#4f46e5' : '#6b7280',
                }}>
                <span style={{ fontSize: 16 }}>🔌</span> USB
              </button>
              <button onClick={() => { setUseBTPrinter(true); localStorage.setItem('cloudairambo_qi_bt_print', 'true'); setUseUSBPrinter(false); localStorage.setItem('cloudairambo_qi_usb_print', 'false'); }}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '10px 4px', borderRadius: 10, border: '1.5px solid', cursor: 'pointer', fontSize: 10, fontWeight: 700,
                  background: useBTPrinter ? '#eef2ff' : '#fff',
                  borderColor: useBTPrinter ? '#6366f1' : '#e5e7eb',
                  color: useBTPrinter ? '#4f46e5' : '#6b7280',
                }}>
                <span style={{ fontSize: 16 }}>🔵</span> Bluetooth
              </button>
            </div>

            {useUSBPrinter && (
              <>
                <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 8 }}>
                  {isUSBPrinterConnected() ? `Connected: ${getUSBPrinterName() || 'USB Printer'}` : 'No USB printer connected'}
                </div>
                {!isUSBPrinterConnected() && (
                  <button onClick={async () => { const ok = await connectUSBPrinter(); setUsbConnected(ok); }}
                    style={{ width: '100%', fontSize: 11, fontWeight: 700, padding: '8px 8px', borderRadius: 8, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer', marginBottom: 10 }}>
                    🔌 Connect USB Printer
                  </button>
                )}
              </>
            )}

            {useBTPrinter && (
              <>
                <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 8 }}>
                  {btConnected ? `Connected: ${getBTPrinterName() || 'BT Printer'}` : 'No Bluetooth printer connected'}
                </div>
                <button onClick={async () => { const char = await connectBluetoothPrinter(); setBtConnected(!!char); }}
                  style={{ width: '100%', fontSize: 11, fontWeight: 700, padding: '8px 8px', borderRadius: 8, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer', marginBottom: 10 }}>
                  🔵 Connect Bluetooth Printer
                </button>
              </>
            )}

            <button onClick={async () => {
              const bill = billsPrinterPicker;
              setBillsPrinterPicker(null);
              if (bill) await handlePrintBill(bill);
            }}
              style={{ width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', background: '#0f172a', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer', marginBottom: 8 }}>
              🖨 Print
            </button>
            <button onClick={() => {
              const bill = billsPrinterPicker;
              setBillsPrinterPicker(null);
              if (bill) onPrintPDF?.(bill);
            }}
              style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f8fafc', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Print via Dialog instead
            </button>
          </div>
        </div>
      )}

      {/* SELECTION BANNER */}
      {selectedIds.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg"><CheckCircle className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-xs font-bold text-slate-900 uppercase tracking-wider">Claims Selection Active</p>
              <p className="text-xs text-blue-700/80 mt-0.5 font-medium">Selected <span className="font-extrabold text-slate-900">{selectedIds.length}</span> invoices for bulk action.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds([])} className="h-9 text-xs text-slate-500 hover:text-slate-800 font-bold uppercase px-3 hover:bg-slate-100 rounded-10 cursor-pointer">Clear</button>
            <button onClick={() => {
              if (!perms.canExportExcel) {
                window.dispatchEvent(new CustomEvent('show-upgrade-plans'));
                return;
              }
              setExportDateRange('all');
              setExportPopover({ records: bills.filter(b => selectedIds.includes(b.id)) });
            }}
              className={`h-9 px-4 rounded-10 text-xs font-bold uppercase cursor-pointer flex items-center gap-2 transition-all ${perms.canExportExcel ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}>
              {perms.canExportExcel ? <FileSpreadsheet className="w-4 h-4" /> : <span>🔒</span>}
              <span>Export ({selectedIds.length})</span>
            </button>
            {onExportMergedPDF && (
              <button onClick={() => {
                if (!perms.canMergePDF) {
                  window.dispatchEvent(new CustomEvent('show-upgrade-plans'));
                  return;
                }
                onExportMergedPDF(bills.filter(b => selectedIds.includes(b.id)));
              }}
                className={`h-9 px-4 rounded-10 text-xs font-bold uppercase cursor-pointer flex items-center gap-2 transition-all ${perms.canMergePDF ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-xs' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}>
                {perms.canMergePDF ? <Download className="w-4 h-4" /> : <span>🔒</span>}
                <span>Merge PDF ({selectedIds.length})</span>
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* TABLE */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-visible">
        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full text-left border-collapse text-xs overflow-visible">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 font-extrabold text-slate-600 uppercase tracking-wider text-[10.5px]">
                <th className="p-4 w-12 text-center">
                  <input type="checkbox" checked={isAllFilteredSelected} onChange={handleSelectAllToggle} className="w-4 h-4 rounded cursor-pointer" />
                </th>
                <th className="p-4">Invoice ID</th>
                <th className="p-4">Billing Client Profile</th>
                <th className="p-4">Amount Invoice Value</th>
                <th className="p-4">Dates (Issue / Due)</th>
                <th className="p-4">Status & Status Control</th>
                <th className="p-4">Reason</th>
                <th className="p-4">Payment Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-150 font-sans text-slate-700 font-medium">
              {filteredBills.map((b) => (
                <motion.tr key={b.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
                  className="hover:bg-slate-50/50 transition-all overflow-visible">
                  <td className="p-4 text-center">
                    <input type="checkbox" checked={selectedIds.includes(b.id)} onChange={() => handleSelectRowToggle(b.id)} className="w-4 h-4 rounded cursor-pointer" />
                  </td>
                  <td className="p-4 font-mono font-bold text-purple-700">
                    {b.invoiceNumber?.startsWith('PENDING-') ? (
                      ['Cancelled', 'cancelled'].includes(b.status) ? (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca',
                          borderRadius: 6, padding: '3px 8px', fontSize: 10, fontWeight: 800,
                          textTransform: 'uppercase', letterSpacing: '0.03em',
                        }}>
                          ✕ Cashier Cancelled
                        </span>
                      ) : (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a',
                          borderRadius: 6, padding: '3px 8px', fontSize: 10, fontWeight: 800,
                          textTransform: 'uppercase', letterSpacing: '0.03em',
                        }}>
                          ⏳ Awaiting Payment
                        </span>
                      )
                    ) : b.invoiceNumber}
                  </td>
                  <td className="p-4">
                    {(() => {
                      const taxId = getInvoiceTaxId(b);
                      const isB2b = isInvoiceB2B(b);
                      const phone = (b as any).clientPhone || '';
                      return (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-slate-900 text-[12px]">{b.clientName || 'Walk-in Customer'}</span>
                            {isB2b ? (
                              <span style={{
                                fontSize: 9, fontWeight: 800, textTransform: 'uppercase',
                                background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
                                padding: '1px 5px', borderRadius: 5, letterSpacing: '0.04em',
                              }}>
                                🏢 B2B
                              </span>
                            ) : (
                              <span style={{
                                fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                                background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0',
                                padding: '1px 5px', borderRadius: 5, letterSpacing: '0.04em',
                              }}>
                                👤 B2C
                              </span>
                            )}
                          </div>

                          {taxId ? (() => {
                            const sellerCountry = getSellerBuyerInfo(b).sellerCountry || 'IN';
                            const info = inspectTaxId(taxId, sellerCountry);
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                <div style={{
                                  fontFamily: 'monospace', fontSize: 10, fontWeight: 700,
                                  color: '#1e293b', background: '#f1f5f9', padding: '1px 6px',
                                  borderRadius: 4, width: 'fit-content', border: '1px solid #e2e8f0',
                                }}>
                                  🏷️ {taxId}
                                </div>
                                <span
                                  onClick={() => setShowTaxDisclaimerModal(true)}
                                  style={{
                                    fontSize: 9, fontWeight: 800,
                                    color: info.isValid ? '#16a34a' : '#ef4444',
                                    background: info.isValid ? '#f0fdf4' : '#fef2f2',
                                    border: `1px solid ${info.isValid ? '#bbf7d0' : '#fecaca'}`,
                                    padding: '1px 5px', borderRadius: 4, cursor: 'pointer',
                                    display: 'inline-flex', alignItems: 'center', gap: 2
                                  }}
                                  title="Click to view tax ID validation disclaimer"
                                >
                                  {info.isValid ? '✓ Valid' : '✗ Invalid'} <span style={{ opacity: 0.6, fontSize: 8 }}>ℹ️</span>
                                </span>
                              </div>
                            );
                          })() : isB2b ? (
                            (() => {
                              const tableEl = b?.elements?.find((el: any) => el.type === 'table');
                              const customName = (tableEl?.rows || []).find((r: any) => r.values?.b2bTaxName || r.values?.b2b_tax_name)?.values?.b2bTaxName ||
                                                 (tableEl?.rows || []).find((r: any) => r.values?.b2bTaxName || r.values?.b2b_tax_name)?.values?.b2b_tax_name;
                              return customName ? (
                                <div style={{
                                  fontFamily: 'monospace', fontSize: 9.5, fontWeight: 700,
                                  color: '#16a34a', background: '#f0fdf4', padding: '1px 6px',
                                  borderRadius: 4, width: 'fit-content', border: '1px solid #bbf7d0',
                                }}>
                                  🏷️ {customName}
                                </div>
                              ) : null;
                            })()
                          ) : null}

                          <div className="flex items-center gap-2 text-[10px] text-slate-500 font-mono flex-wrap">
                            {phone && <span>📞 {phone}</span>}
                            {phone && b.clientEmail && <span>•</span>}
                            {b.clientEmail && <span>✉️ {b.clientEmail}</span>}
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="p-4 font-sans">
                    <div className="font-bold text-[12.5px] text-slate-800 font-mono">{formatAmountVal(b.amount, b.currency)}</div>
                    {(b.amountPaid !== undefined && b.amountPaid > 0) && (
                      <div className="text-[10px] space-y-0.5 mt-1">
                        <div className="text-emerald-700 font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3" /><span>Paid: {formatAmountVal(b.amountPaid, b.currency)}</span></div>
                        {b.amount - (b.amountPaid || 0) > 0 ? (
                          <div className="text-rose-600 font-mono text-[9px]">Due: {formatAmountVal(b.amount - (b.amountPaid || 0), b.currency)}</div>
                        ) : (
                          <div className="text-[9px] text-emerald-800 font-bold bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5 w-max font-mono uppercase">Paid fully</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-[11px]">
                    <div className="flex flex-col gap-1">
                      <div className="text-slate-700 font-bold flex items-center gap-1">
                        <span className="text-[9px] font-bold text-slate-450 uppercase w-10 shrink-0">Issue:</span>
                        <span className="font-mono text-slate-600">{parseUtcDate(b.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      </div>
                      {(() => {
                        const check = isOverdueOrNearing(b);
                        const isPaid = ['Paid', 'paid'].includes(b.status);
                        const isCancelled = ['Cancelled', 'cancelled'].includes(b.status);
                        if (isPaid) return <div className="text-emerald-700 flex items-center gap-1"><span className="text-[9px] font-bold text-slate-450 uppercase w-10 shrink-0">Due:</span><span className="font-mono line-through text-slate-400">{check.dueDateStr}</span></div>;
                        if (isCancelled) return <div className="flex items-center gap-1"><span className="text-[9px] font-bold text-slate-450 uppercase w-10 shrink-0">Due:</span><span className="font-mono text-slate-400">Cancelled</span></div>;
                        return (
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1"><span className="text-[9px] font-bold text-slate-450 uppercase w-10 shrink-0">Due:</span><span className="font-mono text-slate-900 font-extrabold">{check.dueDateStr}</span></div>
                            {check.isOverdue ? (
                              <span className="inline-flex items-center gap-0.5 font-mono text-[8px] font-black uppercase text-rose-600 bg-rose-50 border border-rose-100 px-1 py-0.5 rounded w-max">
                                <AlertCircle className="w-2.5 h-2.5" /><span>{check.daysLeft < 0 ? `${Math.abs(check.daysLeft)}d Overdue` : 'Overdue'}</span>
                              </span>
                            ) : (
                              <span className={`inline-flex items-center gap-0.5 font-mono text-[8px] font-bold uppercase px-1 py-0.5 rounded w-max ${check.isNearing ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-blue-50 text-purple-800 border border-blue-100'}`}>
                                <Clock className="w-2.5 h-2.5" /><span>In {check.daysLeft}d</span>
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <div className="relative" onClick={e => e.stopPropagation()}>
                      <span onClick={() => {
  const isPosDiscard = b.pageSize === 'THERMAL_80MM' && ['Cancelled','cancelled'].includes(b.status);
  if (isPosDiscard) return;
  if (!perms.canEditInvoice) return;
  setStatusMenuOpenId(prev => prev === b.id ? null : b.id);
}} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider border ${(perms.canEditInvoice && !(b.pageSize === 'THERMAL_80MM' && ['Cancelled','cancelled'].includes(b.status))) ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed opacity-90'} ${
                        ['Paid','paid'].includes(b.status) ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : ['Draft','draft'].includes(b.status) ? 'bg-slate-100 border-slate-300 text-slate-600'
                        : ['Sent','pending'].includes(b.status) ? 'bg-blue-50 border-blue-200 text-blue-700'
                        : b.status === 'Overdue' ? 'bg-rose-50 border-rose-200 text-rose-700'
                        : ['Updated','edited'].includes(b.status) ? 'bg-sky-50 border-sky-200 text-sky-600'
                        : ['Cancelled','cancelled'].includes(b.status) ? 'bg-red-50 border-red-300 text-red-700'
                        : 'bg-zinc-100 border-zinc-300 text-zinc-600'
                      }`}>
                        {['Paid','paid'].includes(b.status) ? <><CheckCircle className="w-3.5 h-3.5" /><span>PAID</span></>
                          : ['Draft','draft'].includes(b.status) ? <><Clock className="w-3.5 h-3.5" /><span>DRAFT</span></>
                          : ['Sent','pending'].includes(b.status) ? <><ExternalLink className="w-3.5 h-3.5" /><span>SENT</span></>
                          : b.status === 'Overdue' ? <><AlertCircle className="w-3.5 h-3.5 animate-pulse" /><span>OVERDUE</span></>
                          : ['Updated','edited'].includes(b.status) ? (
  <span className="flex flex-col items-start gap-0">
    <span className="flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /><span>EDITED</span></span>
    {(b as any).lastEditedAt && (
      <span className="text-[8px] text-sky-500 font-mono">
        {parseUtcDate((b as any).lastEditedAt).toLocaleString()}
      </span>
    )}
  </span>
)
: ['Cancelled','cancelled'].includes(b.status) ? (
  <><XCircle className="w-3.5 h-3.5" /><span>DISCARDED</span></>
)
: <><AlertCircle className="w-3.5 h-3.5" /><span>UNKNOWN</span></>}
                      </span>

                      <AnimatePresence>
                      {statusMenuOpenId === b.id && (
                        <motion.div
                          initial={{ opacity: 0, y: -6, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -6, scale: 0.96 }}
                          transition={{ duration: 0.14, ease: 'easeOut' }}
                          style={{
                            position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 50,
                            background: '#fff', border: '1px solid #eef1f5', borderRadius: 14,
                            boxShadow: '0 12px 32px -4px rgba(15,23,42,0.16), 0 4px 10px -4px rgba(15,23,42,0.08)',
                            minWidth: 190, overflow: 'hidden',
                          }}
                        >
                          {pendingCancelBill?.id === b.id ? (
                            <div style={{ padding: '12px 14px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                <XCircle className="w-3.5 h-3.5" style={{ color: '#dc2626' }} />
                                <span style={{ fontSize: 11, fontWeight: 800, color: '#111827' }}>Discard Invoice</span>
                              </div>
                              <p style={{ fontSize: 10, color: '#94a3b8', marginBottom: 8, lineHeight: 1.4 }}>
                                Add a reason for discarding this invoice.
                              </p>
                              <textarea
                                autoFocus
                                value={cancelReasonDraft}
                                onChange={e => setCancelReasonDraft(e.target.value)}
                                placeholder="e.g. Duplicate, client cancelled order..."
                                rows={2}
                                style={{
                                  width: '100%', resize: 'none', fontSize: 11, padding: '7px 9px',
                                  borderRadius: 8, border: '1px solid #e5e7eb', outline: 'none',
                                  fontFamily: 'inherit', color: '#111827', background: '#f8fafc',
                                  boxSizing: 'border-box', marginBottom: 8,
                                }}
                                onFocus={e => (e.currentTarget.style.borderColor = '#dc2626')}
                                onBlur={e => (e.currentTarget.style.borderColor = '#e5e7eb')}
                              />
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  onClick={() => { setPendingCancelBill(null); setCancelReasonDraft(''); }}
                                  style={{
                                    flex: 1, padding: '7px 0', borderRadius: 8, border: '1px solid #e5e7eb',
                                    background: '#fff', color: '#6b7280', fontSize: 10.5, fontWeight: 700,
                                    cursor: 'pointer', textTransform: 'uppercase',
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleConfirmCancel(b)}
                                  style={{
                                    flex: 1.4, padding: '7px 0', borderRadius: 8, border: 'none',
                                    background: '#dc2626', color: '#fff', fontSize: 10.5, fontWeight: 800,
                                    cursor: 'pointer', textTransform: 'uppercase',
                                    boxShadow: '0 2px 8px rgba(220,38,38,0.35)',
                                  }}
                                >
                                  Confirm Discard
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div style={{
                                padding: '9px 14px', fontSize: 9.5, fontWeight: 800, color: '#9aa4b2',
                                textTransform: 'uppercase', letterSpacing: '0.06em',
                                borderBottom: '1px solid #f1f5f9', background: '#fafbfc',
                              }}>
                                Set Status
                              </div>
                              <div style={{ padding: 5 }}>
                                {STATUS_OPTIONS.map(opt => {
                                  const isActive = b.status.toLowerCase() === opt.val.toLowerCase();
                                  return (
                                    <button
                                      key={opt.val}
                                      onClick={() => handleSetStatus(b, opt.val)}
                                      style={{
                                        display: 'flex', alignItems: 'center', gap: 9, width: '100%',
                                        padding: '8px 10px', background: isActive ? `${opt.color}0F` : 'transparent',
                                        border: 'none', borderRadius: 8,
                                        fontSize: 11.5, fontWeight: isActive ? 800 : 600, textAlign: 'left', cursor: 'pointer',
                                        color: isActive ? opt.color : '#374151',
                                        transition: 'background 0.12s ease',
                                      }}
                                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
                                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                                    >
                                      <span style={{
                                        width: 7, height: 7, borderRadius: 99, background: opt.color, flexShrink: 0,
                                        boxShadow: isActive ? `0 0 0 3px ${opt.color}22` : 'none',
                                      }} />
                                      <span style={{ flex: 1 }}>{opt.label}</span>
                                      {isActive && <Check className="w-3 h-3" style={{ color: opt.color, flexShrink: 0 }} />}
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </motion.div>
                      )}
                      </AnimatePresence>
                      </div>

                      {(b as any).payment_mode && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          background: (b as any).payment_mode === 'cash' ? '#f0fdf4'
                                    : (b as any).payment_mode === 'card' ? '#eff6ff'
                                    : '#faf5ff',
                          color: (b as any).payment_mode === 'cash' ? '#16a34a'
                               : (b as any).payment_mode === 'card' ? '#2563eb'
                               : '#7c3aed',
                          border: `1px solid ${(b as any).payment_mode === 'cash' ? '#bbf7d0' : (b as any).payment_mode === 'card' ? '#bfdbfe' : '#ddd6fe'}`,
                          borderRadius: 6, fontSize: 10, fontWeight: 700,
                          padding: '2px 7px', textTransform: 'uppercase',
                        }}>
                          {(b as any).payment_mode === 'cash' ? '💵' : (b as any).payment_mode === 'card' ? '💳' : '📱'}
                          {' '}{(b as any).payment_mode}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    {['Cancelled','cancelled'].includes(b.status) && (b as any).cancellation_reason ? (
                      <span className="text-[10px] text-red-600 font-mono font-semibold">
                        {(b as any).cancellation_reason}
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-300">—</span>
                    )}
                  </td>
                  <td className="p-4">
                    {(() => {
                      // Cash is settled at the counter — there's no gateway to
                      // confirm, so a stray Razorpay "pending" row created
                      // automatically alongside every invoice is meaningless here.
                      if ((b as any).payment_mode === 'cash') {
                        return <span className="text-[10px] text-slate-300">—</span>;
                      }
                      const pay = paymentStatusMap[b.id];
                      if (!pay) return <span className="text-[10px] text-slate-300">—</span>;
                      const cfg = {
                        success: { label: 'Accepted', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: <CheckCircle className="w-3 h-3" /> },
                        pending: { label: 'Pending', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: <Clock className="w-3 h-3" /> },
                        failed: { label: 'Rejected', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: <XCircle className="w-3 h-3" /> },
                        refunded: { label: 'Refunded', bg: 'bg-slate-100', border: 'border-slate-300', text: 'text-slate-600', icon: <RefreshCw className="w-3 h-3" /> },
                      }[pay.status] || { label: pay.status, bg: 'bg-slate-100', border: 'border-slate-300', text: 'text-slate-600', icon: null };
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9.5px] font-extrabold uppercase tracking-wider border w-max ${cfg.bg} ${cfg.border} ${cfg.text}`}>
                            {cfg.icon}<span>{cfg.label}</span>
                          </span>
                          <span className="text-[8.5px] text-slate-400 font-mono">{pay.gateway}</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <>
                        <button onClick={() => setSendingInvoice(b)}
                          className="w-8 h-8 flex items-center justify-center border border-indigo-200 hover:bg-indigo-50 bg-indigo-50/20 text-indigo-700 rounded-lg shadow-xs cursor-pointer hover:scale-105 transition-all" title="Remind">
                          <Clock className="w-4 h-4" />
                        </button>
                        <div className="relative group">
                            <button className="w-8 h-8 flex items-center justify-center border border-amber-200 hover:bg-amber-50 bg-amber-50/20 text-amber-700 rounded-lg shadow-xs cursor-pointer hover:scale-105 transition-all" title="Send Email">
                              <Mail className="w-4 h-4" />
                            </button>
                            <div className="absolute right-0 top-8 z-50 hidden group-hover:flex flex-col bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden w-48">
                              <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                                <p className="text-[9px] font-black uppercase tracking-wider text-slate-500">Send to</p>
                                <p className="text-[10px] font-mono text-slate-700 truncate">{b.clientEmail || 'No email'}</p>
                              </div>
                              <button
                                onClick={async () => {
  if (!b.clientEmail) { alert('No client email on this invoice.'); return; }
  const templates = await loadMessageTemplates((b as any).outlet_id);
  const activeCompany = companyProfile.name || 'Our Company';
  const templateVars = {
    name: b.clientName,
    invoice: b.invoiceNumber,
    amount: b.amount,
    currency: sym(b.currency),
    date: parseUtcDate(b.createdAt).toLocaleDateString(),
    company: activeCompany,
  };
  let subject = applyTemplate(templates.email_subject, templateVars);
  let bodyText = applyTemplate(templates.email_body, templateVars);
  if (activeCompany) {
    subject = subject.replace(/CloudAIRambo/gi, activeCompany);
    bodyText = bodyText.replace(/CloudAIRambo/gi, activeCompany);
  }
  const html = `<div style="font-family:sans-serif;padding:24px;color:#334155;max-width:600px;margin:auto;border:1px solid #e2e8f0;border-radius:12px;background:#fff">
    ${bodyText.split('\n').map(line => `<p style="margin:0 0 8px">${line}</p>`).join('')}
    <p style="font-size:12px;color:#94a3b8;margin-top:16px">Invoice #${b.invoiceNumber} · ${parseUtcDate(b.createdAt).toLocaleDateString()}</p>
  </div>`;
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
  try {
    let attachments: any[] = [];
    const pdfRes = await fetch(`${BASE}/invoices/${b.id}/pdf`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (pdfRes.ok) {
      const blob = await pdfRes.blob();
      const base64 = await new Promise<string>(res3 => {
        const reader = new FileReader();
        reader.onloadend = () => res3((reader.result as string).split(',')[1]);
        reader.readAsDataURL(blob);
      });
      attachments = [{ filename: `Invoice_${b.invoiceNumber}.pdf`, content: base64, encoding: 'base64' }];
    }
    const res = await fetch(`${BASE}/email/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ to: b.clientEmail, subject, html, attachments, outlet_id: (b as any).outlet_id }),
    });
    if (!res.ok) throw new Error('Failed');
    alert(`Email sent to ${b.clientEmail}!`);
    onAddLog?.('PAYMENT_REMINDER_SENT', `Quick email sent to ${b.clientEmail} for Invoice ${b.invoiceNumber}.`);
    postLog('INVOICE_EMAIL_SENT', `Quick email sent to ${b.clientEmail} | Invoice ${b.invoiceNumber}`);
  } catch { alert('Email send failed.'); }
}}
                                className="px-3 py-2.5 text-left text-[11px] font-bold text-blue-700 hover:bg-blue-50 flex items-center gap-2 cursor-pointer"
                              >
                                <Send className="w-3.5 h-3.5" /> Send via Gmail
                              </button>
                              <button
                                onClick={() => {
                                  const check = isOverdueOrNearing(b);
                                  const templateKey = check.isOverdue ? 'urgent' : 'friendly';
                                  const temp = TEMPLATES_MAP[templateKey];
                                  const remaining = b.amount - (b.amountPaid || 0);
                                  const amtStr = (remaining > 0 ? remaining : b.amount).toLocaleString('en-US', { minimumFractionDigits: 2 });
                                  const dueDateStr = getInvoiceDueDate(b.createdAt).toLocaleDateString();
                                  const subject = temp.subject(b.invoiceNumber);
                                  const body = `Hi ${b.clientName},\n\nInvoice #${b.invoiceNumber} — ${amtStr} due on ${dueDateStr}.\n\nPlease clear at your earliest.`;
                                  setEmailTemplateKey(templateKey);
setSendingInvoice(b);
                                }}
                                className="px-3 py-2.5 text-left text-[11px] font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer border-t border-slate-100"
                              >
                                <Mail className="w-3.5 h-3.5" /> Open in Mail App
                              </button>
                            </div>
                          </div>
                          <button onClick={async () => {
  const sellerCountry = getSellerBuyerInfo(b).sellerCountry || 'IN';
  const cleanPhone = getCleanE164Phone((b as any).clientPhone || '', sellerCountry);
  if (!cleanPhone) { alert('No valid phone number on this invoice.'); return; }
  if (!onSendInvoiceWhatsApp) { alert('WhatsApp not configured.'); return; }
  try {
    await onSendInvoiceWhatsApp(b, cleanPhone);
    alert(`Invoice sent to ${cleanPhone} via WhatsApp!`);
    onAddLog?.('WHATSAPP_SENT', `Invoice sent to ${cleanPhone} for Invoice ${b.invoiceNumber}.`);
  } catch (err: any) {
    alert(`Failed: ${err.message}`);
  }
}}
                          className="w-8 h-8 flex items-center justify-center border border-green-200 hover:bg-green-50 bg-green-50/20 text-green-700 rounded-lg shadow-xs cursor-pointer hover:scale-105 transition-all" title="WhatsApp">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        </button>
                      </>
                      {onReuseInvoiceLayout && (
                        <button onClick={() => onReuseInvoiceLayout(b)}
                          className="w-8 h-8 flex items-center justify-center border border-slate-200 hover:bg-sky-50 hover:border-sky-200 hover:text-sky-650 rounded-lg text-sky-700 shadow-xs cursor-pointer hover:scale-105 transition-all" title="Reuse Layout">
                          <Copy className="w-4 h-4" />
                        </button>
                      )}
                      {perms.canEditInvoice && (
                        <button onClick={() => openEdit(b)}
                          className="w-8 h-8 flex items-center justify-center border border-slate-200 hover:bg-emerald-50 hover:border-emerald-250 hover:text-emerald-700 rounded-lg text-slate-600 shadow-xs cursor-pointer hover:scale-105 transition-all" title="Edit Invoice">
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {(() => {
  const qrEl = b.elements.find(el => el.type === 'qr' && el.qrValue);
  if (!qrEl) return null;
  return (
    <button onClick={() => { navigator.clipboard.writeText(qrEl.qrValue!); alert('Share link copied!'); }}
      className="w-8 h-8 flex items-center justify-center border border-slate-200 hover:bg-violet-50 hover:border-violet-200 hover:text-violet-700 rounded-lg text-slate-600 shadow-xs cursor-pointer hover:scale-105 transition-all" title="Copy Share Link">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/></svg>
    </button>
  );
})()}

{perms.canShareInvoice && <button
  onClick={async () => {
    const paySym = sym(b.currency);
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

    // ⚡ Calculate remaining balance if partially paid
    const alreadyPaid = parseFloat(String(b.amountPaid || 0)) || 0;
    const remaining = Math.max(0, b.amount - alreadyPaid);
    const isPartial = alreadyPaid > 0 && remaining > 0;
    const chargeAmt = isPartial ? remaining : b.amount;

    let payUrl = `${window.location.origin}/pay/bill/${b.id}`;
    let qrImageUrl: string | null = null;
    let qrImageContent: string | null = null;
    try {
      const gwRes = await fetch(`${BASE}/payments/bill/${b.id}`);
      if (gwRes.ok) {
        const gwData = await gwRes.json();
        if (gwData.payment_link) payUrl = gwData.payment_link;
        qrImageUrl = gwData.qr_image_url || null;
        qrImageContent = gwData.qr_image_content || null;
      }
    } catch (e) {
      console.warn('Gateway link fetch failed, falling back to placeholder link:', e);
    }

    const defaultPhone = (b as any).clientPhone || '';
    const promptHeader = isPartial
      ? `Send link for REMAINING BALANCE: ${paySym}${chargeAmt.toFixed(2)} (Total ${paySym}${b.amount.toFixed(2)}, Paid ${paySym}${alreadyPaid.toFixed(2)})`
      : `Send payment link for Invoice #${b.invoiceNumber} (${paySym}${chargeAmt.toFixed(2)})`;

    const inputPhone = window.prompt(
      `${promptHeader}\n\nEnter WhatsApp number (or leave as is):`,
      defaultPhone
    );
    if (inputPhone === null) return;

    const inputEmail = window.prompt(
      `Enter email to send payment link (or leave blank to skip):`,
      b.clientEmail || ''
    );
    if (inputEmail === null) return;

    const activeCompany = companyProfile.name || 'Our Company';
    const message = isPartial
      ? `💳 Payment Request (Remaining Balance)\n\nHi ${b.clientName},\nInvoice #${b.invoiceNumber}\nTotal Amount: ${paySym}${b.amount.toFixed(2)}\nAlready Paid: ${paySym}${alreadyPaid.toFixed(2)}\nBalance Due: ${paySym}${chargeAmt.toFixed(2)}\n\nClick to pay balance:\n${payUrl}\n\n— ${activeCompany}`
      : `💳 Payment Request\n\nHi ${b.clientName},\nInvoice #${b.invoiceNumber} — ${paySym}${chargeAmt.toFixed(2)}\n\nClick to pay:\n${payUrl}\n\n— ${activeCompany}`;

    if (inputEmail?.trim()) {
      const html = `<div style="font-family:sans-serif;padding:24px;max-width:500px;margin:auto;border:1px solid #e2e8f0;border-radius:12px">
        <h2 style="color:#7c3aed">${isPartial ? 'Remaining Balance Payment Request' : 'Payment Request'}</h2>
        <p>Hi <strong>${b.clientName}</strong>,</p>
        <p>Invoice <strong>#${b.invoiceNumber}</strong></p>
        ${isPartial ? `<p style="color:#64748b;margin:4px 0;">Total: ${paySym}${b.amount.toFixed(2)}<br/>Already Paid: <span style="color:#16a34a;">${paySym}${alreadyPaid.toFixed(2)}</span></p>` : ''}
        <p style="font-size:16px;margin:10px 0;"><strong>${isPartial ? 'Remaining Balance Due:' : 'Amount Due:'} <span style="color:#7c3aed;">${paySym}${chargeAmt.toFixed(2)}</span></strong></p>
        <a href="${payUrl}" style="display:inline-block;margin-top:16px;padding:12px 28px;background:#7c3aed;color:#fff;border-radius:8px;text-decoration:none;font-weight:700">Pay ${paySym}${chargeAmt.toFixed(2)} Now</a>
        <p style="margin-top:20px;font-size:12px;color:#94a3b8">— ${activeCompany}</p>
      </div>`;
      fetch(`${BASE}/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ to: inputEmail.trim(), subject: `${isPartial ? 'Remaining Balance Due' : 'Payment Request'} — Invoice #${b.invoiceNumber}`, html, outlet_id: (b as any).outlet_id }),
      }).catch(() => {});
    }

    let waSent = false;
    if (inputPhone?.trim()) {
      const waPhone = resolveWaPhone(inputPhone, getSellerBuyerInfo(b).sellerCountry);
      if (waPhone.length >= 7) {
        try {
          if (qrImageUrl || qrImageContent) {
            const qrDataUrl = qrImageUrl
              ? await fetch(`${BASE}/payments/qr-image?url=${encodeURIComponent(qrImageUrl)}`, {
                  headers: { Authorization: `Bearer ${jwt}` },
                }).then(r => { if (!r.ok) throw new Error(`QR image fetch failed: ${r.status}`); return r.blob(); })
                  .then(blob => new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                  }))
              : `data:image/png;base64,${qrImageContent}`;
            const caption = `${message}`;
            const pureBase64 = qrDataUrl.replace(/^data:image\/\w+;base64,/, '');
            await fetch(`${BASE}/whatsapp/send-image`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
              body: JSON.stringify({ phone: waPhone, base64: pureBase64, caption }),
            });
          } else {
            await fetch(`${BASE}/whatsapp/send`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
              body: JSON.stringify({ phone: waPhone, message }),
            });
          }
          waSent = true;
        } catch (e) {
          console.warn('WhatsApp payment link send failed:', e);
        }
      }
    }

    navigator.clipboard.writeText(payUrl).catch(() => {});
    alert(`Payment link sent${inputEmail?.trim() ? ` to ${inputEmail}` : ''}${waSent ? ' & WhatsApp' : ''} and copied!`);
    onAddLog?.('PAYMENT_LINK_SENT', `Payment link sent for Invoice ${b.invoiceNumber} to ${inputPhone || 'N/A'} / ${inputEmail || 'N/A'}`);
  }}
  className="w-8 h-8 flex items-center justify-center border border-purple-200 hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700 rounded-lg text-slate-600 shadow-xs cursor-pointer hover:scale-105 transition-all text-sm"
  title="Send Payment Link"
>
  💳
</button>}
                     {perms.canExportPDF && <button onClick={async () => {
                        setDownloadingBillId(b.id);
                        try {
                        const isBilingualInvoice = (b as any).isBilingual === true && b.elements.some((el: any) =>
                          el.arabicContent || el.columns?.some((c: any) => c.arabicHeader)
                        );
                        const isThermalInvoice = b.pageSize === 'THERMAL_80MM';

                        // ── ALL THERMAL INVOICES (Bilingual & Standard) USE POSReceiptRenderer ──
                        if (isThermalInvoice) {
                          const storedLang = isBilingualInvoice
                            ? ((b as any).translate_target_lang ||
                               (b as any).translated_lang ||
                               (b as any).target_lang ||
                               b.elements.find((el: any) => el.translatedLang)?.translatedLang ||
                               'en')
                            : 'en';
                          let liveAmountWordsAr = '';
                          if (isBilingualInvoice) {
                            try {
                              const englishWords = amountToWords(b.amount, b.currency || 'USD');
                              const jwt = localStorage.getItem('cloudairambo_jwt') || '';
                              const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
                              const res = await fetch(`${BASE}/translate?text=${encodeURIComponent(englishWords)}&tl=${storedLang}`, {
                                headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
                              });
                              const data = await res.json();
                              const wordsTranslated = data.translated || '';
                              const wordsEchoed = !wordsTranslated || wordsTranslated.trim().toLowerCase() === englishWords.trim().toLowerCase();
                              liveAmountWordsAr = wordsEchoed
                                ? await transliterateName(englishWords, storedLang)
                                : wordsTranslated;
                            } catch (e) {
                              console.warn('Live amount-in-words translation failed:', e);
                            }
                          }

                          const pageWidth = 302;
                          const container = document.createElement('div');
                          container.style.cssText = `position:absolute;left:-9999px;top:-9999px;width:${pageWidth}px;background:#ffffff;`;
                          document.body.appendChild(container);
                          const { createRoot } = await import('react-dom/client');
                          const root = createRoot(container);
                          const tableEl = b.elements.find((el: any) => el.type === 'table');
                          const dlExtraColumns = ((tableEl as any)?.columns || []).filter((c: any) =>
                            !['description','qty','rate','total','sno','serial','index','arabicDescription','arabicName',
                              'taxRate','tax_rate','taxName','tax_name','b2bTaxRate','b2b_tax_rate','b2bTaxName','b2b_tax_name',
                              'taxInclusive','tax_inclusive'].includes(c.key)
                          );
                          const _dlValCacheKey = (v: string) => `cloudairambo_value_cache_${storedLang}_${v}`;
                          
                          // Pre-collect and batch translate all custom column values in 1 single call
                          if (isBilingualInvoice && dlExtraColumns.length > 0) {
                            const uniqueDlVals = new Set<string>();
                            (tableEl?.rows || []).forEach((r: any) => {
                              dlExtraColumns.forEach((col: any) => {
                                const raw = String(r.values[col.key] ?? '').trim();
                                if (raw && !/^[\d.,%\s]+$/.test(raw) && !localStorage.getItem(_dlValCacheKey(raw))) {
                                  uniqueDlVals.add(raw);
                                }
                              });
                            });
                            if (uniqueDlVals.size > 0) {
                              const dlList = Array.from(uniqueDlVals);
                              const dlBatch = await translateBatchBills(dlList, storedLang);
                              dlList.forEach((orig, idx) => {
                                const tr = dlBatch[idx] || orig;
                                localStorage.setItem(_dlValCacheKey(orig), tr);
                              });
                            }
                          }

                          const _getDlValueTranslated = async (value: string): Promise<string> => {
                            const clean = String(value ?? '').trim();
                            if (!isBilingualInvoice || !clean || /^[\d.,%\s]+$/.test(clean)) return '';
                            let cached = localStorage.getItem(_dlValCacheKey(clean));
                            if (!cached || cached.toLowerCase() === clean.toLowerCase()) {
                              if (/[a-zA-Z]/.test(clean)) {
                                cached = await transliterateName(clean, storedLang);
                                if (cached) localStorage.setItem(_dlValCacheKey(clean), cached);
                              }
                            }
                            return cached || clean;
                          };

                          const lineItems = await Promise.all((tableEl?.rows || []).map(async (r: any) => {
                            const customColAr = isBilingualInvoice
                              ? Object.fromEntries(
                                  await Promise.all(dlExtraColumns.map(async (col: any) => {
                                    const raw = r.values[col.key] ?? '';
                                    const ar = r.values[`arabic_${col.key}`] || r.values[`arabicContent_${col.key}`] || (await _getDlValueTranslated(String(raw)));
                                    return [col.key, ar];
                                  }))
                                )
                              : undefined;
                            return {
                              id: r.id,
                              description: r.values.description || '',
                              qty: parseFloat(r.values.qty) || 1,
                              rate: parseFloat(r.values.rate) || 0,
                              arabicName: r.values.arabicDescription || r.values.arabicName || '',
                              taxRate: r.values.taxRate ?? r.values.tax_rate ?? 0,
                              taxName: r.values.taxName ?? r.values.tax_name,
                              b2bTaxRate: r.values.b2bTaxRate ?? r.values.b2b_tax_rate,
                              b2bTaxName: r.values.b2bTaxName ?? r.values.b2b_tax_name,
                              taxInclusive: r.values.taxInclusive ?? r.values.tax_inclusive ?? false,
                              ...Object.fromEntries(dlExtraColumns.map((col: any) => [col.key, r.values[col.key] ?? ''])),
                              ...(customColAr ? { customColAr } : {}),
                            };
                          }));
                          const rowLabelsAr = (b as any).row_labels_ar || (b as any).rowLabelsAr;
                          const taxLinesAr = (b as any).tax_lines_ar || (b as any).taxLinesAr || [];
                          const discountLinesAr = (b as any).discount_lines_ar || (b as any).discountLinesAr || [];
                          const notesAr = (b as any).notes_ar || (b as any).notesAr;

                          let netSubtotalDl = 0;
                          const lineBasesDl: { base: number; taxRate: number }[] = [];
                          const isDlB2b = isInvoiceB2B(b);
                          (tableEl?.rows || []).forEach((r: any) => {
                            const qty = parseFloat(String(r.values.qty)) || 0;
                            const rate = parseFloat(String(r.values.rate)) || 0;
                            const lineTotal = qty * rate;
                            const rawB2b = r.values?.b2bTaxRate ?? r.values?.b2b_tax_rate;
                            const taxRate = isDlB2b && rawB2b !== undefined && rawB2b !== null
                              ? parseFloat(String(rawB2b)) || 0
                              : parseFloat(String(r.values?.taxRate ?? r.values?.tax_rate ?? 0)) || 0;
                            const inclusive = !!(r.values?.taxInclusive ?? r.values?.tax_inclusive);
                            const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
                            lineBasesDl.push({ base, taxRate });
                            netSubtotalDl += base;
                          });
                          const discTotalForTaxDl = ((tableEl as any)?.discounts || []).reduce(
                            (s: number, d: any) => s + netSubtotalDl * (d.rate / 100), 0
                          );
                          const discountRatioDl = netSubtotalDl > 0 ? Math.min(1, discTotalForTaxDl / netSubtotalDl) : 0;
                          const productTaxTotalDl = lineBasesDl.reduce(
                            (s, lb) => s + (lb.base * (1 - discountRatioDl)) * (lb.taxRate / 100), 0
                          );
                          const dlSellerBuyerInfo = getSellerBuyerInfo(b);
                          const dlTaxScheme = getDefaultSchemeForCountryAndRegion(dlSellerBuyerInfo.sellerCountry, dlSellerBuyerInfo.sellerRegion);
                          const dlCustomTaxScheme = (b as any).custom_tax_scheme || (b as any).customTaxScheme;
                          const savedBreakdownThermal = (tableEl as any)?.taxBreakdown || (b as any)?.tax_breakdown || (b as any)?.taxBreakdown;
                          const dlTaxBreakdownEntries = (Array.isArray(savedBreakdownThermal) && savedBreakdownThermal.length > 0)
                            ? savedBreakdownThermal
                            : computeNamedTaxBreakdown(
                                lineItems as any, (tableEl as any)?.discounts || [], dlTaxScheme, dlSellerBuyerInfo.sellerRegion, dlSellerBuyerInfo.buyerRegion, isDlB2b, dlCustomTaxScheme
                              );
                          const dlTaxBreakdownAr = (dlTaxBreakdownEntries && isBilingualInvoice)
                            ? Object.fromEntries(await Promise.all(
                                dlTaxBreakdownEntries.map(async e => [e.name, await transliterateName(e.name, storedLang)] as const)
                              ))
                            : {};

                          // Strip any legacy synthesized duplicate client blocks from elements
                          const cleanThermalElements = (b.elements || []).filter((el: any) =>
                            !el.id?.startsWith('synth-client-block') &&
                            !el.id?.startsWith('client-info-')
                          );

                          const pdfInc = (b as any).includeFields || (b as any).include_fields || (b.elements?.[0] as any)?.includeFields || (b.elements?.[0] as any)?.include_fields;

                          await new Promise<void>(resolve => {
                            root.render(React.createElement(POSReceiptRenderer, {
                              template: { ...b, elements: cleanThermalElements },
                              clientName: pdfInc?.name === false ? '' : b.clientName,
                              clientEmail: pdfInc?.email === false ? '' : (b.clientEmail || (b as any).client_email || ''),
                              clientPhone: pdfInc?.phone === false ? '' : ((b as any).clientPhone || (b as any).client_phone || ''),
                              clientAddress: pdfInc?.address === false ? '' : ((b as any).clientAddress || (b as any).client_address || ''),
                              buyerTaxId: pdfInc?.taxId === false ? undefined : (getInvoiceTaxId(b) || undefined),
                              isB2B: isDlB2b,
                              invoiceDate: parseUtcDate(b.createdAt).toLocaleDateString(),
                              invoiceTime: '',
                              notes: (b as any).notes || '',
                              lineItems,
                              extraColumns: dlExtraColumns,
                              taxLines: (tableEl as any)?.taxes || [],
                              discountLines: (tableEl as any)?.discounts || [],
                              sym: sym(b.currency),
                              shippingCost: (tableEl as any)?.shippingCost || 0,
                              invoiceStatus: b.status,
                              translateTargetLang: storedLang,
                              isBilingual: isBilingualInvoice,
                              isThermal: true,
                              showTokenQRPlaceholder: false,
                              isEditPreview: false,
                              rowLabelsAr,
                              taxLinesAr,
                              discountLinesAr,
                              notesAr,
                              productTaxTotal: productTaxTotalDl,
                              netSubtotal: netSubtotalDl,
                              taxBreakdown: dlTaxBreakdownEntries,
                              taxBreakdownAr: dlTaxBreakdownAr,
                              showAmountInWords: true,
                              amountInWords: amountToWords(b.amount, b.currency || 'USD'),
                              amountInWordsTranslated: liveAmountWordsAr,
                              dynamicValues: {
                                invoice_number: b.invoiceNumber,
                                ...((b as any).dynamicValues || (b as any).dynamic_values || {}),
                              },
                              dynamicValuesAr: {
                                ...((b as any).dynamicValuesAr || (b as any).dynamic_values_ar || {}),
                              },
                              dynamicFieldLabelsAr:
                                (b as any).dynamicFieldLabelsAr || (b as any).dynamic_field_labels_ar || {},
                            }));
                            setTimeout(resolve, 800);
                          });

                          const inlineStyles = Array.from(document.querySelectorAll('style'))
                            .map(el => el.outerHTML)
                            .join('\n');
                          const html = `<!DOCTYPE html><html><head><meta charset="utf-8">${inlineStyles}<style>
*{margin:0;padding:0;box-sizing:border-box;}
body{
  background:#fff;
  font-family:system-ui,sans-serif;
  width:${pageWidth}px;
  overflow:hidden;
}
</style></head><body>${container.innerHTML}</body></html>`;

                          const renderedElement = container.firstElementChild as HTMLElement | null;
                          const renderedHeight = renderedElement
                            ? Math.ceil(Math.max(renderedElement.scrollHeight, renderedElement.getBoundingClientRect().height))
                            : 800;
                          const pdfHeight = Math.max(renderedHeight + 15, 200);

                          root.unmount();
                          document.body.removeChild(container);

                          const jwt = localStorage.getItem('cloudairambo_jwt') || '';
                          const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

                          try {
                            const { pdfApi } = await import('../utils/api');
                            const blob = await pdfApi.generate(html, pageWidth, pdfHeight);
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${b.invoiceNumber}_${(b.clientName || 'Receipt').replace(/\s+/g, '_')}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                          } catch (err) {
                            // Fallback to backend /invoices/html-to-pdf
                            const res = await fetch(`${BASE}/invoices/html-to-pdf`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
                              body: JSON.stringify({ html, pageWidth, pageHeight: pdfHeight }),
                            });
                            const data = await res.json();
                            const base64 = data.base64 || data.pdf_base64 || data.data;
                            const link = document.createElement('a');
                            link.href = `data:application/pdf;base64,${base64}`;
                            link.download = `${b.invoiceNumber}_${(b.clientName || 'Receipt').replace(/\s+/g, '_')}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        } else {
                          const tableEl = (b.elements || []).find((el: any) => el.type === 'table');
                          const isDlB2b = isInvoiceB2B(b);
                          const dlSellerBuyerInfo = getSellerBuyerInfo(b);
                          const dlTaxScheme = getDefaultSchemeForCountryAndRegion(dlSellerBuyerInfo.sellerCountry, dlSellerBuyerInfo.sellerRegion);
                          const dlCustomTaxScheme = (b as any).custom_tax_scheme || (b as any).customTaxScheme;
                          const buyerTaxId = getInvoiceTaxId(b);

                          const savedBreakdown = (tableEl as any)?.taxBreakdown || (b as any)?.tax_breakdown || (b as any)?.taxBreakdown;

                          const dlTaxBreakdownEntries = (Array.isArray(savedBreakdown) && savedBreakdown.length > 0)
                            ? savedBreakdown
                            : computeNamedTaxBreakdown(
                                (tableEl?.rows || []).map((r: any) => r.values),
                                (tableEl as any)?.discounts || [],
                                dlTaxScheme,
                                dlSellerBuyerInfo.sellerRegion,
                                dlSellerBuyerInfo.buyerRegion,
                                isDlB2b,
                                dlCustomTaxScheme
                              );

                          // Embed B2B Tax breakdown into A4 table element before sending to PDF generator
                          const enrichedElements = (b.elements || []).map((el: any) => {
                            if (el.type === 'table') {
                              return {
                                ...el,
                                taxBreakdown: dlTaxBreakdownEntries || el.taxBreakdown,
                                buyer_tax_id: buyerTaxId || el.buyer_tax_id,
                                buyerTaxId: buyerTaxId || el.buyerTaxId,
                              };
                            }
                            return el;
                          });

                          const deduped = dedupeInvoiceElements(enrichedElements);
                          const qrPos = getBillQrPosition(b);
                          onDownloadPDF({
                            ...b,
                            isB2B: isDlB2b,
                            is_b2b: isDlB2b,
                            buyer_tax_id: buyerTaxId || null,
                            buyerTaxId: buyerTaxId || null,
                            clientGSTIN: buyerTaxId || '',
                            qr_position: qrPos,
                            autoQrPosition: qrPos,
                            elements: deduped,
                          } as InvoiceRecord);
                        }
                        } finally {
                          setDownloadingBillId(null);
                        }
                      }}
                        disabled={downloadingBillId === b.id}
                        className="w-8 h-8 flex items-center justify-center border border-slate-200 hover:bg-blue-50 hover:border-blue-200 hover:text-purple-700 rounded-lg text-slate-600 shadow-xs cursor-pointer hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100" title={downloadingBillId === b.id ? 'Generating...' : 'Download PDF'}>
                        {downloadingBillId === b.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      </button>}

                      {perms.canExportPDF && onPrintPDF && (
                        <button onClick={() => handlePrintBill(b)} disabled={printingBillId === b.id}
                          className="w-8 h-8 flex items-center justify-center border border-slate-200 hover:bg-slate-100 hover:border-slate-300 hover:text-slate-900 rounded-lg text-slate-600 shadow-xs cursor-pointer hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm" title="Print">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H7v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H7a2 2 0 00-2 2v4h14z"/></svg>
                        </button>
                      )}

                      {perms.canDeleteInvoice && (
                        <button onClick={() => { if (window.confirm("Delete this invoice?")) onDeleteBill(b.id); }}
                          className="w-8 h-8 flex items-center justify-center border border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-650 rounded-lg text-slate-600 shadow-xs cursor-pointer hover:scale-105 transition-all" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
              {filteredBills.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-slate-450">
                  <div className="space-y-1"><p className="font-bold">No Invoices Found</p><p className="text-[11px]">Go to the Invoice Creator tab to create invoices.</p></div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── LOAD MORE BUTTON ── */}
      {hasMoreBills && (
        <div className="flex justify-center pt-2 pb-6">
          <button
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="px-6 py-2.5 bg-white hover:bg-slate-50 border border-slate-300 hover:border-slate-400 text-slate-700 font-bold text-xs uppercase tracking-wider rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {isLoadingMore ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                <span>Loading next 50 invoices...</span>
              </>
            ) : (
              <span>⬇️ Load More Invoices ({bills.length} loaded)</span>
            )}
          </button>
        </div>
      )}

{/* ── EDIT MODAL ── */}
<AnimatePresence>
  {editingInvoice && (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-hidden" style={{ height: '100vh', width: '100vw' }}>
    <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }}
  style={{ width: '100%', height: '100vh', minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f1f5f9', overflow: 'hidden' }}>

        {/* Header bar */}
        <div style={{ padding: '14px 20px', background: 'linear-gradient(135deg,#3b82f6,#9333ea)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontWeight: 800, fontSize: 15, color: '#fff', letterSpacing: '0.02em' }}>✏️ Edit Invoice — {editingInvoice.invoiceNumber}</span>
          <button onClick={() => setEditingInvoice(null)} style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 10, width: 36, height: 36, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={18} />
          </button>
        </div>

    {/* Body: form left, preview right */}
<div className="edit-modal-body" style={{ flex: 1, overflow: 'hidden', display: 'flex', minHeight: 0, background: '#f1f5f9' }}>

          {/* ── LEFT FORM PANEL ── */}
          <div ref={editLeftRef} className="edit-modal-left" style={{ flexShrink: 0, overflowY: 'auto', background: '#fff', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>

              {/* Invoice number — locked */}
              <div>
                <label style={qi.lbl}>Invoice / Receipt No</label>
                <input style={{ ...qi.inp, fontFamily: 'monospace', fontWeight: 700, color: '#3b82f6', background: '#f8fafc', cursor: 'not-allowed' }}
                  value={editingInvoice.invoiceNumber?.startsWith('PENDING-') ? 'Not yet assigned' : editingInvoice.invoiceNumber} readOnly />
                <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 3 }}>
                  {editingInvoice.invoiceNumber?.startsWith('PENDING-')
                    ? 'Real number is assigned once payment is confirmed'
                    : 'Locked — assigned on creation'}
                </div>
              </div>

              {/* 👤 Created By Staff / Cashier — Locked & Read-only */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <label style={{ ...qi.lbl, marginBottom: 0 }}>Created By / Staff</label>
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#059669', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '1px 6px', borderRadius: 4 }}>
                    🔒 Locked Record
                  </span>
                </div>
                <div style={{
                  ...qi.inp,
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#1e293b',
                  background: '#f8fafc',
                  border: '1.5px solid #cbd5e1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'not-allowed',
                  userSelect: 'none',
                }}>
                  <span>👤 {
                    (editingInvoice as any)?.staff_name ||
                    (editingInvoice as any)?.cashier_name ||
                    (editingInvoice as any)?.cashierName ||
                    (editingInvoice as any)?.created_by_name ||
                    (editingInvoice as any)?.dynamicValues?.cashier ||
                    (editingInvoice as any)?.dynamic_values?.cashier ||
                    'Staff'
                  }</span>
                  <span style={{ fontSize: 10, color: '#64748b' }}>Read-only</span>
                </div>
                <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 3 }}>
                  Saved on {parseUtcDate(editingInvoice.createdAt).toLocaleString()}
                </div>
              </div>

              {/* Status */}
              <div>
                <label style={qi.lbl}>Status</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(['draft','unpaid','paid','overdue','cancelled'] as const).map(s => {
                    const isPosDiscard = editingInvoice?.pageSize === 'THERMAL_80MM'
                      && editingInvoice?.status?.toLowerCase() === 'cancelled';
                    const isLocked = isPosDiscard
                      ? s !== 'cancelled'
                      : editingInvoice?.status?.toLowerCase() === 'paid'
                      && s !== 'paid'
                      && !['owner', 'admin', 'manager'].includes(userRole?.toLowerCase() || '');
                    const color = s === 'paid' ? '#10b981' : s === 'overdue' ? '#ef4444' : s === 'cancelled' ? '#6b7280' : s === 'unpaid' ? '#f59e0b' : '#3b82f6';
                    const isActive = editStatus.toLowerCase() === s;
                    return (
                      <button
                        key={s}
                        onClick={() => !isLocked && setEditStatus(s)}
                        disabled={isLocked}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '6px 12px', borderRadius: 8, border: '1px solid',
                          fontSize: 11, fontWeight: 700, cursor: isLocked ? 'not-allowed' : 'pointer',
                          textTransform: 'uppercase', opacity: isLocked ? 0.4 : 1,
                          background: isActive ? color : '#fff',
                          color: isActive ? '#fff' : '#6b7280',
                          borderColor: isActive ? 'transparent' : '#e5e7eb',
                          boxShadow: isActive ? `0 2px 8px ${color}55` : 'none',
                          transition: 'all 0.14s ease',
                        }}
                        onMouseEnter={e => { if (!isActive && !isLocked) { e.currentTarget.style.borderColor = color; e.currentTarget.style.color = color; } }}
                        onMouseLeave={e => { if (!isActive && !isLocked) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; } }}
                      >
                        {isActive && <Check className="w-3 h-3" />}
                        {s === 'cancelled' ? 'Discarded' : s}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Payment Mode */}
              <div>
                <label style={qi.lbl}>Payment Mode</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['cash','card','upi'] as const).map(pm => {
                    const color = pm === 'cash' ? '#16a34a' : pm === 'card' ? '#2563eb' : '#7c3aed';
                    const isActive = editPaymentMode === pm;
                    return (
                      <button
                        key={pm}
                        onClick={() => setEditPaymentMode(pm)}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                          padding: '6px 12px', borderRadius: 8, border: '1px solid',
                          fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          textTransform: 'uppercase',
                          background: isActive ? color : '#fff',
                          color: isActive ? '#fff' : '#6b7280',
                          borderColor: isActive ? 'transparent' : '#e5e7eb',
                          boxShadow: isActive ? `0 2px 8px ${color}55` : 'none',
                          transition: 'all 0.14s ease',
                        }}
                        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = color; e.currentTarget.style.color = color; } }}
                        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; } }}
                      >
                        {isActive && <Check className="w-3 h-3" />}
                        {pm}
                      </button>
                    );
                  })}
                </div>
              </div>

           
              {/* Client with Tick/Untick Checkboxes */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <label style={{ ...qi.lbl, marginBottom: 0 }}>Customer Info</label>
                  {/* On already-issued bills, customer reassignment to Walk-in is permanently locked */}
                  <span style={{ fontsize: 9.5, fontweight: 800, color: '#64748b', texttransform: 'uppercase', letterspacing: '0.04em' }}>
                    🔒 recipient locked
                  </span>
                </div>

                {/* ── Checkbox Pills (Name, Phone, Email, Address, Tax ID) ── */}
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
                  {[
                    { key: 'name' as const, label: 'Name' },
                    { key: 'phone' as const, label: 'Phone' },
                    { key: 'email' as const, label: 'Email' },
                    { key: 'address' as const, label: 'Address' },
                    { key: 'taxId' as const, label: 'Tax ID' },
                  ].map(({ key, label }) => (
                    <label
                      key={key}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 10, fontWeight: 700, cursor: 'pointer',
                        padding: '3px 7px', borderRadius: 6, userSelect: 'none',
                        background: editIncludeFields[key] ? '#eff6ff' : '#f8fafc',
                        color: editIncludeFields[key] ? '#2563eb' : '#94a3b8',
                        border: `1px solid ${editIncludeFields[key] ? '#bfdbfe' : '#e2e8f0'}`,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={editIncludeFields[key]}
                        onChange={e => setEditIncludeFields(prev => ({ ...prev, [key]: e.target.checked }))}
                        style={{ accentColor: '#2563eb', cursor: 'pointer', margin: 0, width: 12, height: 12 }}
                      />
                      {label}
                    </label>
                  ))}
                </div>

                {/* 🔒 Locked Customer Name (Anti-Forgery Protection) */}
                <div style={{ opacity: editIncludeFields.name ? 1 : 0.45 }}>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                      style={{
                        ...qi.inp,
                        background: '#f8fafc',
                        color: '#334155',
                        fontWeight: 700,
                        cursor: 'not-allowed',
                        paddingRight: 90,
                      }}
                      value={editClientName || 'Walk-in Customer'}
                      readOnly
                      title="Customer name is locked to prevent invoice tampering and fraud"
                    />
                    <span style={{
                      position: 'absolute', right: 8,
                      fontSize: 9, fontWeight: 800, color: '#059669',
                      background: '#ecfdf5', border: '1px solid #a7f3d0',
                      padding: '2px 6px', borderRadius: 4, userSelect: 'none',
                    }}>
                      🔒 Anti-Forgery
                    </span>
                  </div>
                  <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 3 }}>
                    Customer name is locked to prevent legal tampering. Contact details below can still be updated.
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
                  <div style={{ opacity: editIncludeFields.email ? 1 : 0.45 }}>
                    <input style={qi.inp} placeholder="Email" value={editClientEmail} onChange={e => setEditClientEmail(e.target.value)} />
                  </div>
                  <div style={{ opacity: editIncludeFields.phone ? 1 : 0.45 }}>
                    <input style={qi.inp} placeholder="Phone" value={editClientPhone} onChange={e => setEditClientPhone(e.target.value)} />
                  </div>
                </div>
                <div style={{ marginTop: 6, opacity: editIncludeFields.address ? 1 : 0.45 }}>
                  <input style={qi.inp} placeholder="Address" value={editClientAddress} onChange={e => {
                    setEditClientAddress(e.target.value);
                    scheduleEditTranslate(e.target.value, 'clientAddress');
                  }} />
                </div>

                {/* Tax ID / GSTIN / NPWP Input — Directly controls B2B vs B2C */}
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 5, opacity: editIncludeFields.taxId ? 1 : 0.45 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                    <label style={{ ...qi.lbl, marginBottom: 0 }}>Buyer {editTaxIdMeta.taxLabel || 'Tax ID / GSTIN'}</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => setShowTaxDisclaimerModal(true)}
                        style={{ background: 'none', border: 'none', color: '#3b82f6', fontSize: 10, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                      >
                        ℹ️ Legal Disclaimer
                      </button>
                      {isEditB2B ? (
                        <span style={{ fontSize: 9, fontWeight: 800, color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '1px 6px', borderRadius: 4 }}>
                          🏢 B2B ACTIVE
                        </span>
                      ) : (
                        <span style={{ fontSize: 9, fontWeight: 700, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '1px 6px', borderRadius: 4 }}>
                          👤 B2C RETAIL
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                      style={{ ...qi.inp, paddingRight: 80, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: 0.5 }}
                      placeholder={`Enter Buyer ${editTaxIdMeta.taxLabel || 'Tax ID'} (leaves B2C if empty)`}
                      maxLength={25}
                      value={editTaxId}
                      onChange={e => {
                        const clean = e.target.value.replace(/[\s\-\.]/g, '').toUpperCase();
                        setEditTaxId(clean);
                      }}
                    />
                    {editTaxIdMeta.currentLength > 0 && (
                      <div
                        onClick={() => setShowTaxDisclaimerModal(true)}
                        style={{ position: 'absolute', right: 10, cursor: 'pointer' }}
                        title="Click to view verification disclaimer"
                      >
                        {editTaxIdMeta.isComplete || editTaxIdMeta.currentLength >= editTaxIdMeta.expectedLength ? (
                          <span style={{
                            fontSize: 11, fontWeight: 800,
                            color: editTaxIdMeta.isValid ? '#16a34a' : '#ef4444',
                            display: 'flex', alignItems: 'center', gap: 2
                          }}>
                            {editTaxIdMeta.isValid ? '✓ Valid' : '✗ Invalid'} <span style={{ fontSize: 9, opacity: 0.7 }}>ℹ️</span>
                          </span>
                        ) : (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af' }}>
                            {editTaxIdMeta.currentLength}/{editTaxIdMeta.expectedLength}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Detected Country & State Banner */}
                  {editTaxIdMeta.currentLength >= 2 && (
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      backgroundColor: (editTaxIdMeta.isComplete && !editTaxIdMeta.isValid) ? '#fef2f2' : editTaxIdMeta.isValid ? '#f0fdf4' : '#eff6ff',
                      border: `1px solid ${(editTaxIdMeta.isComplete && !editTaxIdMeta.isValid) ? '#fecaca' : editTaxIdMeta.isValid ? '#bbf7d0' : '#bfdbfe'}`,
                      borderRadius: 7, padding: '4px 8px', gap: 6, fontSize: 11,
                    }}>
                      <span style={{ fontSize: 13 }}>{editTaxIdMeta.countryFlag}</span>
                      <span style={{ fontWeight: 700, color: '#1e293b', flex: 1 }}>
                        {editTaxIdMeta.countryName}
                        {editTaxIdMeta.detectedRegion ? ` · ${editTaxIdMeta.detectedRegion}` : ''}
                        <span style={{ color: '#64748b', fontWeight: 500 }}> ({editTaxIdMeta.taxLabel})</span>
                      </span>
                    </div>
                  )}

                  <div style={{ fontSize: 9, color: '#b91c1c', display: 'flex', alignItems: 'center', gap: 4, marginTop: -1, lineHeight: 1.4 }}>
                    <span>⚠️ <strong>Format check only:</strong> Always verify active business status on official government tax portals.</span>
                  </div>
                </div>
              </div>

          
              {/* Currency */}
              <div>
                <label style={qi.lbl}>Currency</label>
                <CurrencySelector value={editCurrency} onChange={setEditCurrency} />
              </div>

              {/* Line Items */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <label style={{ ...qi.lbl, marginBottom: 0 }}>Line Items</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => setEditLineItems(prev => [...prev, { id: `li-${Date.now()}`, description: '', arabicDescription: '', qty: 1, rate: 0 }])}
                      style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}
                    >
                      <Plus size={12} /> Add Row
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEditAddCol(s => !s)}
                      style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}
                    >
                      <Plus size={12} /> Add Col
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowScanner(true)}
                      style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#16a34a', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      📦 Scan
                    </button>
                  </div>
                </div>

                {/* ── Quick Invoice Column Selection & Creation Tray ── */}
                {showEditAddCol && (() => {
                  const usedKeys = new Set(editToggleableCols.map(c => c.key));
                  const availableOptions = catalogColumnOptions.filter(o => !usedKeys.has(o.key));

                  const addEditColumn = (label: string) => {
                    if (!label.trim()) return;
                    const match = catalogColumnOptions.find(o => o.label.toLowerCase() === label.trim().toLowerCase());
                    const key = match ? match.key : label.trim().toLowerCase().replace(/\s+/g, '_');
                    const header = match ? match.header : label.trim();

                    // Insert column and auto-fill values from catalog products
                    setEditLineItems(prev => prev.map(li => {
                      const p = catalogProducts.find((cp: any) =>
                        (li.barcode && cp.barcode === li.barcode) ||
                        (cp.name && cp.name.trim().toLowerCase() === (li.description || '').trim().toLowerCase())
                      );
                      const val = p ? getCatalogColValue(p, { key, header }) : ((li as any)[key] ?? '');
                      return { ...li, [key]: val };
                    }));

                    setEditShownCustomKeys(prev => prev.includes(key) ? prev : [...prev, key]);
                    setNewEditColName('');
                    setShowEditAddCol(false);
                  };

                  return (
                    <div style={{ background: '#f5f3ff', border: '1.5px solid #ddd6fe', borderRadius: 8, padding: '10px 12px', marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {availableOptions.length > 0 && (
                        <div>
                          <div style={{ fontSize: 9.5, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', marginBottom: 4 }}>
                            Catalog Columns (Pick to auto-fill from products)
                          </div>
                          <select
                            style={{ ...qi.inp, fontSize: 11, background: '#fff' }}
                            value=""
                            onChange={e => { if (e.target.value) addEditColumn(e.target.value); }}
                          >
                            <option value="">— Select a column —</option>
                            {availableOptions.map(o => <option key={o.key} value={o.label}>{o.label}</option>)}
                          </select>
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          style={{ ...qi.inp, fontSize: 11, flex: 1, background: '#fff' }}
                          placeholder={availableOptions.length > 0 ? 'Or type a custom column name...' : 'Column name (e.g. Shelf No)'}
                          value={newEditColName}
                          onChange={e => setNewEditColName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') addEditColumn(newEditColName); }}
                        />
                        <button
                          type="button"
                          onClick={() => addEditColumn(newEditColName)}
                          style={{ background: '#7c3aed', border: 'none', borderRadius: 6, padding: '6px 12px', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowEditAddCol(false)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16, lineHeight: 1, padding: '0 4px' }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* ── Auto-Detected Column Chips (matches POS [x] removal & Restore Tray) ── */}
                {editToggleableCols.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    {/* Restore Tray for Hidden Columns */}
                    {editHiddenColumns.length > 0 && (
                      <div style={{ background: '#ecfdf5', border: '1.5px solid #a7f3d0', borderRadius: 8, padding: '7px 10px', marginBottom: 8 }}>
                        <div style={{ fontSize: 9.5, fontWeight: 800, color: '#059669', textTransform: 'uppercase', marginBottom: 5 }}>
                          + Restore Removed Column (Click to show)
                        </div>
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {editToggleableCols.filter(c => editHiddenColumns.includes(c.key)).map(c => (
                            <button
                              key={c.key}
                              type="button"
                              onClick={() => setEditHiddenColumns(prev => prev.filter(k => k !== c.key))}
                              style={{
                                background: '#fff', border: '1px solid #86efac', borderRadius: 6,
                                padding: '3px 8px', color: '#16a34a', fontSize: 11, fontWeight: 700,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3
                              }}
                            >
                              + {c.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Active Column Chips with [x] */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {editToggleableCols.filter(c => !editHiddenColumns.includes(c.key)).map(c => (
                        <div
                          key={c.key}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            background: '#f1f5f9', border: '1px solid #cbd5e1',
                            borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700, color: '#334155'
                          }}
                        >
                          <span>{c.label}</span>
                          <button
                            type="button"
                            title={`Hide ${c.label} column`}
                            onClick={() => setEditHiddenColumns(prev => [...prev, c.key])}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, lineHeight: 1, padding: 0 }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                            onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 85px 28px', gap: 6, marginBottom: 4, padding: '0 2px' }}>
                  {['Description','Qty','Rate',''].map(h => <div key={h} style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</div>)}
                </div>

                {editLineItems.map((li: any, idx: number) => {
                  const isCatalogItem = catalogProducts.some((p: any) =>
                    (li.barcode && p.barcode === li.barcode) ||
                    (p.name && p.name.trim().toLowerCase() === (li.description || '').trim().toLowerCase())
                  );

                  return (
                    <div key={li.id || idx} style={{ marginBottom: 12, padding: 12, border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                      {isCatalogItem && (
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span>🔒 Catalog Item (Name & Price locked, edit Qty)</span>
                        </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 85px 28px', gap: 6, alignItems: 'center' }}>
                        <div className="edit-product-search-container" style={{ position: 'relative', width: '100%' }}>
                          <input
                            style={{
                              ...qi.inp, fontSize: 12.5, padding: '7px 10px', background: '#fff',
                              ...(isCatalogItem ? { color: '#334155', cursor: 'not-allowed' } : {})
                            }}
                            value={li.description}
                            readOnly={isCatalogItem}
                            onChange={e => {
                              if (isCatalogItem) return;
                              const val = e.target.value;
                              const isLast = idx === editLineItems.length - 1;
                              setEditLineItems(prev => {
                                const updated = prev.map((x, i) => i === idx ? { ...x, description: val } : x);
                                if (isLast && val.trim()) {
                                  return [...updated, { id: `li-${Date.now()}-${Math.random()}`, description: '', arabicDescription: '', qty: 1, rate: 0 }];
                                }
                                return updated;
                              });
                              scheduleEditTranslate(val, `item_${li.id}`);
                              setProductDropdownId(li.id || `li-${idx}`);
                            }}
                            onFocus={() => { if (!isCatalogItem) setProductDropdownId(li.id || `li-${idx}`); }}
                            onClick={() => { if (!isCatalogItem) setProductDropdownId(li.id || `li-${idx}`); }}
                            placeholder="Item Description or search catalog..."
                          />

                          {/* Product Search Catalog Dropdown */}
                          {productDropdownId === (li.id || `li-${idx}`) && !isCatalogItem && (() => {
                            const q = (li.description || '').toLowerCase().trim();
                            const matches = catalogProducts.filter(p =>
                              p.name?.toLowerCase().includes(q) || p.barcode?.toLowerCase().includes(q)
                            );
                            if (matches.length === 0) return null;
                            return (
                              <div
                                style={{
                                  position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
                                  border: '1px solid #e2e8f0', borderRadius: 8, zIndex: 300,
                                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 180, overflowY: 'auto',
                                }}
                              >
                                {matches.map(p => (
                                  <div
                                    key={p.id}
                                    onPointerDown={e => {
                                      e.preventDefault();
                                      const b2cRate = p.tax_rate ?? 0;
                                      const b2cName = p.tax_name || 'Tax';
                                      const b2bRate = (p.b2b_tax_rate !== undefined && p.b2b_tax_rate !== null && String(p.b2b_tax_rate).trim() !== '') ? Number(p.b2b_tax_rate) : b2cRate;
                                      const b2bName = p.b2b_tax_name || (p.b2b_tax_rate !== undefined ? `${b2cName} B2B` : b2cName);

                                      setEditLineItems(prev => prev.map((x, i) => i === idx ? {
                                        ...x,
                                        description: p.name,
                                        rate: p.rate,
                                        barcode: p.barcode,
                                        taxRate: isEditB2B ? b2bRate : b2cRate,
                                        tax_rate: isEditB2B ? b2bRate : b2cRate,
                                        taxName: isEditB2B ? b2bName : b2cName,
                                        tax_name: isEditB2B ? b2bName : b2cName,
                                        b2bTaxRate: b2bRate,
                                        b2b_tax_rate: b2bRate,
                                        b2bTaxName: b2bName,
                                        b2b_tax_name: b2bName,
                                        taxInclusive: Boolean(p.tax_inclusive),
                                        tax_inclusive: Boolean(p.tax_inclusive),
                                      } : x));
                                      scheduleEditTranslate(p.name, `item_${li.id}`);
                                      setProductDropdownId(null);
                                    }}
                                    style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 11, borderBottom: '1px solid #f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                  >
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#111827' }}>{p.name}</div>
                                      {p.unit && <div style={{ fontSize: 10, color: '#9ca3af' }}>{p.unit}</div>}
                                    </div>
                                    <div style={{ fontWeight: 700, color: '#3b82f6', fontSize: 12 }}>
                                      {sym(editCurrency)}{p.rate?.toFixed(2)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        <input
                          type="number"
                          style={{ ...qi.inp, fontSize: 12.5, padding: '7px 6px', textAlign: 'center', fontWeight: '700', background: '#fff' }}
                          value={li.qty}
                          min={1}
                          onChange={e => setEditLineItems(prev => prev.map((x, i) => i === idx ? { ...x, qty: parseFloat(e.target.value) || 1 } : x))}
                        />
                        <input
                          type="number"
                          style={{
                            ...qi.inp, fontSize: 12.5, padding: '7px 8px', textAlign: 'right', fontWeight: '600', background: '#fff',
                            ...(isCatalogItem ? { color: '#334155', cursor: 'not-allowed' } : {})
                          }}
                          value={li.rate || ''}
                          readOnly={isCatalogItem}
                          onChange={e => {
                            if (isCatalogItem) return;
                            setEditLineItems(prev => prev.map((x, i) => i === idx ? { ...x, rate: parseFloat(e.target.value) || 0 } : x));
                          }}
                          placeholder="0.00"
                        />
                        <button
                          type="button"
                          onClick={() => editLineItems.length > 1 && setEditLineItems(prev => prev.filter((_, i) => i !== idx))}
                          style={{ background: 'none', border: 'none', cursor: editLineItems.length === 1 ? 'not-allowed' : 'pointer', color: editLineItems.length === 1 ? '#cbd5e1' : '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          disabled={editLineItems.length === 1}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>

                      {/* Custom Tax Editing per Item (Matches Quick Invoice) */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                        <div style={{ position: 'relative', width: 85 }}>
                          <input
                            type="number"
                            style={{ ...qi.inp, fontSize: 11, padding: '4px 20px 4px 6px', background: '#fff' }}
                            placeholder="Tax %"
                            min={0}
                            max={100}
                            value={li.taxRate ?? ''}
                            onChange={e => {
                              const r = parseFloat(e.target.value) || 0;
                              setEditLineItems(prev => prev.map((x, i) => i === idx ? {
                                ...x,
                                taxRate: r,
                                tax_rate: r,
                                b2bTaxRate: isEditB2B ? r : x.b2bTaxRate,
                                b2b_tax_rate: isEditB2B ? r : x.b2b_tax_rate,
                              } : x));
                            }}
                          />
                          <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#94a3af' }}>%</span>
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            const next = !(li.taxInclusive ?? li.tax_inclusive);
                            setEditLineItems(prev => prev.map((x, i) => i === idx ? {
                              ...x,
                              taxInclusive: next,
                              tax_inclusive: next,
                            } : x));
                          }}
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            borderRadius: 6,
                            border: '1px solid',
                            padding: '4px 8px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            background: (li.taxInclusive ?? li.tax_inclusive) ? '#ecfdf5' : '#fff',
                            color: (li.taxInclusive ?? li.tax_inclusive) ? '#059669' : '#64748b',
                            borderColor: (li.taxInclusive ?? li.tax_inclusive) ? '#a7f3d0' : '#cbd5e1',
                          }}
                        >
                          {(li.taxInclusive ?? li.tax_inclusive) ? '✓ Tax Inclusive' : 'Tax Exclusive'}
                        </button>
                      </div>

                      {/* Custom Table Columns (e.g. Batch No) - with clean slate label and white background */}
                      {editToggleableCols
                        .filter(col =>
                          !['qty', 'rate', 'total', 'amt', 'tax', 'tax_rate'].includes(col.key.toLowerCase()) &&
                          !col.key.toLowerCase().startsWith('cat_tax') &&
                          !editHiddenColumns.includes(col.key)
                        )
                        .map(col => {
                          const val = (li as any)[col.key] ?? '';
                          return (
                            <div key={col.key} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 28px', gap: 6, marginTop: 6, alignItems: 'center' }}>
                              <div style={{ fontSize: 10.5, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                {col.label}
                              </div>
                              <input
                                style={{ ...qi.inp, fontSize: 12, padding: '5px 9px', background: '#fff' }}
                                placeholder={col.label}
                                value={String(val ?? '')}
                                onChange={e => setEditLineItems(prev => prev.map((x, i) => i === idx ? { ...x, [col.key]: e.target.value } : x))}
                              />
                              <button
                                type="button"
                                onClick={() => setEditHiddenColumns(prev => [...prev, col.key])}
                                title={`Hide ${col.label} column`}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                                onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          );
                        })
                      }
                    </div>
                  );
                })}
              </div>

              {/* Tax Lines */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <label style={{ ...qi.lbl, marginBottom: 0 }}>Tax Lines</label>
                  <button onClick={() => setEditTaxLines(prev => [...prev, { label: 'Tax', rate: 0 }])}
                    style={{ fontSize: 10, fontWeight: 600, color: '#10b981', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}>+ Add Tax</button>
                </div>
                {editTaxLines.map((t, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 22px', gap: 4, marginBottom: 4 }}>
                    <input style={{ ...qi.inp, fontSize: 11 }} value={t.label}
                      onChange={e => setEditTaxLines(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                    <div style={{ position: 'relative' }}>
                      <input type="number" style={{ ...qi.inp, fontSize: 11, paddingRight: 18 }} value={t.rate || ''} min={0} max={100}
                        onChange={e => setEditTaxLines(prev => prev.map((x, j) => j === i ? { ...x, rate: parseFloat(e.target.value) || 0 } : x))} />
                      <span style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#9ca3af' }}>%</span>
                    </div>
                    <button onClick={() => editTaxLines.length > 1 && setEditTaxLines(prev => prev.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>

              {/* Discount Lines */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <label style={{ ...qi.lbl, marginBottom: 0 }}>Discount Lines</label>
                  <button onClick={() => setEditDiscountLines(prev => [...prev, { label: 'Discount', rate: 0 }])}
                    style={{ fontSize: 10, fontWeight: 600, color: '#f59e0b', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}>+ Add Discount</button>
                </div>
                {editDiscountLines.map((d, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 22px', gap: 4, marginBottom: 4 }}>
                    <input style={{ ...qi.inp, fontSize: 11 }} value={d.label}
                      onChange={e => setEditDiscountLines(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                    <div style={{ position: 'relative' }}>
                      <input type="number" style={{ ...qi.inp, fontSize: 11, paddingRight: 18 }} value={d.rate || ''} min={0} max={100}
                        onChange={e => setEditDiscountLines(prev => prev.map((x, j) => j === i ? { ...x, rate: parseFloat(e.target.value) || 0 } : x))} />
                      <span style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#9ca3af' }}>%</span>
                    </div>
                    <button onClick={() => editDiscountLines.length > 1 && setEditDiscountLines(prev => prev.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>

             {/* Shipping Cost */}
              <div>
                <label style={qi.lbl}>Shipping Cost</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#9ca3af' }}>{sym(editCurrency)}</span>
                  <input
                    type="number"
                    style={{ ...qi.inp, paddingLeft: 22 }}
                    placeholder="0.00"
                    value={editShipping || ''}
                    min={0}
                    onChange={e => setEditShipping(parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>

              {/* Totals summary */}
<div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
  <div style={{ ...qi.row, marginBottom: 4 }}><span style={{ color: '#6b7280', fontSize: 12 }}>Subtotal</span><span style={{ fontSize: 12, fontWeight: 600 }}>{sym(editCurrency)}{editSubtotal.toFixed(2)}</span></div>
  {editProductTaxTotal > 0 && editTaxBreakdownEntries && editTaxBreakdownEntries.map(({ name, rate, amount }) => (
    <div key={name} style={{ ...qi.row, marginBottom: 3 }}>
      <span style={{ color: isEditB2B ? '#16a34a' : '#7c3aed', fontSize: 11 }}>{name} ({rate}%)</span>
      <span style={{ fontSize: 11, color: isEditB2B ? '#16a34a' : '#7c3aed' }}>+{sym(editCurrency)}{amount.toFixed(2)}</span>
    </div>
  ))}
  {editProductTaxTotal > 0 && !editTaxBreakdownEntries && (() => {
    const fallbackBaseName = (editCustomTaxScheme as any)?.name || editTaxScheme?.components?.[0]?.name || editTaxScheme?.label?.replace(/\s*\(.*?\)/, '') || 'Tax';
    const fallbackLabel = isEditB2B ? ((editCustomTaxScheme as any)?.b2b_name || fallbackBaseName) : fallbackBaseName;
    return (
      <div style={{ ...qi.row, marginBottom: 3 }}>
        <span style={{ color: isEditB2B ? '#16a34a' : '#7c3aed', fontSize: 11 }}>{fallbackLabel} ({editSubtotal ? ((editProductTaxTotal / editSubtotal) * 100).toFixed(1) : 0}%)</span>
        <span style={{ fontSize: 11, color: isEditB2B ? '#16a34a' : '#7c3aed' }}>+{sym(editCurrency)}{editProductTaxTotal.toFixed(2)}</span>
      </div>
    );
  })()}
  {editTaxLines.filter(t => t.rate > 0).map((t, i) => (
    <div key={i} style={{ ...qi.row, marginBottom: 3 }}><span style={{ color: '#6b7280', fontSize: 11 }}>{t.label} ({t.rate}%)</span><span style={{ fontSize: 11 }}>+{sym(editCurrency)}{(editTaxableValue * t.rate / 100).toFixed(2)}</span></div>
  ))}
  {editDiscountLines.filter(d => d.rate > 0).map((d, i) => (
    <div key={i} style={{ ...qi.row, marginBottom: 3 }}><span style={{ color: '#6b7280', fontSize: 11 }}>{d.label} ({d.rate}%)</span><span style={{ fontSize: 11, color: '#ef4444' }}>-{sym(editCurrency)}{(editSubtotal * d.rate / 100).toFixed(2)}</span></div>
  ))}
  {editShipping > 0 && (
    <div style={{ ...qi.row, marginBottom: 3 }}>
      <span style={{ color: '#6b7280', fontSize: 11 }}>Shipping</span>
      <span style={{ fontSize: 11 }}>+{sym(editCurrency)}{editShipping.toFixed(2)}</span>
    </div>
  )}
  <div style={{ ...qi.row, borderTop: '1px solid #e5e7eb', paddingTop: 6, marginTop: 4 }}>
    <span style={{ fontWeight: 700, fontSize: 13 }}>Total</span>
    <span style={{ fontWeight: 800, fontSize: 15, color: '#3b82f6' }}>{sym(editCurrency)}{editTotal.toFixed(2)}</span>
  </div>
</div>

              {/* Amount Paid with 1-Tap Quick Cash & Clean Balance / Pay Back Indicators */}
              {(() => {
                const paidVal = parseFloat(String(editAmountPaid)) || 0;
                const diff = editTotal - paidVal; // Positive = Customer owes us, Negative = Pay back to customer

                return (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <label style={{ ...qi.lbl, marginBottom: 0 }}>Amount Paid</label>

                      {/* Header Status Pill */}
                      {paidVal > 0 && (
                        diff > 0.005 ? (
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', padding: '2px 8px', borderRadius: 5 }}>
                            ⚠️ Due: {sym(editCurrency)}{diff.toFixed(2)}
                          </span>
                        ) : diff < -0.005 ? (
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#059669', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '2px 8px', borderRadius: 5 }}>
                            💵 Pay Back: {sym(editCurrency)}{Math.abs(diff).toFixed(2)}
                          </span>
                        ) : (
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '2px 8px', borderRadius: 5 }}>
                            ✓ Paid in Full
                          </span>
                        )
                      )}
                    </div>

                    {/* 1-Tap Quick Cash Pills */}
                    <div style={{ display: 'flex', gap: 6, marginBottom: 8, marginTop: 4 }}>
                      {[
                        { label: 'Exact', val: editTotal },
                        { label: '+10', val: Math.ceil((editTotal + 1) / 10) * 10 },
                        { label: '+50', val: Math.ceil((editTotal + 1) / 50) * 50 },
                        { label: '+100', val: Math.ceil((editTotal + 1) / 100) * 100 },
                      ].map((cash, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setEditAmountPaid(Number(cash.val.toFixed(2)))}
                          style={{
                            flex: 1,
                            padding: '6px 4px',
                            borderRadius: 7,
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: 'pointer',
                            background: Math.abs(paidVal - cash.val) < 0.01 ? '#dbeafe' : '#f8fafc',
                            border: `1px solid ${Math.abs(paidVal - cash.val) < 0.01 ? '#3b82f6' : '#cbd5e1'}`,
                            color: '#1e293b',
                            transition: 'all 0.1s ease',
                          }}
                        >
                          {cash.label === 'Exact' ? 'Exact' : `${cash.val}`}
                        </button>
                      ))}
                    </div>

                    <input
                      type="number"
                      style={qi.inp}
                      value={editAmountPaid || ''}
                      min={0}
                      onChange={e => setEditAmountPaid(parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                    />

                    {/* Bottom Breakdown Banner */}
                    {diff > 0.005 ? (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, padding: '7px 10px', backgroundColor: '#fef2f2', borderRadius: 8, border: '1px solid #fee2e2' }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#991b1b' }}>
                          🔴 Balance Due (Customer Owes):
                        </span>
                        <span style={{ fontSize: 12.5, fontWeight: 900, color: '#dc2626', fontFamily: 'monospace' }}>
                          {sym(editCurrency)}{diff.toFixed(2)}
                        </span>
                      </div>
                    ) : diff < -0.005 ? (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, padding: '7px 10px', backgroundColor: '#ecfdf5', borderRadius: 8, border: '1px solid #d1fae5' }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#065f46' }}>
                          🟢 Change Due (Pay Back to Customer):
                        </span>
                        <span style={{ fontSize: 12.5, fontWeight: 900, color: '#059669', fontFamily: 'monospace' }}>
                          {sym(editCurrency)}{Math.abs(diff).toFixed(2)}
                        </span>
                      </div>
                    ) : paidVal > 0 ? (
                      <div style={{ marginTop: 6, padding: '6px 10px', backgroundColor: '#eff6ff', borderRadius: 8, border: '1px solid #dbeafe', textAlign: 'center' }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: '#1d4ed8' }}>
                          ✓ Paid in full with zero balance.
                        </span>
                      </div>
                    ) : null}
                  </div>
                );
              })()}

            </div>

            {/* Save button */}
            <div style={{ padding: '14px 18px', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
              <button onClick={handleSaveEdit} style={{
                width: '100%', padding: 12, borderRadius: 10, border: 'none',
                background: 'linear-gradient(to right,#3b82f6,#9333ea)',
                color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
                💾 Update Invoice
              </button>
            </div>
          </div>
{/* ── DRAG DIVIDER ── */}
          <div
            className="edit-modal-divider"
            onMouseDown={onEditDividerMouseDown}
            style={{
              width: 5,
              flexShrink: 0,
              background: '#e5e7eb',
              cursor: 'col-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#bfdbfe')}
            onMouseLeave={e => (e.currentTarget.style.background = '#e5e7eb')}
          >
            <div style={{
              width: 3,
              height: 32,
              borderRadius: 99,
              background: '#94a3b8',
              pointerEvents: 'none',
            }} />
          </div>
          {/* ── RIGHT PREVIEW PANEL ── */}
          <div className="edit-modal-preview" style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', background: '#f0f4f8', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 32px', minHeight: 0 }}>
            <div style={{ marginBottom: 16, width: '100%', maxWidth: 660, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>Live Preview</h2>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: '#9ca3af' }}>Updates as you edit</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 8px' }}>
                <button onClick={() => setEditZoom(z => Math.max(0.4, z - 0.1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#6b7280', padding: '0 2px' }}>−</button>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#374151', minWidth: 36, textAlign: 'center' }}>{Math.round(editZoom * 100)}%</span>
                <button onClick={() => setEditZoom(z => Math.min(2, z + 0.1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#6b7280', padding: '0 2px' }}>+</button>
                <button onClick={() => setEditZoom(1)} style={{ background: '#f3f4f6', border: 'none', cursor: 'pointer', fontSize: 10, color: '#6b7280', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>Reset</button>
              </div>
            </div>
            <div style={{ transform: `scale(${editZoom})`, transformOrigin: 'top center', transition: 'transform 0.15s ease', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'visible', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', minWidth: 620 }}>
  {(editingInvoice.pageSize || 'A4') === 'THERMAL_80MM' ? (
        <POSReceiptRenderer
      template={liveEditInvoice || editingInvoice}
      clientName={editIncludeFields.name ? editClientName : ''}
      clientNameAr={
        editIncludeFields.name ? (
          editTranslatedFields['clientName'] ||
          (editingInvoice as any)?.dynamicValuesAr?.client_name ||
          (editingInvoice as any)?.dynamic_values_ar?.client_name ||
          editClientName
        ) : ''
      }
      clientAddress={editIncludeFields.address ? editClientAddress : ''}
      clientAddressAr={
        editIncludeFields.address ? (
          editTranslatedFields['clientAddress'] ||
          (editingInvoice as any)?.dynamicValuesAr?.client_address ||
          (editingInvoice as any)?.dynamic_values_ar?.client_address ||
          editClientAddress
        ) : ''
      }
      clientLineAr={
        editTranslatedFields['clientAddress'] ||
        (editingInvoice as any)?.dynamicValuesAr?.client_address ||
        (editingInvoice as any)?.dynamic_values_ar?.client_address ||
        editClientAddress
      }
      clientEmail={editIncludeFields.email ? editClientEmail : ''}
      clientPhone={editIncludeFields.phone ? editClientPhone : ''}
      buyerTaxId={editIncludeFields.taxId ? (editTaxId.trim() || undefined) : undefined}
      dynamicFieldLabelsAr={{
        email: editTranslatedFields['emailLabel'],
        Email: editTranslatedFields['emailLabel'],
        phone: editTranslatedFields['phoneLabel'],
        Phone: editTranslatedFields['phoneLabel'],
        address: editTranslatedFields['addressLabel'],
        Address: editTranslatedFields['addressLabel'],
        tax_id: editTranslatedFields['taxIdLabel'],
        taxId: editTranslatedFields['taxIdLabel'],
        'Tax ID': editTranslatedFields['taxIdLabel'],
      }}
      dynamicValues={{
        invoice_number: editingInvoice.invoiceNumber,
        ...((editingInvoice as any).dynamicValues || {}),
        ...((editingInvoice as any).dynamic_values || {}),
      }}
      dynamicValuesAr={{
        client_name: editTranslatedFields['clientName'] || editClientName,
        client_address: editTranslatedFields['clientAddress'] || editClientAddress,
      }}
      isB2B={isEditB2B}
      invoiceDate={parseUtcDate(editingInvoice.createdAt).toLocaleDateString()}
      invoiceTime={editInvoiceTime}
      notes={editNotes}
      lineItems={editLineItems.map(li => {
        const customAr = Object.fromEntries(
          Object.entries(li)
            .filter(([k, v]) => isEditableItemKey(k) && typeof v === 'string' && (editTranslatedColVals[v.trim()] || (li as any).__customAr?.[k]))
            .map(([k, v]) => [k, editTranslatedColVals[(v as string).trim()] || (li as any).__customAr?.[k] || ''])
        );
        return {
          ...li,
          qty: parseFloat(String(li.qty)) || 1,
          rate: parseFloat(String(li.rate ?? (li as any).unit_price ?? 0)) || 0,
          arabicName: li.arabicDescription,
          __customAr: customAr,
          customColAr: customAr,
        };
      })}
      taxLines={editTaxLines.map(t => ({
        ...t,
        rate: parseFloat(String(t.rate ?? 0).replace('%', '')) || 0,
      }))}
      discountLines={editDiscountLines.map(d => ({
        ...d,
        rate: parseFloat(String(d.rate ?? 0).replace('%', '')) || 0,
      }))}
      sym={sym(editCurrency)}
      shippingCost={editShipping}
      productTaxTotal={editProductTaxTotal}
      netSubtotal={editSubtotal}
      invoiceStatus={editStatus}
      isThermal={true}
      showTokenQRPlaceholder={false}
      isEditPreview={true}
      isBilingual={editIsBilingual}
      translateTargetLang={editIsBilingual ? editTargetLang : 'en'}
      rowLabelsAr={(editingInvoice as any)?.row_labels_ar || (editingInvoice as any)?.rowLabelsAr}
      taxLinesAr={(editingInvoice as any)?.tax_lines_ar || (editingInvoice as any)?.taxLinesAr}
      discountLinesAr={(editingInvoice as any)?.discount_lines_ar || (editingInvoice as any)?.discountLinesAr}
      showAmountInWords={editShowAmountWords}

amountInWords={
  editShowAmountWords
    ? amountToWords(editTotal, editCurrency)
    : undefined
}

amountInWordsTranslated={
  editIsBilingual && editShowAmountWords
    ? editAmountWordsTranslated
    : undefined
}
      taxBreakdown={editTaxBreakdownEntries?.map(tb => ({
        ...tb,
        rate: parseFloat(String(tb.rate ?? 0).replace('%', '')) || 0,
        amount: parseFloat(String(tb.amount ?? 0)) || 0,
      }))}
      taxBreakdownAr={editTaxBreakdownAr}
    />
  ) : editIsBilingual ? (
    <BilingualInvoiceRenderer
      template={liveEditInvoice || editingInvoice}
      clientName={editIncludeFields.name ? editClientName : ''}
      clientNameAr={
        editIncludeFields.name ? (
          editTranslatedFields['clientName'] ||
          (editingInvoice as any)?.dynamicValuesAr?.client_name ||
          (editingInvoice as any)?.dynamic_values_ar?.client_name ||
          ''
        ) : ''
      }
      clientAddress={editIncludeFields.address ? editClientAddress : ''}
      clientAddressAr={
        editIncludeFields.address ? (
          editTranslatedFields['clientAddress'] ||
          (editingInvoice as any)?.dynamicValuesAr?.client_address ||
          (editingInvoice as any)?.dynamic_values_ar?.client_address ||
          ''
        ) : ''
      }
      clientEmail={editIncludeFields.email ? editClientEmail : ''}
      clientPhone={editIncludeFields.phone ? editClientPhone : ''}
      buyerTaxId={editIncludeFields.taxId ? (editTaxId.trim() || undefined) : undefined}
      isB2B={isEditB2B}
      taxBreakdown={editTaxBreakdownEntries}
      savedElements={(liveEditInvoice || editingInvoice)?.elements?.map((el: any) => ({
        ...el,
        arabicContent: el.arabicContent || el.translatedContent || '',
      }))}
      invoiceDate={parseUtcDate(editingInvoice.createdAt).toLocaleDateString()}
      invoiceTime={editInvoiceTime}
      notes={editNotes}
      notesTranslated={(editingInvoice as any)?.notes_ar || (editingInvoice as any)?.notesAr || ''}
      lineItems={editLineItems.map(li => {
        const customAr = Object.fromEntries(
          Object.entries(li)
            .filter(([k, v]) => isEditableItemKey(k) && typeof v === 'string' && (editTranslatedColVals[v.trim()] || (li as any).__customAr?.[k]))
            .map(([k, v]) => [k, editTranslatedColVals[(v as string).trim()] || (li as any).__customAr?.[k] || ''])
        );
        return {
          ...li,
          arabicName: li.arabicDescription,
          __customAr: customAr,
          customColAr: customAr,
        };
      })}
      taxLines={editTaxLines}
      discountLines={editDiscountLines}
      sym={sym(editCurrency)}
      shippingCost={editShipping}
      invoiceStatus={editStatus}
      translateTargetLang={editTargetLang}
      isBilingual={true}
      bilingualLayout="side"
      isThermal={false}
      showAmountInWords={editShowAmountWords}
      currencyCode={editCurrency}
      amountInWordsTranslated={editIsBilingual ? editAmountWordsTranslated : ''}
      showTrackingQR={editingInvoiceHasToken}
      autoQrPosition={getBillQrPosition(editingInvoice)}
      invoiceNumber={editingInvoice.invoiceNumber}
      dynamicValues={{
        invoice_number: editingInvoice.invoiceNumber,
        ...((editingInvoice as any).dynamicValues || {}),
        ...((editingInvoice as any).dynamic_values || {}),
      }}
      dynamicValuesAr={{
        ...((editingInvoice as any).dynamicValuesAr || {}),
        ...((editingInvoice as any).dynamic_values_ar || {}),
        client_name:
          editTranslatedFields['clientName'] ||
          (editingInvoice as any)?.dynamicValuesAr?.client_name ||
          (editingInvoice as any)?.dynamic_values_ar?.client_name ||
          '',
        client_address: (
          editTranslatedFields['clientAddress'] ||
          (editingInvoice as any)?.dynamicValuesAr?.client_address ||
          (editingInvoice as any)?.dynamic_values_ar?.client_address ||
          ''
        )
          .split(/[،,]/)
          .filter(part => !/(gstin|tax\s*id|vat|trn|pan|تاكس|غستين|ضريب)/i.test(part))
          .join('، ')
          .trim(),
      }}
      dynamicFieldLabelsAr={
        (editingInvoice as any).dynamicFieldLabelsAr ||
        (editingInvoice as any).dynamic_field_labels_ar ||
        {}
      }
      taxScheme={editTaxScheme}
      sellerRegion={editSellerBuyerInfo.sellerRegion}
      buyerRegion={editSellerBuyerInfo.buyerRegion}
      taxBreakdownAr={editTaxBreakdownAr}
    />
  ) : (() => {
    const currentInvoice = liveEditInvoice || editingInvoice;
    const elementsToRender = dedupeInvoiceElements(currentInvoice.elements || []);

    const allLiveQrs = elementsToRender.filter((el: any) => el.type === 'qr' || el.type === 'image');
    const invQr = allLiveQrs.find((q: any) => q.id?.startsWith('invoice-qr-'));
    const tokQr = allLiveQrs.find((q: any) => q.id?.startsWith('token-qr-'));

    if (invQr && tokQr) {
      tokQr.y = invQr.y;
    }

    const invLabel = elementsToRender.find((el: any) =>
      el.type === 'text' && el.id?.startsWith('invoice-label-')
    );
    const tokLabel = elementsToRender.find((el: any) =>
      el.type === 'text' && el.id?.startsWith('token-label-')
    );

    if (invQr && invLabel) {
      const qrH = invQr.height || 80;
      const qrW = invQr.width || 80;
      const lWidth = Math.max(invLabel.width || 0, qrW, 120);
      invLabel.y = (invQr.y || 0) + qrH + 6;
      invLabel.width = lWidth;
      invLabel.x = (invQr.x || 0) + Math.round((qrW - lWidth) / 2);
      invLabel.textAlign = 'center';
    }

    if (tokQr && tokLabel) {
      const qrH = tokQr.height || 80;
      const qrW = tokQr.width || 80;
      const lWidth = Math.max(tokLabel.width || 0, qrW, 180);
      tokLabel.y = (tokQr.y || 0) + qrH + 6;
      tokLabel.width = lWidth;
      tokLabel.x = (tokQr.x || 0) + Math.round((qrW - lWidth) / 2);
      tokLabel.textAlign = 'center';
    }

    const naturalBottom = elementsToRender.reduce(
      (max: number, el: any) => Math.max(max, (el.y || 0) + (el.height || 0)),
      0
    ) + 40;
    const pageConfig = PAGE_CONFIGS[editingInvoice.pageSize || 'A4'] || PAGE_CONFIGS['A4'];
    const computedHeight = Math.max(pageConfig.height, naturalBottom);

    return (
      <CanvasWorkspace
        pageConfig={{ ...pageConfig, name: editingInvoice.pageSize || 'A4' }}
        computedHeight={computedHeight}
        pageCount={1}
        pageBehavior="auto_expand"
        setPageBehavior={() => {}}
        zoom={1}
        setZoom={() => {}}
        showGridLines={false}
        elements={elementsToRender}
        selectedElementId={null}
        inlineEditingId={null}
        inlineEditText=""
        setInlineEditText={() => {}}
        lockedElementIds={[]}
        dragState={null}
        qrDataUrls={{}}
        isDrawingMode={false}
        isDrawingActive={false}
        currentPathPoints={[]}
        currentPenColor="#000000"
        currentPenWidth={2}
        bilingualMode={false}
        bilingualDirection="en-ar"
        translateTargetLang="en"
        activeCurrency={editCurrency}
        currencyDisplayMode={(localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol'|'code') || 'symbol'}
        computeTableTotals={(el: any) => {
          let subtotal = 0;
          const lineBases: { base: number; taxRate: number }[] = [];
          (el.rows || []).forEach((r: any) => {
            const qty = parseFloat(r.values.qty) || 0;
            const rate = parseFloat(r.values.rate) || 0;
            const lineTotal = qty * rate;
            const taxRate = parseFloat(r.values.taxRate ?? r.values.tax_rate ?? 0) || 0;
            const inclusive = !!(r.values.taxInclusive ?? r.values.tax_inclusive);
            const base = taxRate > 0 && inclusive ? lineTotal / (1 + taxRate / 100) : lineTotal;
            lineBases.push({ base, taxRate });
            subtotal += base;
          });
          const discount = (el.discounts || []).reduce((s: number, d: any) => s + subtotal * (d.rate/100), 0);
          const discountRatio = subtotal > 0 ? Math.min(1, discount / subtotal) : 0;
          const productTax = lineBases.reduce((s, lb) => s + (lb.base * (1 - discountRatio)) * (lb.taxRate / 100), 0);
          const taxableValue = subtotal - discount;
          const tax = (el.taxes || []).reduce((s: number, t: any) => s + taxableValue * (t.rate/100), 0);
          const shipping = el.shippingCost || 0;
          return { subtotal, tax, discount, shipping, productTax, taxBreakdown: editTaxBreakdownEntries, total: taxableValue + productTax + tax + shipping };
        }}
        formatSerialNumber={(s: any) => s ? `${s.prefix}${String(s.current).padStart(s.digits,'0')}${s.suffix}` : ''}
        canvasRef={editCanvasWorkspaceRef}
        handleMouseDown={() => {}}
        handleDeleteElement={() => {}}
        handleInlineEditStart={() => {}}
        handleInlineEditSave={() => {}}
        commitCurrentState={() => {}}
        updateElementProps={() => {}}
        handleCanvasMouseDown={() => {}}
        handleCanvasMouseMove={() => {}}
        handleCanvasMouseUp={() => {}}
        onBackgroundClick={() => {}}
      />
    );
  })()}
</div>
          </div>

        </div>
      </motion.div>
    </div>
  )}
</AnimatePresence>

      {showScanner && (
        <QuickScannerModal
          onAddItem={(desc, qty, rate, barcode, taxInfo) => {
            handleScanAdd(desc, qty, rate, barcode, taxInfo);
          }}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* EMAIL REMINDER MODAL */}
      <AnimatePresence>
        {sendingInvoice && (
          <div className="fixed inset-0 bg-slate-900/65 backdrop-blur-xs flex items-center justify-center z-50 p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white border border-slate-200 rounded-2xl max-w-2xl w-full shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
              <div className="bg-slate-900 text-white p-5 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 bg-white/10 rounded-lg text-emerald-400"><Mail className="w-5 h-5" /></div>
                  <div>
                    <h3 className="text-xs font-black uppercase tracking-wider">Mail Reminder Dispatch</h3>
                    <p className="text-[10px] text-slate-400 mt-0.5">Invoice #{sendingInvoice.invoiceNumber}</p>
                  </div>
                </div>
                <button onClick={() => setSendingInvoice(null)} className="p-1 text-slate-400 hover:text-white hover:bg-white/15 rounded-full cursor-pointer"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6 overflow-y-auto space-y-4 font-sans text-xs">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black tracking-wider text-slate-500">Recipient Email</label>
                    <input type="email" value={emailRecipient} onChange={e => setEmailRecipient(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="client@company.com" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] uppercase font-black tracking-wider text-slate-500">Urgency Level</label>
                    <div className="flex bg-slate-100 p-1 rounded-lg border border-slate-200 gap-1">
                      {(['friendly','standard','urgent'] as const).map(key => (
                        <button key={key} type="button" onClick={() => setEmailTemplateKey(key)}
                          className={`flex-1 py-1.5 rounded-md text-[10px] font-bold uppercase cursor-pointer ${emailTemplateKey === key ? (key === 'urgent' ? 'bg-rose-600 text-white' : key === 'standard' ? 'bg-amber-500 text-white' : 'bg-emerald-600 text-white') : 'text-slate-550 hover:text-slate-800'}`}>
                          {key}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-wider text-slate-500">Subject</label>
                  <input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-wider text-slate-500">Message Body</label>
                  <textarea
                    value={emailBody}
                    onChange={e => setEmailBody(e.target.value)}
                    rows={8}
                    className="w-full border border-slate-200 rounded-lg bg-slate-50 p-3 text-xs text-slate-700 leading-relaxed focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
                  />
                </div>

              </div>
              <div className="bg-slate-50 border-t border-slate-200 p-4 flex items-center justify-between shrink-0">
                <button type="button" onClick={() => setSendingInvoice(null)} className="px-4 py-2 font-black text-[10.5px] uppercase text-slate-500 hover:text-purple-700 hover:bg-blue-50 rounded-lg cursor-pointer">Close</button>
                <div className="flex items-center gap-2">
                  <button type="button" disabled={isSendingWhatsApp} onClick={handleSendWhatsAppReminder}
                    className={`px-3.5 py-2.5 rounded-lg text-xs font-black uppercase border-b-2 cursor-pointer flex items-center gap-1.5 ${isSendingWhatsApp ? 'bg-slate-200 text-slate-400 border-slate-300 cursor-not-allowed opacity-60' : 'bg-green-500 hover:bg-green-600 text-white border-green-700'}`}>
                    {isSendingWhatsApp ? <><RefreshCw className="w-4 h-4 animate-spin" /><span>Sending...</span></> : <><svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg><span>WhatsApp</span></>}
                  </button>
                  <button type="button" disabled={isSendingEmail} onClick={handleSendGmailReminder}
                    className={`px-5 py-2.5 rounded-lg text-xs font-black uppercase flex items-center gap-1.5 ${isSendingEmail ? 'bg-slate-200 text-slate-400 border-b-2 border-slate-300 cursor-not-allowed opacity-60' : 'bg-blue-600 hover:bg-blue-700 text-white border-b-2 border-purple-800 cursor-pointer shadow-md'}`}>
                    {isSendingEmail ? <><RefreshCw className="w-4 h-4 animate-spin" /><span>Sending...</span></> : <><Send className="w-4 h-4" /><span>Send Gmail</span></>}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* SMART RECONCILIATION TOASTS */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 max-w-sm w-full pointer-events-none">
        <AnimatePresence>
          {livePaymentMatches.map(({ invoice, transaction, matchType }) => {
            const key = `${invoice.id}_${transaction.id}`;
            return (
              <motion.div key={key} layout initial={{ opacity: 0, y: 50, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.85, x: 100 }}
                className="pointer-events-auto bg-slate-900 border border-slate-800 text-white rounded-xl p-4 shadow-xl flex flex-col gap-3 relative overflow-hidden">
                <span className="absolute -right-8 -top-8 w-24 h-24 bg-emerald-500/10 rounded-full blur-xl pointer-events-none animate-pulse" />
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center animate-pulse"><CheckCircle className="w-4 h-4" /></div>
                    <div>
                      <p className="text-[9px] font-black tracking-widest text-emerald-400 uppercase font-mono">Payment Match Detected</p>
                      <h4 className="text-[11px] font-bold text-slate-100">{matchType === 'exact' ? '🎯 High Confidence' : '⚖️ Amount Match'}</h4>
                    </div>
                  </div>
                  <button onClick={() => setDismissedMatches(prev => [...prev, key])} className="p-1 hover:bg-white/10 rounded-full text-slate-400 hover:text-white cursor-pointer"><X className="w-3.5 h-3.5" /></button>
                </div>
                <div className="space-y-1 text-slate-300">
                  <p className="text-[10.5px]">Bank Credit <span className="font-mono text-white font-semibold">{transaction.txnSerialNumber}</span> for <span className="text-emerald-400 font-extrabold font-mono">${transaction.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span> matched an unpaid bill.</p>
                  <p className="text-[10px] text-slate-400">Matches Invoice <span className="text-white font-mono font-bold uppercase">{invoice.invoiceNumber}</span> ({invoice.clientName}).</p>
                </div>
                <div className="flex gap-2 pt-1 border-t border-slate-800">
                  <button onClick={() => handleQuickReconcile(invoice, transaction)} className="flex-1 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-[10px] uppercase rounded-md cursor-pointer flex items-center justify-center gap-1 border-b-2 border-emerald-800">
                    <Check className="w-3.5 h-3.5" /><span>Settle to PAID</span>
                  </button>
                  <button onClick={() => setDismissedMatches(prev => [...prev, key])} className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-extrabold uppercase rounded-md cursor-pointer">Dismiss</button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Tax ID Legal Disclaimer Modal */}
      {showTaxDisclaimerModal && (
        <div 
          onClick={() => setShowTaxDisclaimerModal(false)} 
          style={{ 
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', 
            backdropFilter: 'blur(3px)', zIndex: 9999, 
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 
          }}
        >
          <div 
            onClick={e => e.stopPropagation()} 
            style={{ 
              background: '#fff', borderRadius: 16, padding: '22px 24px', 
              maxWidth: 440, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' 
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>⚖️</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>Tax ID Verification Notice</span>
              </div>
              <button 
                onClick={() => setShowTaxDisclaimerModal(false)} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af', lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#991b1b', marginBottom: 4 }}>
                ⚠️ Do NOT rely on or trust this badge for legal or tax compliance:
              </div>
              <ul style={{ fontSize: 11, color: '#7f1d1d', lineHeight: 1.6, paddingLeft: 16, margin: 0 }}>
                <li><strong>Format check only:</strong> It only tests character structure and length. It has <strong>no connection</strong> to government databases.</li>
                <li><strong>Filtering can be wrong:</strong> Automated filters can make mistakes, pass fake numbers, or misidentify edge cases.</li>
                <li><strong>Not a legal verification:</strong> A green badge does <strong>not</strong> mean the company is real, active, or legally authorized to claim tax credits.</li>
              </ul>
            </div>

            <p style={{ fontSize: 11, color: '#475569', lineHeight: 1.5, marginBottom: 16 }}>
              You must independently authenticate and verify all buyer tax numbers on the <strong>official government tax portal</strong> (e.g., GST Portal, IRS, HMRC, VIES) before claiming tax credits or exemptions.
            </p>

            <button 
              onClick={() => setShowTaxDisclaimerModal(false)} 
              style={{ 
                width: '100%', padding: '10px 0', background: '#3b82f6', color: '#fff', 
                border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' 
              }}
            >
              I Understand
            </button>
          </div>
        </div>
      )}

    </div>
    </>
  );
}
